import { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  getProcessTemplates,
  createProcessTemplate,
  updateProcessTemplate,
  deleteProcessTemplate,
  getMonthlyProcesses,
  getAllMonthlyProcesses,
  createMonthlyProcess,
  updateMonthlyProcessDetails,
  updateMonthlyProcessSteps,
  updateMonthlyProcessStepProgress,
  archiveMonthlyProcess,
  deleteMonthlyProcess,
  getProcessOwners,
  createProcessOwner,
  updateProcessOwner,
  deleteProcessOwner,
  updateMonthlyProcessOwner,
  createMonthlyProcessesBulk,
  updateMonthlyProcessNote,
  updateMonthlyProcessRepeatSettings,
  updateMonthlyProcessExemptionDayReportStatus,
  recordMonthlyProcessReminderDraft,
  completeMonthlyProcess,
  DEFAULT_PROCESS_PROJECT_ID,
  getCompanyContacts,
  getMonthlyProcessTimeEntries,
  startMonthlyProcessTimer,
  stopMonthlyProcessTimer,
  addManualMonthlyProcessTimeEntry
} from "../services/monthlyProcessService";
import { createUserNotification } from "../services/notificationService";
import { findMonthlyFinancialReport } from "../services/monthlyFinancialReportService";
import {
  createMonthlyProcessCopyDraft,
  createMonthlyProcessCopyTemplate,
  getMonthlyProcessSortValue
} from "../helpers/monthlyProcessCopy";

function MonthlyProcessesView({
  firmaListesi = [],
  user,
  users = [],
  processProject,
  processProjects = [],
  selectProcessProject,
  createNewProcessProject,
  renameProcessProject,
  archiveProcessProject,
  moveProcessProject,
  addActivityLog,
  openMailTasks,
  openVioOperator,
  themeMode = "light",
  tenantMode = false,
  tenantId = ""
}) {
  const TIMER_REMINDER_INTERVAL_MS = 30 * 60 * 1000;
  const ACTIVE_TIMER_STORAGE_KEY = "ecalp-active-process-timer";
  const processProjectId = processProject?.id || "__no_process_project_access__";
  const isDefaultMonthlyProcessProject = processProjectId === DEFAULT_PROCESS_PROJECT_ID;
  const userRoleValue = String(
    user?.role ||
    user?.userRole ||
    user?.rol ||
    user?.yetki ||
    user?.authority ||
    user?.permission ||
    ""
  ).toLowerCase();

  const isAdmin = tenantMode || Boolean(user?.isAdmin) || userRoleValue === "admin";
  const isEditor = isAdmin || Boolean(user?.isEditor) || userRoleValue === "editor";
  const isDark = themeMode === "dark";
  const [kanbanColumnSettings, setKanbanColumnSettings] = useState({ order: [], colors: {} });
  const writeProcessLog = async (action, detail) => {
    try {
      await addActivityLog?.(action, `${processProject?.name || "Süreç Projesi"} - ${detail}`);
    } catch (error) {
      console.error("Süreç hareketi kaydedilemedi:", error);
    }
  };
  const getOwnerSettingKey = (owner) => String(owner?.id || owner?.name || "");
  const kanbanColumnSettingsStorageKey = `ecalp-kanban-column-settings:${processProjectId}:${user?.uid || user?.email || "guest"}`;
  const defaultKanbanColumnColors = ["#f1f5f9", "#ecfeff", "#eff6ff", "#f0fdf4", "#fff7ed", "#fdf2f8", "#f5f3ff"];

  const saveKanbanColumnSettings = (updater) => {
    setKanbanColumnSettings((current) => {
      const nextSettings =
        typeof updater === "function"
          ? updater(current || { order: [], colors: {} })
          : updater;
      const safeSettings = {
        order: Array.isArray(nextSettings?.order) ? nextSettings.order : [],
        colors: nextSettings?.colors || {}
      };

      try {
        window.localStorage.setItem(kanbanColumnSettingsStorageKey, JSON.stringify(safeSettings));
      } catch {
        // Tarayıcı depolaması kapalıysa ayar sadece oturum boyunca tutulur.
      }

      return safeSettings;
    });
  };

  useEffect(() => {
    try {
      const rawValue = window.localStorage.getItem(kanbanColumnSettingsStorageKey);
      setKanbanColumnSettings(rawValue ? JSON.parse(rawValue) : { order: [], colors: {} });
    } catch {
      setKanbanColumnSettings({ order: [], colors: {} });
    }
  }, [kanbanColumnSettingsStorageKey]);
  const modalTheme = {
    surface: isDark ? "#172234" : "#fff",
    surface2: isDark ? "#101827" : "#f8fafc",
    surface3: isDark ? "#1d2a3f" : "#eef6f8",
    input: isDark ? "#111c2e" : "#fff",
    border: isDark ? "#2c3d55" : "#e5e7eb",
    borderSoft: isDark ? "#26364d" : "#dbe2ea",
    text: isDark ? "#e5edf6" : "#124254",
    bodyText: isDark ? "#dbeafe" : "#1f2937",
    muted: isDark ? "#9ca3af" : "#64748b",
    button: isDark ? "#223047" : "#fff",
    progressTrack: isDark ? "#29364a" : "#e5e7eb",
    successBg: isDark ? "#163424" : "#dcfce7",
    successBorder: isDark ? "#2f7d4f" : "#86efac",
    successText: isDark ? "#86efac" : "#166534",
    noteBg: isDark ? "#332916" : "#fffbeb",
    noteBorder: isDark ? "#7c5b18" : "#fde68a",
    noteText: isDark ? "#fde68a" : "#92400e",
    infoBg: isDark ? "#102a3d" : "#f0f9ff",
    infoBorder: isDark ? "#1d4f72" : "#bae6fd",
    infoText: isDark ? "#93c5fd" : "#075985"
  };
  const timeInputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "9px 10px",
    borderRadius: 10,
    border: `1px solid ${modalTheme.borderSoft}`,
    background: modalTheme.input,
    color: modalTheme.bodyText,
    fontSize: 13,
    fontWeight: 800
  };
  const [showProcessProjectManagement, setShowProcessProjectManagement] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [companyContacts, setCompanyContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [templateName, setTemplateName] = useState("MFR Aylık Süreç");
  const [owners, setOwners] = useState([]);
  const [newOwnerName, setNewOwnerName] = useState("");
  const [showOwnerManagement, setShowOwnerManagement] = useState(false);
  const [creatingDefaultKanban, setCreatingDefaultKanban] = useState(false);
  const [editingOwnerId, setEditingOwnerId] = useState("");
  const [editingOwnerName, setEditingOwnerName] = useState("");
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [copySourceProcess, setCopySourceProcess] = useState(null);
  const [copyProcessForm, setCopyProcessForm] = useState(null);
  const [isCopyingProcess, setIsCopyingProcess] = useState(false);
  const [timeEntries, setTimeEntries] = useState([]);
  const [timeEntriesLoading, setTimeEntriesLoading] = useState(false);
  const [timeActionLoading, setTimeActionLoading] = useState(false);
  const [timeTicker, setTimeTicker] = useState(Date.now());
  const [manualTimeForm, setManualTimeForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    startTime: "",
    endTime: "",
    note: ""
  });
  const [showTimeEntries, setShowTimeEntries] = useState(false);
  const [showManualTimeForm, setShowManualTimeForm] = useState(false);
  const [newProcessModalOwner, setNewProcessModalOwner] = useState(null);
  const [newProcessModalTab, setNewProcessModalTab] = useState("details");
  const [newProcessForm, setNewProcessForm] = useState({
    companyName: "",
    month: "",
    scheduleType: "month",
    startDate: "",
    endDate: "",
    title: "",
    steps: [
      {
        title: "",
        subSteps: [""]
      }
    ]
  });
  const [draggingProcessId, setDraggingProcessId] = useState(null);
  const [processSearchText, setProcessSearchText] = useState("");
  const [processListMode, setProcessListMode] = useState("active");
  const [showDelayedOnly, setShowDelayedOnly] = useState(false);
  const [isBulkCreating, setIsBulkCreating] = useState(false);
  const [newProcessNoteText, setNewProcessNoteText] = useState("");
  const [reportListMode, setReportListMode] = useState(null);
  const [showCompanyAssignmentReport, setShowCompanyAssignmentReport] = useState(false);
  const [showPeriodReport, setShowPeriodReport] = useState(false);
  const [periodReportProcesses, setPeriodReportProcesses] = useState(null);
  const [periodReportLoading, setPeriodReportLoading] = useState(false);
  const [periodReportSelection, setPeriodReportSelection] = useState("all");
  const [showProcessActionsMenu, setShowProcessActionsMenu] = useState(false);
  const [reminderDraft, setReminderDraft] = useState(null);
  const [financialVerificationKey, setFinancialVerificationKey] = useState("");
  const completionPromptOpenRef = useRef("");
  const [isEditingRepeatSettings, setIsEditingRepeatSettings] = useState(false);
  const [editableRepeatSettings, setEditableRepeatSettings] = useState({
    autoRepeat: false,
    recurrence: {
      frequency: "monthly",
      interval: 1,
      createTiming: "periodEnd"
    }
  });
  const [showBulkCreateModal, setShowBulkCreateModal] = useState(false);
  const [isBulkRepeatUpdating, setIsBulkRepeatUpdating] = useState(false);
  const [showTemplateCreateModal, setShowTemplateCreateModal] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [isEditingSelectedSteps, setIsEditingSelectedSteps] = useState(false);
  const [isSavingSelectedSteps, setIsSavingSelectedSteps] = useState(false);
  const [isNotesPanelExpanded, setIsNotesPanelExpanded] = useState(false);
  const [editableSelectedProcessForm, setEditableSelectedProcessForm] = useState({
    title: "",
    companyName: "",
    companyShortName: "",
    owner: "",
    scheduleType: "month",
    month: "",
    startDate: "",
    endDate: ""
  });
  const [editableSelectedSteps, setEditableSelectedSteps] = useState([]);
  const [editableSelectedStepGroups, setEditableSelectedStepGroups] = useState([]);
  const [stepEditScope, setStepEditScope] = useState("single");
  const [stepsText, setStepsText] = useState(
`Personel yıllık izin/rapor, proje ilerleme, mali veriler istendi
PDKS giriş-çıkış bilgisi istendi
Puantaj exceli oluşturuldu
Ar-Ge faaliyet tür puantajları girildi
Uzaktan çalışma puantajları girildi
Proje ilerleme verileri sisteme girildi
SMM aylık excel bilgi formu girildi
5746 hizmet listesi kontrolü yapıldı`
  );

  const [structuredSteps, setStructuredSteps] = useState([
    {
      title: "Bilgi Talepleri",
      subSteps: [
        "Personel yıllık izin/rapor, proje ilerleme, mali veriler istendi",
        "PDKS giriş-çıkış bilgisi istendi"
      ]
    },
    {
      title: "Puantaj Hazırlık ve Giriş",
      subSteps: [
        "Puantaj exceli oluşturuldu",
        "Ar-Ge faaliyet tür puantajları girildi",
        "Uzaktan çalışma puantajları girildi"
      ]
    },
    {
      title: "Sistem Girişleri",
      subSteps: [
        "Proje ilerleme verileri sisteme girildi",
        "SMM aylık excel bilgi formu girildi"
      ]
    },
    {
      title: "Kontroller",
      subSteps: [
        "5746 hizmet listesi kontrolü yapıldı"
      ]
    }
  ]);

  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  });
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templateInterval, setTemplateInterval] = useState(1);
  const [selectedOwner, setSelectedOwner] = useState("");
  const dialogResolverRef = useRef(null);
  const [appDialog, setAppDialog] = useState({
    open: false,
    mode: "alert",
    type: "info",
    title: "",
    message: "",
    confirmText: "Tamam",
    cancelText: "Vazgeç",
    inputValue: "",
    inputPlaceholder: ""
  });

  const companyAssignmentReportRows = useMemo(() => {
    return (users || [])
      .map((item) => ({
        id: item.id || item.uid || item.email || item.name,
        name: item.name || item.email || "-",
        role:
          item.role === "admin"
            ? "Admin"
            : item.role === "editor"
            ? "Düzenleyici"
            : "Görüntüleyici",
        companies: Array.isArray(item.assignedCompanies)
          ? item.assignedCompanies.filter(Boolean)
          : []
      }))
      .filter((item) => item.companies.length > 0)
      .sort((a, b) => String(a.name).localeCompare(String(b.name), "tr"));
  }, [users]);

  const exportCompanyAssignmentReportToExcel = () => {
    const exportRows = companyAssignmentReportRows.flatMap((row) =>
      row.companies.map((companyName) => ({
        Kullanıcı: row.name,
        Yetki: row.role,
        Firma: companyName
      }))
    );

    const worksheet = XLSX.utils.json_to_sheet(
      exportRows.length
        ? exportRows
        : [{ Kullanıcı: "", Yetki: "", Firma: "Firma ataması bulunmuyor" }]
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Firma Atama Raporu");
    XLSX.writeFile(workbook, "Firma_Atama_Raporu.xlsx");
  };

  const printCompanyAssignmentReport = () => {
    const reportDate = new Date().toLocaleString("tr-TR");
    const rowsHtml = companyAssignmentReportRows.length
      ? companyAssignmentReportRows
          .map(
            (row) => `
              <tr>
                <td>${row.name}</td>
                <td>${row.role}</td>
                <td>${row.companies.length}</td>
                <td>${row.companies.join("<br/>")}</td>
              </tr>
            `
          )
          .join("")
      : `<tr><td colspan="4">Firma ataması yapılmış kullanıcı bulunmuyor.</td></tr>`;

    const printWindow = window.open("", "_blank", "width=1000,height=720");
    if (!printWindow) {
      alert("PDF/Yazdır penceresi açılamadı. Lütfen tarayıcı pop-up iznini kontrol edin.");
      return;
    }

    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Firma Atama Raporu</title>
          <style>
            body { font-family: Arial, sans-serif; color: #111827; padding: 28px; }
            h1 { color: #124254; margin: 0 0 6px; }
            .muted { color: #64748b; font-size: 13px; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #dbe2ea; padding: 10px; text-align: left; vertical-align: top; font-size: 12px; }
            th { background: #eef6f8; color: #124254; }
          </style>
        </head>
        <body>
          <h1>Firma Atama Raporu</h1>
          <div class="muted">Oluşturma tarihi: ${reportDate}</div>
          <table>
            <thead>
              <tr>
                <th>Kullanıcı</th>
                <th>Yetki</th>
                <th>Firma Sayısı</th>
                <th>Atanan Firmalar</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
          <script>
            window.onload = function () {
              window.print();
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const getDialogTitle = (type) => {
    if (type === "success") return "İşlem tamamlandı";
    if (type === "danger") return "Dikkat";
    if (type === "warning") return "Uyarı";
    if (type === "error") return "Hata";
    return "Bilgilendirme";
  };

  const showDialog = ({
    mode = "alert",
    type = "info",
    title,
    message,
    confirmText = mode === "alert" ? "Tamam" : "Onayla",
    cancelText = "Vazgeç",
    inputPlaceholder = ""
  }) => new Promise((resolve) => {
    dialogResolverRef.current = resolve;
    setAppDialog({
      open: true,
      mode,
      type,
      title: title || getDialogTitle(type),
      message,
      confirmText,
      cancelText,
      inputValue: "",
      inputPlaceholder
    });
  });

  const alert = (message, type = "info", title = "") => {
    dialogResolverRef.current = null;
    setAppDialog({
      open: true,
      mode: "alert",
      type,
      title: title || getDialogTitle(type),
      message,
      confirmText: "Tamam",
      cancelText: "Vazgeç",
      inputValue: "",
      inputPlaceholder: ""
    });
  };

  const confirmDialog = (message, options = {}) =>
    showDialog({
      mode: "confirm",
      type: options.type || "warning",
      title: options.title,
      message,
      confirmText: options.confirmText || "Onayla",
      cancelText: options.cancelText || "Vazgeç"
    });

  const promptDialog = (message, options = {}) =>
    showDialog({
      mode: "prompt",
      type: options.type || "danger",
      title: options.title,
      message,
      confirmText: options.confirmText || "Onayla",
      cancelText: options.cancelText || "Vazgeç",
      inputPlaceholder: options.inputPlaceholder || ""
    });

  const closeDialog = (result) => {
    const resolver = dialogResolverRef.current;
    const response = appDialog.mode === "prompt"
      ? result ? appDialog.inputValue : null
      : Boolean(result);

    dialogResolverRef.current = null;
    setAppDialog((current) => ({ ...current, open: false }));

    if (resolver) {
      resolver(response);
    }
  };

  const startEditingRepeatSettings = () => {
    setEditableRepeatSettings({
      autoRepeat: Boolean(selectedProcess?.autoRepeat),
      recurrence: {
        frequency: "monthly",
        interval: Math.max(1, Number(selectedProcess?.recurrence?.interval) || 1),
        createTiming: "periodEnd"
      }
    });

    setIsEditingRepeatSettings(true);
  };

  const cancelEditingRepeatSettings = () => {
    setIsEditingRepeatSettings(false);

    setEditableRepeatSettings({
      autoRepeat: false,
      recurrence: {
        frequency: "monthly",
        interval: 1,
        createTiming: "periodEnd"
      }
    });
  };

  const saveRepeatSettings = async () => {
    if (!selectedProcess?.id) return;

    try {
      await updateMonthlyProcessRepeatSettings({
        processId: selectedProcess.id,
        autoRepeat: editableRepeatSettings.autoRepeat,
        recurrence: {
          frequency: "monthly",
          interval: editableRepeatSettings.recurrence.interval,
          createTiming: "periodEnd"
        },
        userEmail: user?.email
      });

      const updatedProcess = {
        ...selectedProcess,
        autoRepeat: editableRepeatSettings.autoRepeat,
        recurrence: {
          frequency: "monthly",
          interval: editableRepeatSettings.recurrence.interval,
          createTiming: "periodEnd"
        }
      };

      setSelectedProcess(updatedProcess);

      setProcesses((current) =>
        current.map((item) =>
          item.id === selectedProcess.id
            ? updatedProcess
            : item
        )
      );

      setIsEditingRepeatSettings(false);

      await writeProcessLog(
        "SUREC_TEKRAR_AYARI_GUNCELLENDI",
        getProcessDisplayTitle(updatedProcess)
      );
      await loadData(true);
    } catch (error) {
      console.error(error);
      alert("Tekrarlama ayarı kaydedilemedi.");
    }
  };

  const enableRepeatForSelectedPeriod = async () => {
    if (!selectedMonth || isBulkRepeatUpdating) return;

    const targetProcesses = processes.filter(
      (processItem) =>
        processItem.status !== "archived" &&
        processItem.month === selectedMonth &&
        processItem.autoRepeat !== true
    );

    if (targetProcesses.length === 0) {
      alert(
        `${formatMonthLabel(selectedMonth)} dönemindeki görevlerin tekrarlama ayarı zaten açık görünüyor.`,
        "info",
        "Tekrarlama ayarı"
      );
      return;
    }

    const approved = await confirmDialog(
      `${formatMonthLabel(selectedMonth)} dönemindeki ${targetProcesses.length} görev için otomatik tekrarlama açılacak. Bu görevler ayın son günü saat 18:00'de sonraki dönem için yeniden oluşturulabilir. Devam edilsin mi?`,
      {
        title: "Dönem tekrarı açılsın mı?",
        type: "warning",
        confirmText: "Tekrarlı Yap"
      }
    );

    if (!approved) return;

    setIsBulkRepeatUpdating(true);

    const recurrence = {
      frequency: "monthly",
      interval: 1,
      createTiming: "periodEnd"
    };

    try {
      await Promise.all(
        targetProcesses.map((processItem) =>
          updateMonthlyProcessRepeatSettings({
            processId: processItem.id,
            autoRepeat: true,
            recurrence,
            userEmail: user?.email
          })
        )
      );

      const updatedIds = new Set(targetProcesses.map((processItem) => processItem.id));

      setProcesses((current) =>
        current.map((processItem) =>
          updatedIds.has(processItem.id)
            ? { ...processItem, autoRepeat: true, recurrence }
            : processItem
        )
      );

      if (selectedProcess?.id && updatedIds.has(selectedProcess.id)) {
        setSelectedProcess((current) =>
          current ? { ...current, autoRepeat: true, recurrence } : current
        );
      }

      await writeProcessLog(
        "SUREC_TEKRAR_AYARI_TOPLU_ACILDI",
        `${formatMonthLabel(selectedMonth)} / ${targetProcesses.length} görev`
      );

      await loadData(true);

      alert(
        `${formatMonthLabel(selectedMonth)} dönemi için ${targetProcesses.length} görevin tekrarlama ayarı açıldı.`,
        "success",
        "Tekrarlama açıldı"
      );
    } catch (error) {
      console.error(error);
      alert("Dönem tekrarlama ayarı toplu olarak açılamadı.", "danger", "İşlem tamamlanamadı");
    } finally {
      setIsBulkRepeatUpdating(false);
    }
  };

  const getCurrentUserId = () => user?.uid || user?.email || "";
  const getCurrentUserName = () => user?.name || user?.displayName || user?.email || "Kullanıcı";

  const formatTimeDuration = (secondsValue = 0) => {
    const totalSeconds = Math.max(0, Math.floor(Number(secondsValue) || 0));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours} sa ${String(minutes).padStart(2, "0")} dk`;
    }

    if (minutes > 0) {
      return `${minutes} dk ${String(seconds).padStart(2, "0")} sn`;
    }

    return `${seconds} sn`;
  };

  const requestTimerNotificationPermission = () => {
    if (!("Notification" in window)) return;

    if (Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  };

  const getStoredActiveTimer = () => {
    try {
      const rawValue = window.localStorage.getItem(ACTIVE_TIMER_STORAGE_KEY);
      return rawValue ? JSON.parse(rawValue) : null;
    } catch {
      return null;
    }
  };

  const storeActiveTimer = (entry, processItem = selectedProcess) => {
    if (!entry?.id || !entry?.startedAtIso) return;

    const currentStoredTimer = getStoredActiveTimer();
    const nextTimer = {
      entryId: entry.id,
      processId: entry.processId || processItem?.id || "",
      processTitle: entry.processTitle || getProcessDisplayTitle(processItem || {}) || "Görev",
      startedAtIso: entry.startedAtIso,
      userId: entry.userId || getCurrentUserId(),
      userEmail: entry.userEmail || user?.email || "",
      lastReminderAt: currentStoredTimer?.entryId === entry.id
        ? currentStoredTimer.lastReminderAt || 0
        : 0
    };

    window.localStorage.setItem(ACTIVE_TIMER_STORAGE_KEY, JSON.stringify(nextTimer));
  };

  const clearStoredActiveTimer = (entryId = "") => {
    const storedTimer = getStoredActiveTimer();
    if (!storedTimer) return;

    if (!entryId || storedTimer.entryId === entryId) {
      window.localStorage.removeItem(ACTIVE_TIMER_STORAGE_KEY);
    }
  };

  const showTimerReminder = async (storedTimer, elapsedSeconds) => {
    const message = `${storedTimer.processTitle || "Görev"} için zaman takibi ${formatTimeDuration(elapsedSeconds)} süredir devam ediyor.`;

    if (!("Notification" in window)) return;

    let permission = Notification.permission;

    if (permission === "default") {
      permission = await Notification.requestPermission();
    }

    if (permission === "granted") {
      new Notification("Zaman takibi devam ediyor", {
        body: message,
        tag: `process-timer-${storedTimer.entryId}`,
        renotify: true
      });
    }
  };

  const getTimeEntryDuration = (entry) => {
    if (!entry) return 0;

    if (entry.status === "running" && entry.startedAtIso) {
      const startedAt = new Date(entry.startedAtIso);
      if (!Number.isNaN(startedAt.getTime())) {
        return Math.max(0, Math.round((timeTicker - startedAt.getTime()) / 1000));
      }
    }

    return Number(entry.durationSeconds) || 0;
  };

  const loadTimeEntries = async (processId) => {
    if (!processId) {
      setTimeEntries([]);
      return;
    }

    setTimeEntriesLoading(true);

    try {
      const entries = await getMonthlyProcessTimeEntries(processId);
      setTimeEntries(entries);

      const runningEntry = entries.find(
        (entry) =>
          entry.status === "running" &&
          (
            String(entry.userId || "") === String(getCurrentUserId()) ||
            String(entry.userEmail || "") === String(user?.email || "")
          )
      );

      if (runningEntry) {
        storeActiveTimer(runningEntry);
      } else {
        const storedTimer = getStoredActiveTimer();
        if (storedTimer?.processId === processId) {
          clearStoredActiveTimer(storedTimer.entryId);
        }
      }
    } catch (error) {
      console.error("Zaman kayıtları yüklenemedi:", error);
      setErrorMessage(`Zaman kayıtları yüklenemedi: ${error.message}`);
    } finally {
      setTimeEntriesLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedProcess?.id) {
      setTimeEntries([]);
      return;
    }

    loadTimeEntries(selectedProcess.id);
    setManualTimeForm((current) => ({
      ...current,
      date: new Date().toISOString().slice(0, 10),
      startTime: "",
      endTime: "",
      note: ""
    }));
    setShowTimeEntries(false);
    setShowManualTimeForm(false);
  }, [selectedProcess?.id]);

  useEffect(() => {
    if (!timeEntries.some((entry) => entry.status === "running")) {
      return undefined;
    }

    const intervalId = window.setInterval(() => setTimeTicker(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [timeEntries]);

  useEffect(() => {
    const checkActiveTimerReminder = () => {
      const storedTimer = getStoredActiveTimer();
      if (!storedTimer?.startedAtIso) return;

      const startedAt = new Date(storedTimer.startedAtIso);
      if (Number.isNaN(startedAt.getTime())) return;

      const now = Date.now();
      const elapsedMs = now - startedAt.getTime();
      const lastReminderAt = Number(storedTimer.lastReminderAt) || 0;

      if (elapsedMs < TIMER_REMINDER_INTERVAL_MS) return;
      if (lastReminderAt && now - lastReminderAt < TIMER_REMINDER_INTERVAL_MS) return;

      const nextStoredTimer = {
        ...storedTimer,
        lastReminderAt: now
      };

      window.localStorage.setItem(ACTIVE_TIMER_STORAGE_KEY, JSON.stringify(nextStoredTimer));
      showTimerReminder(nextStoredTimer, Math.round(elapsedMs / 1000)).catch(() => {});
    };

    checkActiveTimerReminder();
    const intervalId = window.setInterval(checkActiveTimerReminder, 60 * 1000);
    return () => window.clearInterval(intervalId);
  }, [user?.email]);

  const activeTimeEntry = timeEntries.find(
    (entry) => entry.status === "running" && String(entry.userId || entry.userEmail || "") === String(getCurrentUserId() || user?.email || "")
  ) || timeEntries.find(
    (entry) => entry.status === "running" && entry.userEmail === user?.email
  );

  const totalTrackedSeconds = timeEntries.reduce(
    (total, entry) => total + getTimeEntryDuration(entry),
    0
  );

  const myTrackedSeconds = timeEntries
    .filter((entry) =>
      String(entry.userId || "") === String(getCurrentUserId()) ||
      String(entry.userEmail || "") === String(user?.email || "")
    )
    .reduce((total, entry) => total + getTimeEntryDuration(entry), 0);

  const startTaskTimer = async () => {
    if (!selectedProcess?.id || activeTimeEntry || timeActionLoading) return;

    setTimeActionLoading(true);

    try {
      requestTimerNotificationPermission();

      const startedTimer = await startMonthlyProcessTimer({
        processId: selectedProcess.id,
        processTitle: getProcessDisplayTitle(selectedProcess),
        userId: getCurrentUserId(),
        userEmail: user?.email || "",
        userName: getCurrentUserName()
      });

      storeActiveTimer({
        id: startedTimer.id,
        processId: selectedProcess.id,
        processTitle: getProcessDisplayTitle(selectedProcess),
        userId: getCurrentUserId(),
        userEmail: user?.email || "",
        startedAtIso: startedTimer.startedAtIso
      }, selectedProcess);

      await writeProcessLog(
        "SUREC_ZAMAN_TAKIBI_BASLATILDI",
        getProcessDisplayTitle(selectedProcess)
      );
      await loadTimeEntries(selectedProcess.id);
    } catch (error) {
      console.error("Zaman takibi başlatılamadı:", error);
      setErrorMessage(`Zaman takibi başlatılamadı: ${error.message}`);
    } finally {
      setTimeActionLoading(false);
    }
  };

  const stopTaskTimer = async () => {
    if (!selectedProcess?.id || !activeTimeEntry || timeActionLoading) return;

    setTimeActionLoading(true);

    try {
      await stopMonthlyProcessTimer({
        entryId: activeTimeEntry.id,
        startedAtIso: activeTimeEntry.startedAtIso,
        userEmail: user?.email || ""
      });

      clearStoredActiveTimer(activeTimeEntry.id);
      await writeProcessLog(
        "SUREC_ZAMAN_TAKIBI_DURDURULDU",
        getProcessDisplayTitle(selectedProcess)
      );
      await loadTimeEntries(selectedProcess.id);
    } catch (error) {
      console.error("Zaman takibi durdurulamadı:", error);
      setErrorMessage(`Zaman takibi durdurulamadı: ${error.message}`);
    } finally {
      setTimeActionLoading(false);
    }
  };

  const addManualTimeEntry = async () => {
    if (!selectedProcess?.id || timeActionLoading) return;

    const { date, startTime, endTime, note } = manualTimeForm;

    if (!date || !startTime || !endTime) {
      alert("Manuel süre eklemek için tarih, başlangıç ve bitiş saatini seçmelisiniz.");
      return;
    }

    const startedAt = new Date(`${date}T${startTime}:00`);
    const endedAt = new Date(`${date}T${endTime}:00`);

    if (Number.isNaN(startedAt.getTime()) || Number.isNaN(endedAt.getTime()) || endedAt <= startedAt) {
      alert("Bitiş saati başlangıç saatinden sonra olmalıdır.");
      return;
    }

    const durationSeconds = Math.round((endedAt.getTime() - startedAt.getTime()) / 1000);

    setTimeActionLoading(true);

    try {
      await addManualMonthlyProcessTimeEntry({
        processId: selectedProcess.id,
        processTitle: getProcessDisplayTitle(selectedProcess),
        userId: getCurrentUserId(),
        userEmail: user?.email || "",
        userName: getCurrentUserName(),
        startedAtIso: startedAt.toISOString(),
        endedAtIso: endedAt.toISOString(),
        durationSeconds,
        note: note || ""
      });

      setManualTimeForm({
        date: new Date().toISOString().slice(0, 10),
        startTime: "",
        endTime: "",
        note: ""
      });
      await writeProcessLog(
        "SUREC_MANUEL_SURE_EKLENDI",
        `${getProcessDisplayTitle(selectedProcess)} / ${Math.round(durationSeconds / 60)} dk`
      );
      await loadTimeEntries(selectedProcess.id);
    } catch (error) {
      console.error("Manuel süre eklenemedi:", error);
      setErrorMessage(`Manuel süre eklenemedi: ${error.message}`);
    } finally {
      setTimeActionLoading(false);
    }
  };

  const companies = useMemo(() => {
    const companyMap = new Map();

    [...(firmaListesi || [])].forEach((firma) => {
      const firmaAdi = firma.firmaAdi || firma.companyName || firma.name || "";

      if (!firmaAdi) return;

      if (!companyMap.has(firmaAdi)) {
        companyMap.set(firmaAdi, {
          name: firmaAdi,
          shortName: firma.firmaKisaAdi || firma.companyShortName || firma.kisaAd || ""
        });
      }
    });

    return Array.from(companyMap.values()).sort((a, b) =>
      String(a.name).localeCompare(String(b.name), "tr")
    );
  }, [firmaListesi]);

  const getCompanyDisplayName = (companyName) => {
    const company = companies.find((item) => item.name === companyName);
    return company?.shortName || getAutoCompanyShortName(companyName) || companyName;
  };

  const getCompanyShortName = (companyName) => {
    const company = companies.find((item) => item.name === companyName);
    return company?.shortName || getAutoCompanyShortName(companyName) || "";
  };

  const getProcessDisplayTitle = (processItem = {}) => {
    return (
      processItem.companyShortName ||
      processItem.firmaKisaAdi ||
      getCompanyDisplayName(processItem.companyName) ||
      processItem.title ||
      processItem.templateName ||
      "Genel Görev"
    );
  };

  const getDateFromValue = (value) => {
    if (!value) return null;

    const date = value?.toDate ? value.toDate() : new Date(value);

    return Number.isNaN(date.getTime()) ? null : date;
  };

  const getProcessCompletedDate = (processItem = {}) =>
    getDateFromValue(processItem.completedAt || processItem.updatedAt);

  const isCompletedOwner = (ownerName = "") =>
    String(ownerName || "").trim().toLocaleUpperCase("tr-TR") === "TAMAMLANDI";

  const normalizeContactMatchText = (value = "") =>
    String(value)
      .trim()
      .toLocaleLowerCase("tr-TR")
      .replaceAll("ı", "i")
      .replaceAll("İ", "i")
      .replace(/\s+/g, " ");

  const getCurrentUserDisplayName = () =>
    user?.name || user?.displayName || user?.email || "Bir kullanıcı";

  const getNotificationUsers = () => {
    const uniqueUsers = new Map();

    users.forEach((item) => {
      const targetUserId = item?.id || item?.uid;

      if (!targetUserId || item?.isActive === false) return;

      uniqueUsers.set(targetUserId, item);
    });

    return Array.from(uniqueUsers.entries()).map(([targetUserId, targetUser]) => ({
      targetUserId,
      targetUser
    }));
  };

  const normalizeMentionText = (value = "") =>
    String(value || "")
      .toLocaleLowerCase("tr-TR")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replaceAll("ı", "i")
      .replaceAll("İ", "i")
      .replace(/[^a-z0-9ğüşıöç._-]+/gi, "");

  const getMentionAliasesForUser = (targetUser = {}) => {
    const displayName = String(
      targetUser.name ||
      targetUser.displayName ||
      targetUser.fullName ||
      targetUser.email ||
      ""
    ).trim();
    const email = String(targetUser.email || "").trim();
    const emailName = email.split("@")[0] || "";
    const nameParts = displayName.split(/\s+/).filter(Boolean);

    return Array.from(new Set([
      displayName,
      displayName.replace(/\s+/g, ""),
      nameParts[0] || "",
      email,
      emailName
    ].map(normalizeMentionText).filter(Boolean)));
  };

  const getNoteMentions = (noteText = "") => {
    const mentionMatches = Array.from(String(noteText || "").matchAll(/@([\p{L}\p{N}._-]+)/gu));
    const rawMentions = Array.from(new Set(
      mentionMatches.map((match) => normalizeMentionText(match[1])).filter(Boolean)
    ));
    const mentionAll = rawMentions.includes("herkes");

    if (rawMentions.length === 0) {
      return { mentionAll: false, mentionedUsers: [], rawMentions: [] };
    }

    const mentionedUserMap = new Map();

    getNotificationUsers().forEach(({ targetUserId, targetUser }) => {
      const aliases = getMentionAliasesForUser(targetUser);
      const matched = mentionAll || rawMentions.some((mention) =>
        aliases.includes(mention) ||
        aliases.some((alias) => alias.startsWith(mention) || mention.startsWith(alias))
      );

      if (!matched) return;

      mentionedUserMap.set(targetUserId, { targetUserId, targetUser });
    });

    return {
      mentionAll,
      mentionedUsers: Array.from(mentionedUserMap.values()),
      rawMentions
    };
  };

  const getActiveMentionQuery = (noteText = "") => {
    const match = String(noteText || "").match(/(^|\s)@([\p{L}\p{N}._-]*)$/u);
    return match ? normalizeMentionText(match[2]) : null;
  };

  const getNoteMentionSuggestions = (noteText = "") => {
    const activeQuery = getActiveMentionQuery(noteText);

    if (activeQuery === null) return [];

    const suggestions = [
      {
        token: "herkes",
        label: "Herkes",
        subLabel: "Tüm aktif kullanıcılara bildirim gider",
        avatar: "@"
      }
    ];

    getNotificationUsers().forEach(({ targetUserId, targetUser }) => {
      const displayName = String(
        targetUser.name ||
        targetUser.displayName ||
        targetUser.fullName ||
        targetUser.email ||
        "Kullanıcı"
      ).trim();
      const email = String(targetUser.email || "").trim();
      const aliases = getMentionAliasesForUser(targetUser);
      const matchesQuery = !activeQuery || aliases.some((alias) =>
        alias.includes(activeQuery) || activeQuery.includes(alias)
      );

      if (!matchesQuery) return;

      suggestions.push({
        token: normalizeMentionText(displayName.split(/\s+/)[0] || email.split("@")[0] || targetUserId),
        label: displayName,
        subLabel: email,
        avatar: displayName.slice(0, 1).toLocaleUpperCase("tr-TR")
      });
    });

    return suggestions.slice(0, 7);
  };

  const insertNoteMention = (token) => {
    const safeToken = String(token || "").trim();
    if (!safeToken) return;

    setNewProcessNoteText((currentText) => {
      const text = String(currentText || "");

      if (/(^|\s)@([\p{L}\p{N}._-]*)$/u.test(text)) {
        return text.replace(/(^|\s)@([\p{L}\p{N}._-]*)$/u, `$1@${safeToken} `);
      }

      return `${text}${text && !text.endsWith(" ") ? " " : ""}@${safeToken} `;
    });
  };

  const notifyTeam = async ({ processItem = {}, title, body, type = "task", targetUsers = null }) => {
    const notificationTargets = Array.isArray(targetUsers) ? targetUsers : getNotificationUsers();

    if (notificationTargets.length === 0) return;

    try {
      await Promise.all(
        notificationTargets.map(({ targetUserId, targetUser }) =>
          createUserNotification({
            targetUserId,
            targetUserEmail: targetUser.email || "",
            title,
            body,
            type,
            link: "monthlyProcesses",
            processId: processItem.id || "",
            processProjectId,
            createdBy: user?.email || "",
            createdByUid: user?.uid || ""
          })
        )
      );
    } catch (error) {
      console.error("Görev bildirimi oluşturulamadı:", error);
    }
  };

  const getContactsForProcess = (processItem = {}) => {
    const companyName = normalizeContactMatchText(processItem.companyName);
    const companyShortName = normalizeContactMatchText(
      processItem.companyShortName ||
        processItem.firmaKisaAdi ||
        getCompanyDisplayName(processItem.companyName)
    );

    if (!companyName && !companyShortName) return [];

    return companyContacts.filter((contact) => {
      const contactCompanyName = normalizeContactMatchText(contact.companyName);
      const contactCompanyShortName = normalizeContactMatchText(contact.companyShortName);

      return (
        (companyName && contactCompanyName === companyName) ||
        (companyShortName && contactCompanyShortName === companyShortName) ||
        (companyShortName && contactCompanyName === companyShortName)
      );
    });
  };

  const normalizePhoneForWhatsApp = (value = "") => {
    const digits = String(value).replace(/\D/g, "");

    if (!digits) return "";
    if (digits.startsWith("90")) return digits;
    if (digits.startsWith("0")) return `90${digits.slice(1)}`;
    if (digits.length === 10) return `90${digits}`;

    return digits;
  };

  const getProcessStepsWithProgress = (processItem = {}) => {
    const stepProgress = processItem.stepProgress || {};

    return (processItem.steps || []).map((step, index) => {
      const progress = stepProgress[String(index)] || {};

      return {
        ...step,
        completed:
          progress.completed !== undefined
            ? Boolean(progress.completed)
            : Boolean(step.completed),
        completedAt:
          progress.completedAt !== undefined
            ? progress.completedAt
            : step.completedAt || null,
        completedBy:
          progress.completedBy !== undefined
            ? progress.completedBy
            : step.completedBy || "",
        verification:
          progress.verification !== undefined
            ? progress.verification
            : step.verification || null
      };
    });
  };

  const normalizeIntegrationStepTitle = (value = "") => String(value)
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/[ıİ]/g, "i")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

  const isMonthlyFinancialImportStep = (step = {}) => {
    const title = normalizeIntegrationStepTitle(step.title);
    return title.includes("aylik excel bilgi formu") &&
      (title.includes("mali musavir") || title.includes("smm"));
  };

  const isExemptionDayReportDeliveryStep = (step = {}) => {
    const title = normalizeIntegrationStepTitle(step.title);
    return title.includes("muafiyetli gun sayisi raporu") && title.includes("teslim");
  };

  const getProcessPeriodKey = (processItem = {}) => {
    const month = String(processItem.month || "").trim();
    if (/^20\d{2}-(0[1-9]|1[0-2])$/.test(month)) return month;
    const startDate = String(processItem.startDate || "").trim();
    return /^20\d{2}-(0[1-9]|1[0-2])/.test(startDate) ? startDate.slice(0, 7) : "";
  };

  const getIncompleteProcessSteps = (processItem = {}) =>
    getProcessStepsWithProgress(processItem)
      .map((step, index) => ({
        index,
        order: step.order || "",
        groupTitle: step.groupTitle || "",
        title: step.title || "Adım",
        completed: Boolean(step.completed)
      }))
      .filter((step) => step.index >= 0 && step.index <= 5 && !step.completed);

  const getShiftedProcessMonthLabel = (processItem = {}, offset = -1) => {
    const baseMonthValue = processItem.month || "";
    let year;
    let monthIndex;

    if (baseMonthValue && String(baseMonthValue).includes("-")) {
      const [yearText, monthText] = String(baseMonthValue).split("-");
      year = Number(yearText);
      monthIndex = Number(monthText) - 1;
    } else if (processItem.startDate && String(processItem.startDate).includes("-")) {
      const [yearText, monthText] = String(processItem.startDate).split("-");
      year = Number(yearText);
      monthIndex = Number(monthText) - 1;
    }

    if (!year && year !== 0) return formatProcessPeriod(processItem);

    const shiftedDate = new Date(year, monthIndex + offset, 1);
    const shiftedMonth = `${shiftedDate.getFullYear()}-${String(shiftedDate.getMonth() + 1).padStart(2, "0")}`;

    return formatMonthLabel(shiftedMonth);
  };

  const buildReminderSections = (processItem = {}) => {
    const missingSteps = getIncompleteProcessSteps(processItem);
    const previousMonthLabel = getShiftedProcessMonthLabel(processItem, -1);
    const twoMonthsAgoLabel = getShiftedProcessMonthLabel(processItem, -2);
    const hasPersonnelMissing = missingSteps.some((step) => step.index >= 0 && step.index <= 3);
    const hasProjectProgressMissing = missingSteps.some((step) => step.index === 4);
    const hasFinancialMissing = missingSteps.some((step) => step.index === 5);
    const sections = [];

    if (hasPersonnelMissing) {
      sections.push({
        title: "Personel Durumu;",
        text: `• ${previousMonthLabel} Döneminde çalışan arkadaşlar arasında Yıllık İzinli, Raporlu, İşe yeni başlayan veya ayrılan durum bilgilerine ait ad-soyad, tarih ve gün bilgilerini iletmenizi,`
      });
    }

    if (hasProjectProgressMissing) {
      sections.push({
        title: "Proje İlerlemesi için;",
        text: `• ${previousMonthLabel} Dönemine ait EK’ te yer alan proje/projelerinize ait Proje İlerleme excelinin doldurulup iletilmesini,`
      });
    }

    if (hasFinancialMissing) {
      sections.push({
        title: "Mali Verilere ilişkin;",
        text: `• ${twoMonthsAgoLabel} Dönemine ait EK’ te yer alan Aylık Excel Bilgi Formunun doldurulup iletilmesini,\n• ${twoMonthsAgoLabel} Dönemine ait Hizmet Listesi, Tahakkuk Fişlerini ve Muhtasar Beyannamenizi tarafımıza göndermenizi rica ederiz.`
      });
    }

    return { missingSteps, sections };
  };

  const buildReminderDraft = (processItem = {}) => {
    const { missingSteps, sections } = buildReminderSections(processItem);
    const contacts = getContactsForProcess(processItem);
    const emailRecipients = [
      ...new Set(
        contacts
          .map((contact) => String(contact.email || "").trim())
          .filter(Boolean)
      )
    ];
    const phoneRecipients = [
      ...new Set(
        contacts
          .map((contact) => normalizePhoneForWhatsApp(contact.phone))
          .filter(Boolean)
      )
    ];
    const companyTitle = getProcessDisplayTitle(processItem);
    const periodLabel = formatProcessPeriod(processItem);
    const subject = `${companyTitle} - ${periodLabel} Süreç Hatırlatma`;
    const reminderText = sections
      .map((section) => `${section.title}\n${section.text}`)
      .join("\n\n");

    const body = `Merhaba,\n\n${reminderText}\n\nİyi çalışmalar.`;

    const whatsappText = `Merhaba,\n\n${reminderText}`;

    return {
      processId: processItem.id,
      subject,
      body,
      whatsappText,
      to: emailRecipients[0] || "",
      cc: emailRecipients.slice(1),
      phone: phoneRecipients[0] || "",
      whatsappUrl: phoneRecipients[0]
        ? `https://wa.me/${phoneRecipients[0]}?text=${encodeURIComponent(whatsappText)}`
        : "",
      contacts,
      missingSteps,
      createdAt: new Date().toISOString()
    };
  };

  const copyTextToClipboard = async (text, successMessage = "Metin kopyalandı.") => {
    try {
      await navigator.clipboard.writeText(text);
      alert(successMessage);
    } catch (error) {
      console.error("Metin kopyalanamadı:", error);
      alert("Metin otomatik kopyalanamadı. Açılan taslaktan elle seçip kopyalayabilirsiniz.");
    }
  };

  const createReminderDraftForProcess = async (processItem, channel = "mail") => {
    const missingSteps = getIncompleteProcessSteps(processItem);

    if (missingSteps.length === 0) {
      alert("Bu görev kartında eksik adım yok.");
      return;
    }

    if (
      reminderDraft?.processId === processItem.id &&
      reminderDraft?.channel === channel
    ) {
      setReminderDraft(null);
      return;
    }

    const draft = buildReminderDraft(processItem);
    draft.channel = channel;
    setReminderDraft(draft);

    try {
      await recordMonthlyProcessReminderDraft({
        processId: processItem.id,
        channel,
        subject: draft.subject,
        to: draft.to,
        cc: draft.cc,
        phone: draft.phone,
        missingSteps: draft.missingSteps,
        userEmail: user?.email
      });

      const updatedProcess = {
        ...processItem,
        lastReminderDraftAt: new Date().toISOString(),
        lastReminderDraftBy: user?.email || "",
        lastReminderDraftChannel: channel,
        lastReminderDraftSubject: draft.subject,
        lastReminderDraftTo: draft.to,
        lastReminderDraftCc: draft.cc,
        lastReminderDraftPhone: draft.phone,
        lastReminderDraftMissingSteps: draft.missingSteps
      };

      setSelectedProcess(updatedProcess);
      setProcesses((currentProcesses) =>
        currentProcesses.map((item) => (item.id === processItem.id ? updatedProcess : item))
      );
      await writeProcessLog(
        "SUREC_HATIRLATMA_TASLAGI_OLUSTURULDU",
        `${getProcessDisplayTitle(updatedProcess)} / ${channel}`
      );
    } catch (error) {
      console.error("Hatırlatma taslağı kaydedilemedi:", error);
      setErrorMessage(`Hatırlatma taslağı kaydedilemedi: ${error.message}`);
    }
  };

  const openMailDraft = (draft) => {
    const ccPart = draft.cc?.length ? `&cc=${encodeURIComponent(draft.cc.join(","))}` : "";
    const mailto = `mailto:${encodeURIComponent(draft.to || "")}?subject=${encodeURIComponent(draft.subject || "")}${ccPart}&body=${encodeURIComponent(draft.body || "")}`;

    window.location.href = mailto;
  };

  const getAutoCompanyShortName = (companyName = "") => {
    const legalSuffixes = new Set([
      "ANONİM",
      "ANONIM",
      "LİMİTED",
      "LIMITED",
      "ŞİRKETİ",
      "SIRKETI",
      "ŞTİ.",
      "ŞTİ",
      "STI.",
      "STI",
      "A.Ş.",
      "A.Ş",
      "AŞ.",
      "AŞ",
      "LTD.",
      "LTD"
    ]);

    const words = String(companyName || "")
      .trim()
      .split(/\s+/)
      .filter((word) => word && !legalSuffixes.has(word.toLocaleUpperCase("tr-TR")));

    return words.slice(0, 2).join(" ");
  };

const loadData = async (silent = false) => {
  if (!silent) {
    setLoading(true);
  }

  setErrorMessage("");

  try {
    const [templateData, processData, ownerData, contactData] = await Promise.all([
        getProcessTemplates(processProjectId),
        getMonthlyProcesses(processProjectId),
        getProcessOwners(processProjectId),
        getCompanyContacts()
      ]);

      const archiveThresholdDate = new Date();
      archiveThresholdDate.setDate(archiveThresholdDate.getDate() - 90);

      const processesToAutoArchive = processData.filter((processItem) => {
        if (processItem.status !== "completed") return false;

        const completedDate = getProcessCompletedDate(processItem);

        return completedDate && completedDate <= archiveThresholdDate;
      });

      if (processesToAutoArchive.length > 0) {
        await Promise.all(
          processesToAutoArchive.map((processItem) => archiveMonthlyProcess(processItem.id))
        );
      }

      const autoArchivedIds = new Set(processesToAutoArchive.map((processItem) => processItem.id));
      const normalizedProcessData = processData.map((processItem) =>
        autoArchivedIds.has(processItem.id)
          ? { ...processItem, status: "archived", archivedAt: new Date().toISOString() }
          : processItem
      );

      setTemplates(templateData);
      setProcesses(normalizedProcessData);
      setOwners(ownerData);
      setCompanyContacts(contactData);

    if (!selectedTemplateId && templateData.length > 0) {
      setSelectedTemplateId(templateData[0].id);
    }

    if (!selectedOwner && ownerData.length > 0) {
      setSelectedOwner(ownerData[0].name);
    }

    return normalizedProcessData;
  } catch (error) {
    console.error("Aylık süreç verileri yüklenemedi:", error);
    setErrorMessage(
      `Aylık süreç verileri yüklenemedi. Kod: ${error.code || "bilinmiyor"} - ${error.message}`
    );
    return [];
  } finally {
    if (!silent) {
      setLoading(false);
    }
  }
};

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processProjectId]);


  const addMainStep = () => {
  setStructuredSteps((current) => [
    ...current,
    {
      title: "",
      subSteps: [""]
    }
  ]);
};

const updateMainStepTitle = (index, value) => {
  setStructuredSteps((current) =>
    current.map((step, i) =>
      i === index
        ? { ...step, title: value }
        : step
    )
  );
};

const addSubStep = (mainIndex) => {
  setStructuredSteps((current) =>
    current.map((step, i) =>
      i === mainIndex
        ? {
            ...step,
            subSteps: [...step.subSteps, ""]
          }
        : step
    )
  );
};

const updateSubStep = (mainIndex, subIndex, value) => {
    setStructuredSteps((current) =>
      current.map((step, i) =>
        i === mainIndex
          ? {
              ...step,
              subSteps: step.subSteps.map((sub, j) =>
                j === subIndex ? value : sub
              )
            }
          : step
      )
    );
  };

  const removeMainStep = (mainIndex) => {
    setStructuredSteps((current) =>
      current.filter((_, i) => i !== mainIndex)
    );
  };

  const removeSubStep = (mainIndex, subIndex) => {
    setStructuredSteps((current) =>
      current.map((step, i) =>
        i === mainIndex
          ? {
              ...step,
              subSteps: step.subSteps.filter((_, j) => j !== subIndex)
            }
          : step
      )
    );
  };

  const resetTemplateForm = () => {
    setEditingTemplateId(null);
    setTemplateName("MFR Aylık Süreç");
    setTemplateInterval(1);
    setStructuredSteps([
      {
        title: "Bilgi Talepleri",
        subSteps: [
          "Personel yıllık izin/rapor, proje ilerleme, mali veriler istendi",
          "PDKS giriş-çıkış bilgisi istendi"
        ]
      },
      {
        title: "Puantaj Hazırlık ve Giriş",
        subSteps: [
          "Puantaj exceli oluşturuldu",
          "Ar-Ge faaliyet tür puantajları girildi",
          "Uzaktan çalışma puantajları girildi"
        ]
      },
      {
        title: "Sistem Girişleri",
        subSteps: [
          "Proje ilerleme verileri sisteme girildi",
          "SMM aylık excel bilgi formu girildi"
        ]
      },
      {
        title: "Kontroller",
        subSteps: [
          "5746 hizmet listesi kontrolü yapıldı"
        ]
      }
    ]);
  };

  const saveTemplate = async () => {
    setErrorMessage("");

    if (!templateName.trim()) {
      alert("Şablon adı zorunlu.");
      return;
    }

    if (structuredSteps.some((step) => !String(step.title || "").trim())) {
      alert("Ana adım adları boş bırakılamaz.");
      return;
    }

    let orderCounter = 1;

    const steps = structuredSteps.flatMap((mainStep) =>
      (mainStep.subSteps || [])
        .map((subStep) => ({
          order: orderCounter++,
          groupTitle: String(mainStep.title || "").trim(),
          title: String(subStep || "").trim(),
          isRequired: true
        }))
        .filter((step) => step.groupTitle && step.title)
    );

    if (steps.length === 0) {
      alert("En az bir ana adım ve alt adım girilmelidir.");
      return;
    }

    try {
      if (editingTemplateId) {
        await updateProcessTemplate({
          templateId: editingTemplateId,
          name: templateName.trim(),
          steps,
          recurrence: {
            frequency: "monthly",
            interval: templateInterval,
            createTiming: "periodEnd"
          }
        });

        await writeProcessLog("SUREC_SABLONU_GUNCELLENDI", templateName.trim());
        alert("Süreç şablonu güncellendi.");
      } else {
        await createProcessTemplate({
          name: templateName,
          steps,
          recurrence: {
            frequency: "monthly",
            interval: templateInterval,
            createTiming: "periodEnd"
          },
          processProjectId
        });

        await writeProcessLog("SUREC_SABLONU_EKLENDI", templateName.trim());
        alert("Süreç şablonu oluşturuldu.");
      }

      setShowTemplateCreateModal(false);
      resetTemplateForm();
      await loadData();
    } catch (error) {
      console.error("Süreç şablonu kaydedilemedi:", error);
      setErrorMessage(
        `Süreç şablonu kaydedilemedi. Kod: ${error.code || "bilinmiyor"} - ${error.message}`
      );
    }
  };

  const removeTemplate = async (template) => {
    if (!template?.id) return;

    const usedProcessCount = processes.filter(
      (processItem) => processItem.templateId === template.id && processItem.status !== "archived"
    ).length;

    const confirmMessage = usedProcessCount > 0
      ? `Bu şablon ${usedProcessCount} aktif görev kartında kullanılıyor. Şablonu silersen mevcut görev kartları etkilenmez, ancak yeni çoğaltmada bu şablon seçilemez. Devam edilsin mi?`
      : "Bu şablonu silmek istiyor musunuz?";

    if (!(await confirmDialog(confirmMessage, { title: "Şablon silinsin mi?", type: "danger", confirmText: "Sil" }))) return;

    try {
      await deleteProcessTemplate(template.id);
      await writeProcessLog("SUREC_SABLONU_SILINDI", template.name || template.id);
      alert("Süreç şablonu silindi.");

      if (editingTemplateId === template.id) {
        resetTemplateForm();
      }

      await loadData();
    } catch (error) {
      console.error("Süreç şablonu silinemedi:", error);
      setErrorMessage(`Süreç şablonu silinemedi: ${error.message}`);
    }
  };

  const saveMonthlyProcess = async () => {
    setErrorMessage("");

    if (!selectedCompany) {
      alert("Firma seçmelisiniz.");
      return;
    }

    if (!selectedMonth) {
      alert("Ay seçmelisiniz.");
      return;
    }

    const template = templates.find((item) => item.id === selectedTemplateId);

    if (!template) {
      alert("Şablon seçmelisiniz.");
      return;
    }

    const alreadyExists = processes.some(
      (process) =>
        process.companyName === selectedCompany &&
        process.month === selectedMonth &&
        process.templateId === selectedTemplateId
    );

    if (alreadyExists) {
      alert("Bu firma, ay ve şablon için zaten süreç oluşturulmuş.");
      return;
    }

    try {
      const createdReference = await createMonthlyProcess({
        companyName: selectedCompany,
        companyShortName: getCompanyShortName(selectedCompany),
        month: selectedMonth,
        owner: selectedOwner,
        template,
        processProjectId
      });

      await writeProcessLog(
        "SUREC_GOREVI_EKLENDI",
        `${selectedCompany} / ${formatMonthLabel(selectedMonth)} / ${selectedOwner}`
      );

      await notifyTeam({
        processItem: {
          id: createdReference?.id || "",
          companyName: selectedCompany,
          companyShortName: getCompanyShortName(selectedCompany),
          month: selectedMonth,
          templateName: template?.name || ""
        },
        title: "Yeni görev atandı",
        body: `${getCurrentUserDisplayName()}, ${getProcessDisplayTitle({ companyName: selectedCompany, companyShortName: getCompanyShortName(selectedCompany), templateName: template?.name })} görevini ${selectedOwner} sorumlusuna atadı.`
      });

      alert("Aylık süreç oluşturuldu.");
      await loadData();
    } catch (error) {
      console.error("Aylık süreç oluşturulamadı:", error);
      setErrorMessage(
        `Aylık süreç oluşturulamadı. Kod: ${error.code || "bilinmiyor"} - ${error.message}`
      );
    }
  };

  const addModalMainStep = () => {
  setNewProcessForm((current) => ({
    ...current,
    steps: [
      ...current.steps,
      {
        title: "",
        subSteps: [""]
      }
    ]
  }));
};

const addModalSubStep = (mainIndex) => {
  setNewProcessForm((current) => ({
    ...current,
    steps: current.steps.map((step, index) =>
      index === mainIndex
        ? {
            ...step,
            subSteps: [...step.subSteps, ""]
          }
        : step
    )
  }));
};

const updateModalMainStep = (mainIndex, value) => {
  setNewProcessForm((current) => ({
    ...current,
    steps: current.steps.map((step, index) =>
      index === mainIndex
        ? { ...step, title: value }
        : step
    )
  }));
};


const updateModalSubStep = (mainIndex, subIndex, value) => {
  setNewProcessForm((current) => ({
    ...current,
    steps: current.steps.map((step, index) =>
      index === mainIndex
        ? {
            ...step,
            subSteps: step.subSteps.map((sub, i) =>
              i === subIndex ? value : sub
            )
          }
        : step
    )
  }));
};

const removeModalMainStep = (mainIndex) => {
  setNewProcessForm((current) => ({
    ...current,
    steps: current.steps.filter((_, index) => index !== mainIndex)
  }));
};

const removeModalSubStep = (mainIndex, subIndex) => {
  setNewProcessForm((current) => ({
    ...current,
    steps: current.steps
      .map((step, index) =>
        index === mainIndex
          ? {
              ...step,
              subSteps: step.subSteps.filter((_, i) => i !== subIndex)
            }
          : step
      )
      .filter((step) => step.subSteps.length > 0)
  }));
};

const closeNewProcessModal = () => {
  setNewProcessModalOwner(null);
  setNewProcessModalTab("details");
  setNewProcessForm({
    companyName: "",
    month: selectedMonth,
    scheduleType: "month",
    startDate: "",
    endDate: "",
    title: "",
    steps: [
      {
        title: "",
        subSteps: [""]
      }
    ]
  });
};

const buildNewProcessSteps = () => {
  let orderCounter = 1;

  return (newProcessForm.steps || []).flatMap((mainStep) =>
    (mainStep.subSteps || [])
      .map((subStep) => ({
        groupTitle: String(mainStep.title || "").trim(),
        title: String(subStep || "").trim(),
        order: orderCounter++,
        isRequired: true
      }))
      .filter((step) => step.groupTitle && step.title)
  );
};

const saveNewProcessFromModal = async () => {
  setErrorMessage("");

  const safeTitle = String(newProcessForm.title || "").trim();
  const isGeneralProcess = !newProcessForm.companyName;
  const scheduleType = newProcessForm.scheduleType === "range" ? "range" : "month";
  const safeStartDate = scheduleType === "range" ? newProcessForm.startDate : "";
  const safeEndDate = scheduleType === "range" ? newProcessForm.endDate || newProcessForm.startDate : "";
  const safeMonth = scheduleType === "range" ? "" : newProcessForm.month;

  if (isGeneralProcess && !safeTitle) {
    alert("Firma seçmeden genel görev oluşturmak için görev başlığı girmelisiniz.");
    setNewProcessModalTab("details");
    return;
  }

  if (scheduleType === "month" && !safeMonth) {
    alert("Ay seçmelisiniz.");
    setNewProcessModalTab("details");
    return;
  }

  if (scheduleType === "range" && !safeStartDate) {
    alert("Tarih aralığı için başlangıç günü seçmelisiniz.");
    setNewProcessModalTab("details");
    return;
  }

  if (scheduleType === "range" && safeEndDate < safeStartDate) {
    alert("Bitiş tarihi başlangıç tarihinden önce olamaz.");
    setNewProcessModalTab("details");
    return;
  }

  const processSteps = buildNewProcessSteps();

  if (processSteps.length === 0) {
    alert("En az bir ana adım ve alt adım girmelisiniz.");
    setNewProcessModalTab("steps");
    return;
  }

  const alreadyExists = processes.some(
    (process) =>
      (isGeneralProcess
        ? !process.companyName &&
          String(process.title || process.templateName || "").trim().toLocaleLowerCase("tr") ===
            safeTitle.toLocaleLowerCase("tr")
        : process.companyName === newProcessForm.companyName) &&
      (scheduleType === "range"
        ? (process.startDate || "") === safeStartDate && (process.endDate || process.startDate || "") === safeEndDate
        : process.month === safeMonth) &&
      process.owner === newProcessModalOwner?.name &&
      process.status !== "archived"
  );

  if (alreadyExists && !(await confirmDialog("Bu başlık/firma, dönem ve sorumlu için açık görev kartı var. Yine de yeni görev oluşturulsun mu?", { title: "Açık görev kartı var", type: "warning", confirmText: "Yine de oluştur" }))) {
    return;
  }

  try {
    const createdReference = await createMonthlyProcess({
      companyName: newProcessForm.companyName,
      companyShortName: getCompanyShortName(newProcessForm.companyName),
      title: safeTitle || getCompanyDisplayName(newProcessForm.companyName),
      month: safeMonth,
      scheduleType,
      startDate: safeStartDate,
      endDate: safeEndDate,
      owner: newProcessModalOwner.name,
      processProjectId,
      template: {
        id: `custom-${Date.now()}`,
        name: safeTitle || "Özel Aylık Süreç",
        steps: processSteps,
        autoRepeatDefault: false,
        recurrence: {
          frequency: "monthly",
          interval: 1,
          createTiming: "periodEnd"
        }
      }
    });

    await writeProcessLog(
      "SUREC_GOREVI_EKLENDI",
      `${safeTitle || getCompanyDisplayName(newProcessForm.companyName)} / ${newProcessModalOwner.name}`
    );

    await notifyTeam({
      processItem: {
        id: createdReference?.id || "",
        companyName: newProcessForm.companyName,
        companyShortName: getCompanyShortName(newProcessForm.companyName),
        title: safeTitle || getCompanyDisplayName(newProcessForm.companyName),
        month: safeMonth,
        startDate: safeStartDate,
        endDate: safeEndDate
      },
      title: tenantMode ? "Yeni görev kartı oluşturuldu" : "Yeni görev atandı",
      body: tenantMode
        ? `${getCurrentUserDisplayName()}, ${safeTitle || getCompanyDisplayName(newProcessForm.companyName)} görev kartını ${newProcessModalOwner.name} kolonunda oluşturdu.`
        : `${getCurrentUserDisplayName()}, ${safeTitle || getCompanyDisplayName(newProcessForm.companyName)} görevini ${newProcessModalOwner.name} sorumlusuna atadı.`
    });

    closeNewProcessModal();
    await loadData();
  } catch (error) {
    console.error("Yeni görev oluşturulamadı:", error);
    setErrorMessage(`Yeni görev oluşturulamadı: ${error.message}`);
  }
};

const openProcessCopyModal = (processItem) => {
  if (!processItem?.id) return;

  const draft = createMonthlyProcessCopyDraft(processItem);
  const firstActiveOwner = orderedKanbanOwners.find((owner) => !isCompletedOwner(owner.name));

  if (!draft.owner || draft.owner === "Atanmamış") {
    draft.owner = firstActiveOwner?.name || "Atanmamış";
  }

  setCopySourceProcess(processItem);
  setCopyProcessForm(draft);
};

const closeProcessCopyModal = () => {
  if (isCopyingProcess) return;
  setCopySourceProcess(null);
  setCopyProcessForm(null);
};

const saveProcessCopy = async () => {
  if (!copySourceProcess?.id || !copyProcessForm) return;

  const safeTitle = String(copyProcessForm.title || "").trim();
  const safeOwner = String(copyProcessForm.owner || "").trim() || "Atanmamış";
  const scheduleType = copyProcessForm.scheduleType === "range" ? "range" : "month";
  const safeMonth = scheduleType === "month" ? copyProcessForm.month : "";
  const safeStartDate = scheduleType === "range" ? copyProcessForm.startDate : "";
  const safeEndDate = scheduleType === "range"
    ? copyProcessForm.endDate || copyProcessForm.startDate
    : "";

  if (!safeTitle) {
    alert("Kopyalanacak görev için görev adı girmelisiniz.");
    return;
  }

  if (scheduleType === "month" && !safeMonth) {
    alert("Yeni görev için dönem seçmelisiniz.");
    return;
  }

  if (scheduleType === "range" && !safeStartDate) {
    alert("Yeni görev için başlangıç tarihi seçmelisiniz.");
    return;
  }

  if (scheduleType === "range" && safeEndDate < safeStartDate) {
    alert("Bitiş tarihi başlangıç tarihinden önce olamaz.");
    return;
  }

  const copyTemplate = createMonthlyProcessCopyTemplate(copySourceProcess, copyProcessForm);

  if (copyTemplate.steps.length === 0) {
    alert("Kaynak görevde kopyalanabilecek bir görev adımı bulunmuyor.");
    return;
  }

  const alreadyExists = processes.some((processItem) =>
    processItem.status !== "archived" &&
    processItem.id !== copySourceProcess.id &&
    processItem.companyName === copyProcessForm.companyName &&
    String(processItem.title || processItem.templateName || "").trim().toLocaleLowerCase("tr-TR") ===
      safeTitle.toLocaleLowerCase("tr-TR") &&
    processItem.owner === safeOwner &&
    (scheduleType === "range"
      ? (processItem.startDate || "") === safeStartDate &&
        (processItem.endDate || processItem.startDate || "") === safeEndDate
      : processItem.month === safeMonth)
  );

  if (alreadyExists && !(await confirmDialog(
    "Bu firma, görev adı, dönem ve kolon için açık bir kart zaten var. Yine de kopya oluşturulsun mu?",
    { title: "Benzer görev kartı var", type: "warning", confirmText: "Yine de kopyala" }
  ))) {
    return;
  }

  setIsCopyingProcess(true);
  setErrorMessage("");

  try {
    const createdReference = await createMonthlyProcess({
      companyName: copyProcessForm.companyName,
      companyShortName: copyProcessForm.companyShortName,
      title: safeTitle,
      month: safeMonth,
      scheduleType,
      startDate: safeStartDate,
      endDate: safeEndDate,
      owner: safeOwner,
      processProjectId,
      template: copyTemplate,
      extraFields: {
        copiedFromProcessId: copySourceProcess.id
      }
    });

    await writeProcessLog(
      "SUREC_GOREVI_KOPYALANDI",
      `${getProcessDisplayTitle(copySourceProcess)} → ${safeTitle} / ${scheduleType === "range" ? `${safeStartDate} - ${safeEndDate}` : formatMonthLabel(safeMonth)} / ${safeOwner}`
    );

    await notifyTeam({
      processItem: {
        id: createdReference?.id || "",
        companyName: copyProcessForm.companyName,
        companyShortName: copyProcessForm.companyShortName,
        title: safeTitle,
        month: safeMonth,
        startDate: safeStartDate,
        endDate: safeEndDate
      },
      title: tenantMode ? "Görev kartı kopyalandı" : "Görev kopyalandı",
      body: `${getCurrentUserDisplayName()}, ${getProcessDisplayTitle(copySourceProcess)} görevini ${safeOwner} kolonuna/sorumlusuna kopyaladı.`
    });

    const createdProcessId = createdReference?.id || "";
    const refreshedProcesses = await loadData();
    const createdProcess = refreshedProcesses.find((processItem) => processItem.id === createdProcessId);

    setCopySourceProcess(null);
    setCopyProcessForm(null);
    if (createdProcess) {
      setSelectedProcess(createdProcess);
    }
  } catch (error) {
    console.error("Görev kopyalanamadı:", error);
    setErrorMessage(`Görev kopyalanamadı: ${error.message}`);
  } finally {
    setIsCopyingProcess(false);
  }
};

  const bulkCreateMonthlyProcesses = async () => {
    const template = templates.find((item) => item.id === selectedTemplateId);

    if (!template) {
      alert("Şablon seçmelisiniz.");
      return;
    }

    if (!selectedMonth) {
      alert("Ay seçmelisiniz.");
      return;
    }

    if (!selectedOwner) {
      alert("Sorumlu seçmelisiniz.");
      return;
    }

    const companyNames = companies.map((company) => company.name).filter(Boolean);

    if (!(await confirmDialog(`${companyNames.length} firma için ${formatMonthLabel(selectedMonth)} döneminde süreç oluşturulacak. Devam edilsin mi?`, { title: "Toplu görev oluştur", type: "warning", confirmText: "Oluştur" }))) {
      return;
    }

    try {
      setIsBulkCreating(true);

      const result = await createMonthlyProcessesBulk({
        companies: companyNames,
        month: selectedMonth,
        template,
        owner: selectedOwner,
        existingProcesses: processes,
        processProjectId
      });

      if (result.createdCount > 0) {
        await writeProcessLog(
          "SUREC_GOREVLERI_TOPLU_EKLENDI",
          `${formatMonthLabel(selectedMonth)} / ${selectedOwner} / ${result.createdCount} görev`
        );
        await notifyTeam({
          processItem: { month: selectedMonth, templateName: template?.name || "" },
          title: "Yeni görevler atandı",
          body: `${getCurrentUserDisplayName()}, ${formatMonthLabel(selectedMonth)} dönemi için ${selectedOwner} sorumlusuna ${result.createdCount} yeni görev atadı.`
        });
      }

      alert(`${result.createdCount} süreç oluşturuldu. ${result.skippedCount} mevcut süreç atlandı.`);
      setShowBulkCreateModal(false);
      await loadData();
    } catch (error) {
      console.error("Toplu süreç oluşturulamadı:", error);
      setErrorMessage(`Toplu süreç oluşturulamadı: ${error.message}`);
    } finally {
      setIsBulkCreating(false);
    }
  };

  const toggleStep = async (processItem, stepIndex) => {
    const currentSteps = getProcessStepsWithProgress(processItem);
    const currentStep = currentSteps[stepIndex];
    const willComplete = !currentStep?.completed;
    let financialVerification = currentStep?.verification || null;
    const isReportDeliveryStep = isExemptionDayReportDeliveryStep(currentStep);
    let reportDeliveryConfirmed = false;

    if (willComplete && isReportDeliveryStep) {
      reportDeliveryConfirmed = await confirmDialog(
        "Muafiyetli Gün Sayısı Raporu hazırlandı ve teslim edildi mi?",
        {
          title: "Muafiyetli Gün Sayısı Raporu",
          type: "info",
          confirmText: "Evet, Teslim Edildi",
          cancelText: "Henüz Değil"
        }
      );

      if (!reportDeliveryConfirmed) return;
    }

    if (willComplete && tenantMode && isMonthlyFinancialImportStep(currentStep)) {
      const periodKey = getProcessPeriodKey(processItem);
      const verificationKey = `${processItem.id}:${stepIndex}`;
      if (!periodKey) {
        alert(
          "Görev kartında doğrulama yapılabilecek bir aylık dönem bulunamadı. Önce görev dönemini YYYY-AA biçiminde düzenleyin.",
          "warning",
          "Dönem bilgisi eksik"
        );
        return;
      }

      setFinancialVerificationKey(verificationKey);
      try {
        const report = await findMonthlyFinancialReport({
          tenantId,
          companyId: processItem.companyId || "",
          companyName: processItem.companyName || "",
          companyShortName: processItem.companyShortName || processItem.firmaKisaAdi || "",
          periodKey
        });

        if (!report) {
          alert(
            `${getCompanyDisplayName(processItem.companyName)} için ${formatMonthLabel(periodKey)} dönemine ait Aylık Excel Bilgi Formu bulunamadı. Raporlar > Aktarımlar bölümünden ilgili firma ve dönem için Excel'i yükledikten sonra bu adımı işaretleyebilirsiniz.`,
            "warning",
            "Aylık mali veri yüklenmemiş"
          );
          return;
        }

        financialVerification = {
          type: "monthlyFinancialReport",
          reportId: report.id,
          periodKey: report.periodKey,
          sourceFileName: report.sourceFile?.name || "Aylık Excel Bilgi Formu",
          verifiedAt: new Date().toISOString(),
          verifiedBy: user?.email || ""
        };
      } catch (verificationError) {
        console.error("Aylık mali veri doğrulanamadı:", verificationError);
        alert(
          `Aylık mali veri kontrolü tamamlanamadı: ${verificationError.message}`,
          "error",
          "Doğrulama hatası"
        );
        return;
      } finally {
        setFinancialVerificationKey("");
      }
    } else if (!willComplete && isMonthlyFinancialImportStep(currentStep)) {
      financialVerification = null;
    }

    const wasReportEligible =
      currentSteps.length >= 4 && currentSteps.slice(0, 4).every((step) => Boolean(step.completed));
    const nextSteps = currentSteps.map((step, index) => {
      if (index !== stepIndex) return step;

      const nextCompleted = !step.completed;

      return {
        ...step,
        completed: nextCompleted,
        completedAt: nextCompleted ? new Date().toISOString() : null,
        completedBy: nextCompleted ? user?.email || "" : "",
        verification: index === stepIndex ? financialVerification : step.verification || null
      };
    });
    const isReportEligible =
      nextSteps.length >= 4 && nextSteps.slice(0, 4).every((step) => Boolean(step.completed));

    try {
      let reportStatusFields = reportDeliveryConfirmed
        ? {
            exemptionDayReportDelivered: true,
            exemptionDayReportAsked: true,
            exemptionDayReportUpdatedAt: new Date().toISOString(),
            exemptionDayReportUpdatedBy: user?.email || ""
          }
        : !willComplete && isReportDeliveryStep
        ? {
            exemptionDayReportDelivered: false,
            exemptionDayReportAsked: true,
            exemptionDayReportUpdatedAt: new Date().toISOString(),
            exemptionDayReportUpdatedBy: user?.email || ""
          }
        : {};

      if (
        isDefaultMonthlyProcessProject &&
        !isReportDeliveryStep &&
        isReportEligible &&
        !wasReportEligible &&
        processItem.exemptionDayReportDelivered !== true
      ) {
        const reportDelivered = await confirmDialog(
          "İlk 4 adım tamamlandı. Muafiyetli Gün Sayısı Raporu gönderildi mi?",
          { title: "Gün Sayısı Raporu Kontrolü", type: "info", confirmText: "Gönderildi", cancelText: "Henüz değil" }
        );

        reportStatusFields = {
          exemptionDayReportDelivered: Boolean(reportDelivered),
          exemptionDayReportAsked: true,
          exemptionDayReportUpdatedAt: new Date().toISOString(),
          exemptionDayReportUpdatedBy: user?.email || ""
        };
      }

      const completedCount = nextSteps.filter((step) => step.completed).length;
      const completionRate = nextSteps.length
        ? Math.round((completedCount / nextSteps.length) * 100)
        : 0;
      const shouldAskToComplete =
        completionRate === 100 &&
        processItem.status !== "completed" &&
        currentSteps.some((step) => !step.completed);

      await updateMonthlyProcessStepProgress({
        processId: processItem.id,
        steps: nextSteps,
        userEmail: user?.email,
        status: processItem.status === "completed" ? "completed" : "active",
        extraFields: reportStatusFields
      });

      let updatedProcess = {
        ...processItem,
        steps: nextSteps,
        stepProgress: nextSteps.reduce((acc, step, index) => {
          acc[String(index)] = {
            completed: Boolean(step.completed),
            completedAt: step.completedAt || null,
            completedBy: step.completedBy || "",
            verification: step.verification || null
          };

          return acc;
        }, {}),
        completionRate,
        status: processItem.status === "completed" ? "completed" : "active",
        ...reportStatusFields
      };

      if (shouldAskToComplete) {
        const markCompleted = await confirmDialog(
          "Tüm adımlar tamamlandı. Bu görev kartı Tamamlananlar tarafına aktarılsın mı?",
          { title: "Görev tamamlandı", type: "success", confirmText: "Tamamlananlara aktar", cancelText: "Aktif kalsın" }
        );

        if (markCompleted) {
          await completeMonthlyProcess({
            processId: processItem.id,
            userEmail: user?.email
          });

          updatedProcess = {
            ...updatedProcess,
            status: "completed",
            owner: "TAMAMLANDI",
            completedAt: new Date().toISOString(),
            completedBy: user?.email || ""
          };
        }
      }

      setSelectedProcess(updatedProcess);

      setProcesses((currentProcesses) =>
        currentProcesses.map((item) =>
          item.id === processItem.id ? updatedProcess : item
        )
      );

      const changedStep = nextSteps[stepIndex];

      if (changedStep?.completed) {
        await writeProcessLog(
          "SUREC_ADIMI_TAMAMLANDI",
          `${getProcessDisplayTitle(updatedProcess)} / ${changedStep.title || "Adım"}`
        );
        await notifyTeam({
          processItem: updatedProcess,
          title: "Görev adımı tamamlandı",
          body: `${getCurrentUserDisplayName()}, ${getProcessDisplayTitle(updatedProcess)} görevinde “${changedStep.title || "Adım"}” adımını tamamladı.`
        });
      }

      await loadData(true);

    } catch (error) {
      console.error("Süreç adımı güncellenemedi:", error);
      setErrorMessage(
        `Süreç adımı güncellenemedi. Kod: ${error.code || "bilinmiyor"} - ${error.message}`
      );
    }
  };

	  const startEditingSelectedSteps = () => {
	    const processSteps = (selectedProcess?.steps || []).map((step) => ({
	      ...step,
	      title: step.title || ""
	    }));

	    setEditableSelectedProcessForm({
	      title: selectedProcess?.title || selectedProcess?.companyShortName || selectedProcess?.companyName || "",
	      companyName: selectedProcess?.companyName || "",
	      companyShortName: selectedProcess?.companyShortName || "",
	      owner: selectedProcess?.owner || "",
	      scheduleType: selectedProcess?.scheduleType === "range" ? "range" : "month",
	      month: selectedProcess?.month || "",
	      startDate: selectedProcess?.startDate || "",
	      endDate: selectedProcess?.endDate || selectedProcess?.startDate || ""
	    });

	    setEditableSelectedSteps(processSteps);
	    setEditableSelectedStepGroups(getGroupedSteps(processSteps));
	    setStepEditScope("single");
    setIsEditingSelectedSteps(true);
  };

	  const cancelEditingSelectedSteps = () => {
	    setIsEditingSelectedSteps(false);
	    setEditableSelectedProcessForm({
	      title: "",
	      companyName: "",
	      companyShortName: "",
	      owner: "",
	      scheduleType: "month",
	      month: "",
	      startDate: "",
	      endDate: ""
	    });
	    setEditableSelectedSteps([]);
    setEditableSelectedStepGroups([]);
    setStepEditScope("single");
  };

  const updateEditableSelectedStepTitle = (stepIndex, value) => {
    setEditableSelectedSteps((current) =>
      current.map((step, index) =>
        index === stepIndex
          ? { ...step, title: value }
          : step
      )
    );
  };

  const updateEditableSelectedGroupTitle = (groupIndex, value) => {
    setEditableSelectedStepGroups((current) =>
      current.map((group, index) =>
        index === groupIndex
          ? { ...group, title: value }
          : group
      )
    );
  };

  const updateEditableSelectedGroupStepTitle = (groupIndex, stepIndex, value) => {
    setEditableSelectedStepGroups((current) =>
      current.map((group, index) =>
        index === groupIndex
          ? {
              ...group,
              steps: group.steps.map((step, subIndex) =>
                subIndex === stepIndex
                  ? { ...step, title: value }
                  : step
              )
            }
          : group
      )
    );
  };

  const addEditableSelectedGroup = () => {
    setEditableSelectedStepGroups((current) => [
      ...current,
      {
        title: "Yeni Ana Adım",
        steps: [
          {
            title: "Yeni Alt Adım",
            completed: false,
            isRequired: true
          }
        ]
      }
    ]);
  };

  const removeEditableSelectedGroup = (groupIndex) => {
    setEditableSelectedStepGroups((current) =>
      current.filter((_, index) => index !== groupIndex)
    );
  };

  const moveEditableSelectedGroup = (groupIndex, direction) => {
    setEditableSelectedStepGroups((current) => {
      const nextIndex = groupIndex + direction;

      if (nextIndex < 0 || nextIndex >= current.length) return current;

      const nextGroups = [...current];
      const [movedGroup] = nextGroups.splice(groupIndex, 1);
      nextGroups.splice(nextIndex, 0, movedGroup);

      return nextGroups;
    });
  };

  const addEditableSelectedGroupStep = (groupIndex) => {
    setEditableSelectedStepGroups((current) =>
      current.map((group, index) =>
        index === groupIndex
          ? {
              ...group,
              steps: [
                ...group.steps,
                {
                  title: "Yeni Alt Adım",
                  completed: false,
                  isRequired: true
                }
              ]
            }
          : group
      )
    );
  };

  const removeEditableSelectedGroupStep = (groupIndex, stepIndex) => {
    setEditableSelectedStepGroups((current) =>
      current
        .map((group, index) =>
          index === groupIndex
            ? {
                ...group,
                steps: group.steps.filter((_, subIndex) => subIndex !== stepIndex)
              }
            : group
        )
        .filter((group) => group.steps.length > 0)
    );
  };

  const moveEditableSelectedGroupStep = (groupIndex, stepIndex, direction) => {
    setEditableSelectedStepGroups((current) =>
      current.map((group, index) => {
        if (index !== groupIndex) return group;

        const nextIndex = stepIndex + direction;

        if (nextIndex < 0 || nextIndex >= group.steps.length) return group;

        const nextSteps = [...group.steps];
        const [movedStep] = nextSteps.splice(stepIndex, 1);
        nextSteps.splice(nextIndex, 0, movedStep);

        return {
          ...group,
          steps: nextSteps
        };
      })
    );
  };

  const flattenEditableSelectedStepGroups = () => {
    let orderCounter = 1;

    return editableSelectedStepGroups.flatMap((group) =>
      group.steps.map((step) => {
        const flattenedStep = {
          ...step,
          order: orderCounter,
          title: String(step.title || "").trim(),
          groupTitle: String(group.title || "").trim(),
          isRequired: step.isRequired !== false
        };

        orderCounter += 1;

        return flattenedStep;
      })
    );
  };

	  const saveEditedSelectedSteps = async () => {
	    if (!selectedProcess?.id || isSavingSelectedSteps) return;

	    const flattenedEditableSteps = flattenEditableSelectedStepGroups();
	    const canEditProcessDetails = !isDefaultMonthlyProcessProject;
	    const safeScheduleType = editableSelectedProcessForm.scheduleType === "range" ? "range" : "month";
	    const safeTitle = String(editableSelectedProcessForm.title || "").trim();
	    const safeCompanyName = String(editableSelectedProcessForm.companyName || "").trim();
	    const safeCompanyShortName = String(editableSelectedProcessForm.companyShortName || "").trim();
	    const safeOwner = String(editableSelectedProcessForm.owner || "").trim() || "Atanmamış";
	    const safeMonth = safeScheduleType === "month" ? editableSelectedProcessForm.month || "" : "";
	    const safeStartDate = safeScheduleType === "range" ? editableSelectedProcessForm.startDate || "" : "";
	    const safeEndDate = safeScheduleType === "range"
	      ? editableSelectedProcessForm.endDate || editableSelectedProcessForm.startDate || ""
	      : "";

	    if (canEditProcessDetails && !safeTitle) {
	      alert("Görev başlığı boş bırakılamaz.");
	      return;
	    }

	    if (canEditProcessDetails && safeScheduleType === "month" && !safeMonth) {
	      alert("Dönem ayı seçilmelidir.");
	      return;
	    }

	    if (canEditProcessDetails && safeScheduleType === "range" && !safeStartDate) {
	      alert("Başlangıç tarihi seçilmelidir.");
	      return;
	    }

    if (editableSelectedStepGroups.some((group) => !String(group.title || "").trim())) {
      alert("Ana adım adları boş bırakılamaz.");
      return;
    }

    if (flattenedEditableSteps.length === 0 || flattenedEditableSteps.some((step) => !step.title)) {
      alert("Alt adım adları boş bırakılamaz.");
      return;
    }

    const normalizedCurrentStructure = (selectedProcess.steps || []).map((step) => ({
      groupTitle: String(step.groupTitle || "").trim(),
      title: String(step.title || "").trim()
    }));
    const normalizedNextStructure = flattenedEditableSteps.map((step) => ({
      groupTitle: String(step.groupTitle || "").trim(),
      title: String(step.title || "").trim()
    }));
    const selectedProcessTemplate = templates.find((templateItem) => templateItem.id === selectedProcess.templateId);
    const normalizedTemplateStructure = (selectedProcessTemplate?.steps || []).map((step) => ({
      groupTitle: String(step.groupTitle || "").trim(),
      title: String(step.title || "").trim()
    }));
    const nextStructureValue = JSON.stringify(normalizedNextStructure);
    const stepStructureChanged =
      JSON.stringify(normalizedCurrentStructure) !== nextStructureValue ||
      (selectedProcessTemplate && JSON.stringify(normalizedTemplateStructure) !== nextStructureValue);
    const matchingProcesses = processes.filter((processItem) =>
      processItem.status !== "archived" &&
      (selectedProcess.templateId
        ? processItem.templateId === selectedProcess.templateId
        : processItem.templateName === selectedProcess.templateName)
    );

    let applyStepsToAll = !canEditProcessDetails && stepEditScope === "all";
    let scopeConfirmedByDialog = false;

    if (tenantMode && stepStructureChanged && matchingProcesses.length > 1) {
      applyStepsToAll = await confirmDialog(
        `Adım yapısında değişiklik yaptınız. Bu değişiklik aynı kanban ve şablondaki ${matchingProcesses.length} görev kartına uygulansın mı?\n\nMevcut tikler korunur, yeni eklenen adımlar işaretsiz gelir. Tamamlanmış görevlerin durumu değiştirilmez.`,
        {
          title: "Adım değişiklikleri bütün görevlere uygulansın mı?",
          type: "warning",
          confirmText: "Tümüne Uygula",
          cancelText: "Yalnızca Bu Görev"
        }
      );
      scopeConfirmedByDialog = true;
    }

    const targetProcesses = applyStepsToAll ? matchingProcesses : [selectedProcess];

	    const confirmText = canEditProcessDetails
	      ? "Bu görev kartının başlık, tarih, sorumlu ve adım bilgileri güncellenecek. Devam edilsin mi?"
	      : applyStepsToAll
	      ? `${targetProcesses.length} görev kartında adım adları güncellenecek. İşaretli/işaretsiz durumlar korunacak. Devam edilsin mi?`
	      : "Bu görev kartındaki adım adları güncellenecek. Devam edilsin mi?";

	    if (!scopeConfirmedByDialog && !(await confirmDialog(confirmText, { title: canEditProcessDetails ? "Görev güncellensin mi?" : "Adımlar güncellensin mi?", type: "warning", confirmText: "Güncelle" }))) return;

	    try {
	      setIsSavingSelectedSteps(true);
      const updatedProcessMap = new Map();

      if (applyStepsToAll && selectedProcess.templateId) {
        if (selectedProcessTemplate) {
          await updateProcessTemplate({
            templateId: selectedProcessTemplate.id,
            name: selectedProcessTemplate.name,
            steps: flattenedEditableSteps.map((step, index) => ({
              order: index + 1,
              groupTitle: step.groupTitle,
              title: step.title,
              isRequired: step.isRequired !== false
            })),
            recurrence: selectedProcessTemplate.recurrence
          });
        }
      }

      for (const processItem of targetProcesses) {
        // Historical tasks may keep their checkbox state in stepProgress instead of
        // directly on steps. Merge both sources before changing the structure so a
        // template update cannot reset previously completed steps.
        const processSteps = getProcessStepsWithProgress(processItem);

        const nextSteps = flattenedEditableSteps.map((editableStep, index) => {
          const originalIndex = Number(editableStep.originalIndex);
          const existingStep = Number.isInteger(originalIndex) && originalIndex >= 0
            ? processSteps[originalIndex] || {}
            : {};
          const persistedEditableStep = { ...editableStep };
          delete persistedEditableStep.originalIndex;

          return {
            ...existingStep,
            ...persistedEditableStep,
            order: index + 1,
            completed: Boolean(existingStep.completed),
            completedAt: existingStep.completedAt || null,
            completedBy: existingStep.completedBy || ""
          };
        });

        const completedCount = nextSteps.filter((step) => step.completed).length;
        const completionRate = nextSteps.length
          ? Math.round((completedCount / nextSteps.length) * 100)
          : 0;
        const stepProgress = nextSteps.reduce((acc, step, stepIndex) => {
          acc[String(stepIndex)] = {
            completed: Boolean(step.completed),
            completedAt: step.completedAt || null,
            completedBy: step.completedBy || "",
            verification: step.verification || null
          };
          return acc;
        }, {});

	        if (canEditProcessDetails && processItem.id === selectedProcess.id) {
	          await updateMonthlyProcessDetails({
	            processId: processItem.id,
	            userEmail: user?.email,
	            data: {
	              title: safeTitle,
	              companyName: safeCompanyName,
	              companyShortName: safeCompanyShortName,
	              owner: safeOwner,
	              scheduleType: safeScheduleType,
	              month: safeMonth,
	              startDate: safeStartDate,
	              endDate: safeEndDate,
	              steps: nextSteps,
	              stepProgress,
	              completionRate,
	              status: processItem.status === "completed" ? "completed" : "active"
	            }
	          });
	        } else {
	          await updateMonthlyProcessSteps({
	            processId: processItem.id,
	            steps: nextSteps,
	            userEmail: user?.email,
	            status: processItem.status === "completed" ? "completed" : "active"
	          });
	        }

	        const updatedProcessBase = {
	          ...processItem,
	          steps: nextSteps,
	          stepProgress,
	          completionRate,
	          status: processItem.status === "completed" ? "completed" : "active"
	        };

	        updatedProcessMap.set(processItem.id, canEditProcessDetails && processItem.id === selectedProcess.id
	          ? {
	              ...updatedProcessBase,
	              title: safeTitle,
	              companyName: safeCompanyName,
	              companyShortName: safeCompanyShortName,
	              owner: safeOwner,
	              scheduleType: safeScheduleType,
	              month: safeMonth,
	              startDate: safeStartDate,
	              endDate: safeEndDate
	            }
	          : updatedProcessBase);
	      }

      setProcesses((currentProcesses) =>
        currentProcesses.map((processItem) =>
          updatedProcessMap.get(processItem.id) || processItem
        )
      );

      const updatedSelectedProcess = updatedProcessMap.get(selectedProcess.id);

      if (updatedSelectedProcess) {
        setSelectedProcess(updatedSelectedProcess);
      }

      cancelEditingSelectedSteps();
	      await writeProcessLog(
	        canEditProcessDetails ? "SUREC_GOREVI_GUNCELLENDI" : "SUREC_ADIMLARI_GUNCELLENDI",
	        canEditProcessDetails
	          ? getProcessDisplayTitle(updatedSelectedProcess || selectedProcess)
	          : stepEditScope === "all"
	          ? `${targetProcesses.length} görev kartı`
	          : getProcessDisplayTitle(updatedSelectedProcess || selectedProcess)
	      );
      await loadData(true);
    } catch (error) {
      console.error("Adım adları güncellenemedi:", error);
      setErrorMessage(`Adım adları güncellenemedi: ${error.message}`);
    } finally {
      setIsSavingSelectedSteps(false);
    }
  };

  const archiveProcess = async (processId) => {
    if (!(await confirmDialog("Bu süreci arşive almak istiyor musunuz?", { title: "Süreç arşive alınsın mı?", type: "danger", confirmText: "Arşive al" }))) return;

    try {
      const processToArchive = processes.find((item) => item.id === processId);
      await archiveMonthlyProcess(processId);
      await writeProcessLog(
        "SUREC_GOREVI_ARSIVLENDI",
        getProcessDisplayTitle(processToArchive || selectedProcess || {})
      );
      if (selectedProcess?.id === processId) {
        cancelEditingSelectedSteps();
        cancelEditingRepeatSettings();
        setReminderDraft(null);
        setSelectedProcess(null);
      }
      await loadData();
    } catch (error) {
      console.error("Süreç arşivlenemedi:", error);
      setErrorMessage(
        `Süreç arşivlenemedi. Kod: ${error.code || "bilinmiyor"} - ${error.message}`
      );
    }
  };

  const deleteProcess = async (processId) => {
    if (!isAdmin) {
      alert("Kalıcı silme işlemi sadece admin kullanıcılar tarafından yapılabilir.");
      return;
    }

    const approved = await confirmDialog(
      "Bu görev kalıcı olarak silinecek. Geri almak için arşivden dönme şansı olmayacak. Emin misiniz?",
      { title: "Görev silinsin mi?", type: "danger", confirmText: "Sil" }
    );

    if (!approved) return;

    try {
      const processToDelete = processes.find((item) => item.id === processId);
      await deleteMonthlyProcess(processId);
      await writeProcessLog(
        "SUREC_GOREVI_SILINDI",
        getProcessDisplayTitle(processToDelete || selectedProcess || {})
      );
      if (selectedProcess?.id === processId) {
        cancelEditingSelectedSteps();
        cancelEditingRepeatSettings();
        setReminderDraft(null);
        setSelectedProcess(null);
      }
      await loadData();
    } catch (error) {
      console.error("Süreç silinemedi:", error);
      setErrorMessage(
        `Süreç silinemedi. Kod: ${error.code || "bilinmiyor"} - ${error.message}`
      );
    }
  };

  // Admin için seçili ayın aktif kartlarını güvenli şekilde arşivleme fonksiyonu
  const archiveAllProcessesForMonth = async () => {
    if (!isAdmin) {
      alert("Bu işlem sadece admin kullanıcılar tarafından yapılabilir.");
      return;
    }

    const targetProcesses = processes.filter(
      (item) => item.month === selectedMonth && item.status !== "archived"
    );

    if (targetProcesses.length === 0) {
      alert("Arşivlenecek görev kartı bulunamadı.");
      return;
    }
    const approved = await confirmDialog(
      `${formatMonthLabel(selectedMonth)} dönemi kapatılacak. Bu döneme ait ${targetProcesses.length} görev kartı arşive alınacak. Bu işlem kartları ekrandan kaldırır ancak verileri silmez. Devam edilsin mi?`,
      { title: "Dönem kapatılsın mı?", type: "danger", confirmText: "Devam et" }
    );

    if (!approved) return;

    const typedConfirmation = await promptDialog(
      `Dönemi kapatma işlemini onaylamak için ARSIVLE yazın. Dönem: ${formatMonthLabel(selectedMonth)} / Kart: ${targetProcesses.length}`,
      { title: "Son onay gerekli", type: "danger", confirmText: "Arşivle", inputPlaceholder: "ARSIVLE" }
    );

    if (typedConfirmation !== "ARSIVLE") {
      alert("İşlem iptal edildi.");
      return;
    }

    try {
      for (const processItem of targetProcesses) {
        await archiveMonthlyProcess(processItem.id);
      }

      await writeProcessLog(
        "SUREC_GOREVLERI_TOPLU_ARSIVLENDI",
        `${formatMonthLabel(selectedMonth)} / ${targetProcesses.length} görev`
      );
      alert(`${targetProcesses.length} görev kartı arşive alındı.`);
      await loadData();
    } catch (error) {
      console.error("Toplu arşivleme hatası:", error);
      setErrorMessage(`Toplu arşivleme hatası: ${error.message}`);
    }
  };


  const resetOwnerForm = () => {
    setNewOwnerName("");
    setEditingOwnerId("");
    setEditingOwnerName("");
  };

  const saveProcessOwner = async () => {
    const targetName = (editingOwnerId ? editingOwnerName : newOwnerName).trim();

    if (!targetName) {
      alert(tenantMode ? "Kolon adı zorunlu." : "Sorumlu adı zorunlu.");
      return;
    }

    const duplicateOwner = owners.find(
      (owner) =>
        owner.id !== editingOwnerId &&
        String(owner.name || "").trim().toLocaleLowerCase("tr") ===
          targetName.toLocaleLowerCase("tr")
    );

    if (duplicateOwner) {
      alert(tenantMode ? "Bu isimde bir kanban kolonu zaten var." : "Bu isimde bir sorumlu zaten var.");
      return;
    }

    try {
      if (editingOwnerId) {
        const currentOwner = owners.find((owner) => owner.id === editingOwnerId);
        const previousName = currentOwner?.name || "";

        await updateProcessOwner({
          ownerId: editingOwnerId,
          data: {
            name: targetName
          }
        });

        const activeOwnerProcesses = processes.filter(
          (processItem) =>
            processItem.status !== "archived" &&
            processItem.owner === previousName
        );

        for (const processItem of activeOwnerProcesses) {
          await updateMonthlyProcessOwner({
            processId: processItem.id,
            owner: targetName
          });
        }
        await writeProcessLog(
          "SUREC_SORUMLUSU_GUNCELLENDI",
          `${previousName} → ${targetName}`
        );
      } else {
        await createProcessOwner(targetName, processProjectId, tenantMode ? { type: "column" } : {});
        await writeProcessLog(tenantMode ? "KANBAN_KOLONU_EKLENDI" : "SUREC_SORUMLUSU_EKLENDI", targetName);
      }

      resetOwnerForm();
      await loadData();
    } catch (error) {
      console.error(tenantMode ? "Kanban kolonu kaydedilemedi:" : "Sorumlu kaydedilemedi:", error);
      alert(`${tenantMode ? "Kanban kolonu" : "Sorumlu"} kaydedilemedi: ${error.message}`);
    }
  };

  const createDefaultKanbanColumns = async () => {
    if (owners.length > 0 || creatingDefaultKanban) return;

    const defaultColumns = ["YAPILACAK", "DEVAM EDİYOR", "KONTROL", "TAMAMLANDI"];
    const baseOrder = Date.now();
    setCreatingDefaultKanban(true);
    setErrorMessage("");

    try {
      await Promise.all(
        defaultColumns.map((name, index) => createProcessOwner(name, processProjectId, {
          type: "column",
          order: baseOrder + index
        }))
      );
      await writeProcessLog("KANBAN_KOLONLARI_OLUSTURULDU", defaultColumns.join(" → "));
      setShowOwnerManagement(false);
      await loadData();
    } catch (error) {
      console.error("Kanban kolonları oluşturulamadı:", error);
      setErrorMessage(`Kanban kolonları oluşturulamadı: ${error.message}`);
    } finally {
      setCreatingDefaultKanban(false);
    }
  };

  const editProcessOwner = (owner) => {
    setEditingOwnerId(owner.id);
    setEditingOwnerName(owner.name || "");
  };

  const removeProcessOwner = async (owner) => {
    const activeOwnerProcesses = processes.filter(
      (processItem) =>
        processItem.status !== "archived" &&
        processItem.owner === owner.name
    );

    if (activeOwnerProcesses.length > 0) {
      alert(
        tenantMode
          ? `${owner.name} kolonunda ${activeOwnerProcesses.length} açık görev kartı var. Önce kartları başka bir kolona taşımalısınız.`
          : `${owner.name} üzerinde ${activeOwnerProcesses.length} açık görev kartı var. Önce kartları başka bir sorumluya taşımalısınız.`
      );
      return;
    }

    if (!(await confirmDialog(
      tenantMode ? `${owner.name} kanban kolonunu silmek istiyor musunuz?` : `${owner.name} sorumlusunu silmek istiyor musunuz?`,
      { title: tenantMode ? "Kanban kolonu silinsin mi?" : "Sorumlu silinsin mi?", type: "danger", confirmText: "Sil" }
    ))) return;

    await deleteProcessOwner(owner.id);
    await writeProcessLog("SUREC_SORUMLUSU_SILINDI", owner.name || owner.id);
    if (editingOwnerId === owner.id) resetOwnerForm();
    await loadData();
  };

  const moveProcessOwner = async (ownerIndex, direction) => {
    const targetIndex = ownerIndex + direction;

    if (targetIndex < 0 || targetIndex >= owners.length) return;

    const currentOwner = owners[ownerIndex];
    const targetOwner = owners[targetIndex];
    const currentOrder = Number(currentOwner.order ?? ownerIndex);
    const targetOrder = Number(targetOwner.order ?? targetIndex);

    await Promise.all([
      updateProcessOwner({
        ownerId: currentOwner.id,
        data: { order: targetOrder }
      }),
      updateProcessOwner({
        ownerId: targetOwner.id,
        data: { order: currentOrder }
      })
    ]);

    await writeProcessLog(
      "SUREC_SORUMLU_SIRASI_GUNCELLENDI",
      `${currentOwner.name || currentOwner.id} ↔ ${targetOwner.name || targetOwner.id}`
    );
    await loadData();
  };

  const moveProcessToOwner = async (processId, ownerName) => {
    if (!processId || !ownerName) return;

    const processToMove = processes.find((item) => item.id === processId);

    if (!processToMove || processToMove.owner === ownerName) {
      setDraggingProcessId(null);
      return;
    }

    const updatedProcess = {
      ...processToMove,
      owner: ownerName
    };

    setProcesses((currentProcesses) =>
      currentProcesses.map((item) =>
        item.id === processId ? updatedProcess : item
      )
    );

    try {
      await updateMonthlyProcessOwner({
        processId,
        owner: ownerName
      });

      await writeProcessLog(
        tenantMode ? "KANBAN_KARTI_TASINDI" : "SUREC_GOREV_SORUMLUSU_DEGISTI",
        `${getProcessDisplayTitle(updatedProcess)} / ${processToMove.owner} → ${ownerName}`
      );
      await notifyTeam({
        processItem: updatedProcess,
        title: tenantMode ? "Görev kartı taşındı" : "Görev sorumlusu değişti",
        body: tenantMode
          ? `${getCurrentUserDisplayName()}, ${getProcessDisplayTitle(updatedProcess)} görev kartını ${processToMove.owner} kolonundan ${ownerName} kolonuna taşıdı.`
          : `${getCurrentUserDisplayName()}, ${getProcessDisplayTitle(updatedProcess)} görevini ${processToMove.owner} sorumlusundan ${ownerName} sorumlusuna aktardı.`
      });

      await loadData(true);
    } catch (error) {
      console.error("Süreç sorumlusu güncellenemedi:", error);
      setErrorMessage(
        `Süreç sorumlusu güncellenemedi. Kod: ${error.code || "bilinmiyor"} - ${error.message}`
      );
      await loadData(true);
    } finally {
      setDraggingProcessId(null);
    }
  };

  const moveProcessToKanbanTarget = async (processItem, targetOwnerName) => {
    if (!processItem?.id || !targetOwnerName) return;

    const targetIsCompleted = isCompletedOwner(targetOwnerName);
    const currentIsCompleted = processItem.status === "completed" || isCompletedOwner(processItem.owner);

    if (
      (targetIsCompleted && currentIsCompleted) ||
      (!targetIsCompleted && processItem.owner === targetOwnerName && processItem.status !== "completed")
    ) {
      return;
    }

    const previousOwner = processItem.owner || "Atanmamış";

    try {
      let updatedProcess;

      if (targetIsCompleted) {
        await completeMonthlyProcess({
          processId: processItem.id,
          userEmail: user?.email
        });

        updatedProcess = {
          ...processItem,
          status: "completed",
          owner: "TAMAMLANDI",
          completedAt: new Date().toISOString(),
          completedBy: user?.email || ""
        };
      } else {
        await updateMonthlyProcessDetails({
          processId: processItem.id,
          data: {
            owner: targetOwnerName,
            status: "active",
            completedAt: null,
            completedBy: ""
          },
          userEmail: user?.email
        });

        updatedProcess = {
          ...processItem,
          owner: targetOwnerName,
          status: "active",
          completedAt: null,
          completedBy: ""
        };
      }

      setProcesses((currentProcesses) =>
        currentProcesses.map((item) =>
          item.id === processItem.id ? updatedProcess : item
        )
      );
      setSelectedProcess((current) =>
        current?.id === processItem.id ? updatedProcess : current
      );

      await writeProcessLog(
        "SUREC_KANBAN_KOLONU_DEGISTI",
        `${getProcessDisplayTitle(updatedProcess)} / ${previousOwner} → ${updatedProcess.owner}`
      );
      await notifyTeam({
        processItem: updatedProcess,
        title: "Görev kanban durumu değişti",
        body: `${getCurrentUserDisplayName()}, ${getProcessDisplayTitle(updatedProcess)} görevini ${previousOwner} kolonundan ${updatedProcess.owner} kolonuna aldı.`
      });

      await loadData(true);
    } catch (error) {
      console.error("Görev kanban kolonu güncellenemedi:", error);
      setErrorMessage(`Görev kanban kolonu güncellenemedi: ${error.message}`);
      await loadData(true);
    }
  };

  useEffect(() => {
    if (!selectedProcess) {
      completionPromptOpenRef.current = "";
      return;
    }

    const steps = getProcessStepsWithProgress(selectedProcess);
    const shouldOfferCompletion =
      selectedProcess.status !== "completed" &&
      !isCompletedOwner(selectedProcess.owner) &&
      steps.length > 0 &&
      steps.every((step) => Boolean(step.completed));

    if (!shouldOfferCompletion || completionPromptOpenRef.current === selectedProcess.id) {
      return;
    }

    completionPromptOpenRef.current = selectedProcess.id;
    let isCancelled = false;

    const askCompletion = async () => {
      const markCompleted = await confirmDialog(
        "Bu görevin tüm adımları tamamlanmış görünüyor. Tamamlananlar tarafına aktarılsın mı?",
        { title: "Görev tamamlandı", type: "success", confirmText: "Tamamlananlara aktar", cancelText: "Aktif kalsın" }
      );

      if (isCancelled || !markCompleted) return;

      try {
        await completeMonthlyProcess({
          processId: selectedProcess.id,
          userEmail: user?.email
        });

        const updatedProcess = {
          ...selectedProcess,
          status: "completed",
          owner: "TAMAMLANDI",
          completedAt: new Date().toISOString(),
          completedBy: user?.email || ""
        };

        setSelectedProcess(updatedProcess);
        setProcesses((currentProcesses) =>
          currentProcesses.map((item) =>
            item.id === selectedProcess.id ? updatedProcess : item
          )
        );

        await writeProcessLog(
          "SUREC_GOREVI_TAMAMLANDI",
          getProcessDisplayTitle(updatedProcess)
        );
        await loadData(true);
      } catch (error) {
        console.error("Görev tamamlandıya alınamadı:", error);
        setErrorMessage(`Görev tamamlandıya alınamadı: ${error.message}`);
      }
    };

    askCompletion();

    return () => {
      isCancelled = true;
    };
  }, [selectedProcess?.id]);

  const saveProcessNote = async (processItem) => {
  if (!processItem?.id) return;

  const noteText = newProcessNoteText.trim();

  if (!noteText) {
    alert("Not alanı boş olamaz.");
    return;
  }

  const existingNotes = Array.isArray(processItem.notes) ? processItem.notes : [];
  const noteMentions = getNoteMentions(noteText);

    const nextNotes = [
      ...existingNotes,
      {
        text: noteText,
        createdAt: new Date().toISOString(),
        createdBy: user?.email || "",
        mentionAll: noteMentions.mentionAll,
        mentions: noteMentions.mentionedUsers.map(({ targetUserId, targetUser }) => ({
          targetUserId,
          email: targetUser.email || "",
          name: targetUser.name || targetUser.displayName || targetUser.fullName || targetUser.email || ""
        })),
        rawMentions: noteMentions.rawMentions
      }
    ];

    const updatedProcess = {
      ...processItem,
      note: "",
      notes: nextNotes
    };

    setSelectedProcess(updatedProcess);
    setNewProcessNoteText("");

    setProcesses((currentProcesses) =>
      currentProcesses.map((item) =>
        item.id === processItem.id ? updatedProcess : item
      )
    );

    try {
      await updateMonthlyProcessNote({
        processId: processItem.id,
        notes: nextNotes
      });

      if (noteMentions.mentionedUsers.length > 0) {
        const notePreview = noteText.length > 140
          ? `${noteText.slice(0, 140)}...`
          : noteText;

        await notifyTeam({
          processItem: updatedProcess,
          title: noteMentions.mentionAll ? "@herkes görev notu" : "Görev notunda etiketlendiniz",
          body: `${getCurrentUserDisplayName()}, ${getProcessDisplayTitle(updatedProcess)} görevinde sizi etiketledi: ${notePreview}`,
          type: "mention",
          targetUsers: noteMentions.mentionedUsers
        });
      }

      await writeProcessLog(
        "SUREC_NOTU_EKLENDI",
        getProcessDisplayTitle(updatedProcess)
      );
      await loadData(true);
    } catch (error) {
      console.error("Süreç notu kaydedilemedi:", error);
      setErrorMessage(`Süreç notu kaydedilemedi: ${error.message}`);
    }
  };

  const deleteProcessNote = async (processItem, noteIndex, isLegacyNote = false) => {
    if (!isAdmin) {
      alert("Not silme işlemi yalnızca admin kullanıcılar tarafından yapılabilir.");
      return;
    }

    if (!processItem?.id) return;

    const confirmed = await window.appConfirm(
      "Bu not silinsin mi?",
      { title: "Not silinsin mi?", type: "danger", confirmText: "Sil" }
    );

    if (!confirmed) return;

    const existingNotes = Array.isArray(processItem.notes) ? processItem.notes : [];
    const nextNotes = isLegacyNote
      ? existingNotes
      : existingNotes.filter((_, index) => index !== noteIndex);

    const updatedProcess = {
      ...processItem,
      note: isLegacyNote ? "" : processItem.note || "",
      notes: nextNotes
    };

    setSelectedProcess(updatedProcess);

    setProcesses((currentProcesses) =>
      currentProcesses.map((item) =>
        item.id === processItem.id ? updatedProcess : item
      )
    );

    try {
      await updateMonthlyProcessNote({
        processId: processItem.id,
        notes: nextNotes
      });

      await writeProcessLog(
        "SUREC_NOTU_SILINDI",
        getProcessDisplayTitle(updatedProcess)
      );
      await loadData(true);
    } catch (error) {
      console.error("Süreç notu silinemedi:", error);
      setErrorMessage(`Süreç notu silinemedi: ${error.message}`);
    }
  };

  const formatFirestoreDate = (value) => {
    if (!value) return "-";

    const date = value.toDate ? value.toDate() : new Date(value);

    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatMonthLabel = (monthValue) => {
    if (!monthValue || !String(monthValue).includes("-")) return monthValue || "-";

    const [yearText, monthText] = String(monthValue).split("-");
    const monthIndex = Number(monthText) - 1;

    const monthNames = [
      "Ocak",
      "Şubat",
      "Mart",
      "Nisan",
      "Mayıs",
      "Haziran",
      "Temmuz",
      "Ağustos",
      "Eylül",
      "Ekim",
      "Kasım",
      "Aralık"
    ];

    if (monthIndex < 0 || monthIndex > 11) return monthValue;

    return `${monthNames[monthIndex]} ${yearText}`;
  };

  const formatDateInputLabel = (dateValue) => {
    if (!dateValue || !String(dateValue).includes("-")) return dateValue || "";

    const [yearText, monthText, dayText] = String(dateValue).split("-");
    if (!yearText || !monthText || !dayText) return dateValue;

    return `${dayText}.${monthText}.${yearText}`;
  };

  const formatProcessPeriod = (processItem) => {
    const startDate = processItem?.startDate || "";
    const endDate = processItem?.endDate || startDate;

    if (startDate) {
      if (!endDate || endDate === startDate) return formatDateInputLabel(startDate);

      return `${formatDateInputLabel(startDate)} - ${formatDateInputLabel(endDate)}`;
    }

    return formatMonthLabel(processItem?.month);
  };

  const getMonthDifferenceFromCurrent = (monthValue) => {
    if (!monthValue || !String(monthValue).includes("-")) return null;

    const [yearText, monthText] = String(monthValue).split("-");
    const year = Number(yearText);
    const month = Number(monthText);

    if (!year || !month) return null;

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;

    return (currentYear - year) * 12 + (currentMonth - month);
  };

  const getDayDifferenceFromCurrent = (dateValue) => {
    if (!dateValue || !String(dateValue).includes("-")) return null;

    const targetDate = new Date(`${dateValue}T00:00:00`);
    if (Number.isNaN(targetDate.getTime())) return null;

    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const targetStart = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());

    return Math.floor((todayStart.getTime() - targetStart.getTime()) / (24 * 60 * 60 * 1000));
  };

  const getProcessDelayInfo = (processItem) => {
    if (processItem.status === "completed") return null;

    if (processItem.endDate || processItem.startDate) {
      const dayDifference = getDayDifferenceFromCurrent(processItem.endDate || processItem.startDate);

      if (dayDifference === null || dayDifference <= 0) return null;

      if (dayDifference >= 7) {
        return {
          label: `Kritik • ${dayDifference} gün geçmiş`,
          background: "#111827",
          color: "#ffffff",
          borderColor: "#111827"
        };
      }

      return {
        label: `Geciken • ${dayDifference} gün geçmiş`,
        background: "#fee2e2",
        color: "#b91c1c",
        borderColor: "#fecaca"
      };
    }

    const monthDifference = getMonthDifferenceFromCurrent(processItem.month);

    if (monthDifference === null || monthDifference <= 0) return null;

    if (monthDifference >= 2) {
      return {
        label: `Kritik • ${monthDifference} ay geçmiş`,
        background: "#111827",
        color: "#ffffff",
        borderColor: "#111827"
      };
    }

    return {
      label: "Geciken • Geçmiş ay",
      background: "#fee2e2",
      color: "#b91c1c",
      borderColor: "#fecaca"
    };
  };

  const activeProcesses = processes.filter((process) => process.status !== "archived");
  const archivedProcesses = processes.filter((process) => process.status === "archived");
  const completedProcesses = activeProcesses.filter((process) => process.status === "completed");
  const ongoingProcesses = activeProcesses.filter((process) => process.status !== "completed");
  const orderedKanbanOwners = useMemo(() => {
    const preferredOrder = Array.isArray(kanbanColumnSettings.order)
      ? kanbanColumnSettings.order
      : [];

    return [...owners].sort((firstOwner, secondOwner) => {
      const firstIndex = preferredOrder.indexOf(getOwnerSettingKey(firstOwner));
      const secondIndex = preferredOrder.indexOf(getOwnerSettingKey(secondOwner));

      if (firstIndex === -1 && secondIndex === -1) {
        return Number(firstOwner.order ?? 0) - Number(secondOwner.order ?? 0);
      }

      if (firstIndex === -1) return 1;
      if (secondIndex === -1) return -1;

      return firstIndex - secondIndex;
    });
  }, [kanbanColumnSettings.order, owners]);
  const getKanbanColumnColor = (owner, index = 0) =>
    kanbanColumnSettings.colors?.[getOwnerSettingKey(owner)] ||
    defaultKanbanColumnColors[index % defaultKanbanColumnColors.length];
  const updateKanbanColumnColor = (owner, color) => {
    const ownerKey = getOwnerSettingKey(owner);
    if (!ownerKey) return;

    saveKanbanColumnSettings((current) => ({
      ...current,
      colors: {
        ...(current.colors || {}),
        [ownerKey]: color
      }
    }));
  };
  const moveKanbanColumn = (owner, direction) => {
    const ownerKey = getOwnerSettingKey(owner);
    const currentOrder = orderedKanbanOwners.map(getOwnerSettingKey);
    const currentIndex = currentOrder.indexOf(ownerKey);
    const targetIndex = currentIndex + direction;

    if (currentIndex < 0 || targetIndex < 0 || targetIndex >= currentOrder.length) return;

    const nextOrder = [...currentOrder];
    const [movedOwnerKey] = nextOrder.splice(currentIndex, 1);
    nextOrder.splice(targetIndex, 0, movedOwnerKey);

    saveKanbanColumnSettings((current) => ({
      ...current,
      order: nextOrder
    }));
  };
  const delayedProcessesCount = ongoingProcesses.filter((processItem) => getProcessDelayInfo(processItem)).length;
  const exemptionDayReportReadyProcesses = activeProcesses.filter(
    (processItem) => processItem.exemptionDayReportDelivered === true
  );
  const exemptionDayReportPendingProcesses = ongoingProcesses.filter(
    (processItem) => processItem.exemptionDayReportDelivered !== true
  );

  const markExemptionDayReportDelivered = async (processItem, delivered = true) => {
    try {
      await updateMonthlyProcessExemptionDayReportStatus({
        processId: processItem.id,
        delivered,
        userEmail: user?.email
      });

      const updatedProcess = {
        ...processItem,
        exemptionDayReportDelivered: Boolean(delivered),
        exemptionDayReportAsked: true,
        exemptionDayReportUpdatedAt: new Date().toISOString(),
        exemptionDayReportUpdatedBy: user?.email || ""
      };

      setProcesses((currentProcesses) =>
        currentProcesses.map((item) => (item.id === processItem.id ? updatedProcess : item))
      );

      if (selectedProcess?.id === processItem.id) {
        setSelectedProcess(updatedProcess);
      }

      await writeProcessLog(
        "SUREC_GUN_SAYISI_RAPOR_DURUMU_GUNCELLENDI",
        `${getProcessDisplayTitle(updatedProcess)} / ${delivered ? "Teslim edildi" : "Bekliyor"}`
      );
      await loadData(true);
    } catch (error) {
      console.error("Gün sayısı raporu durumu güncellenemedi:", error);
      setErrorMessage(`Gün sayısı raporu durumu güncellenemedi: ${error.message}`);
    }
  };

  const filteredOngoingProcesses = ongoingProcesses.filter((process) => {
    const search = processSearchText.toLocaleLowerCase("tr-TR").trim();
    const companyDisplayName = process.companyShortName || process.firmaKisaAdi || getCompanyDisplayName(process.companyName);
    const matchesSearch =
      !search ||
      process.companyName?.toLocaleLowerCase("tr-TR").includes(search) ||
      companyDisplayName?.toLocaleLowerCase("tr-TR").includes(search) ||
      process.title?.toLocaleLowerCase("tr-TR").includes(search) ||
      process.owner?.toLocaleLowerCase("tr-TR").includes(search) ||
      process.templateName?.toLocaleLowerCase("tr-TR").includes(search);

    const matchesDelay = !showDelayedOnly || Boolean(getProcessDelayInfo(process));

    return matchesSearch && matchesDelay;
  });

  const sortedOngoingProcesses = [...filteredOngoingProcesses].sort((a, b) => {
    const periodCompare = getMonthlyProcessSortValue(b) - getMonthlyProcessSortValue(a);

    if (periodCompare !== 0) return periodCompare;

    const aCompany = getProcessDisplayTitle(a) || "";
    const bCompany = getProcessDisplayTitle(b) || "";

    return String(aCompany).localeCompare(String(bCompany), "tr");
  });
  const filteredArchivedProcesses = archivedProcesses
    .filter((process) => {
      const search = processSearchText.toLocaleLowerCase("tr-TR").trim();
      const companyDisplayName = process.companyShortName || process.firmaKisaAdi || getCompanyDisplayName(process.companyName);

      return (
        !search ||
        process.companyName?.toLocaleLowerCase("tr-TR").includes(search) ||
        companyDisplayName?.toLocaleLowerCase("tr-TR").includes(search) ||
        process.title?.toLocaleLowerCase("tr-TR").includes(search) ||
        process.owner?.toLocaleLowerCase("tr-TR").includes(search) ||
        process.templateName?.toLocaleLowerCase("tr-TR").includes(search)
      );
    })
    .sort((a, b) => {
      const firstArchivedAt = a.archivedAt?.toDate?.()?.getTime?.() || new Date(a.archivedAt || 0).getTime() || 0;
      const secondArchivedAt = b.archivedAt?.toDate?.()?.getTime?.() || new Date(b.archivedAt || 0).getTime() || 0;

      if (firstArchivedAt !== secondArchivedAt) return secondArchivedAt - firstArchivedAt;

      const firstCompletedAt = getProcessCompletedDate(a)?.getTime() || 0;
      const secondCompletedAt = getProcessCompletedDate(b)?.getTime() || 0;

      return secondCompletedAt - firstCompletedAt;
    });

      // (Removed duplicate date and delay helper functions)

  const getProgressColor = (rate = 0) => {
    if (rate >= 100) return "#16a34a";
    if (rate >= 80) return "#2563eb";
    if (rate >= 40) return "#f59e0b";
    return "#ef4444";
  };

  const getProcessStageLabel = (processItem = {}) => {
    if (processItem.status === "completed" || isCompletedOwner(processItem.owner)) {
      return "Tamamlandı";
    }

    const steps = getProcessStepsWithProgress(processItem);
    const firstIncompleteIndex = steps.findIndex((step) => !step.completed);

    if (firstIncompleteIndex === -1 && steps.length > 0) {
      return "Tüm adımlar tamamlandı · Kolona taşıma bekliyor";
    }

    const targetStep = steps[firstIncompleteIndex] || steps[0] || {};
    const groupTitle = String(targetStep.groupTitle || "").trim();
    const stepTitle = String(targetStep.title || "").trim();
    const readableTitle = groupTitle || stepTitle || "Aşama bilgisi yok";

    return `${firstIncompleteIndex + 1}. ${readableTitle}`;
  };

  const getProcessStepSummary = (processItem = {}, maxSteps = 10) => {
    const steps = getProcessStepsWithProgress(processItem);

    if (!steps.length) {
      return {
        items: [],
        text: "Adım bilgisi yok"
      };
    }

    const visibleSteps = steps.slice(0, maxSteps).map((step, index) => {
      const groupTitle = String(step.groupTitle || "").trim();
      const stepTitle = String(step.title || "").trim() || "Adım";
      const label = groupTitle && groupTitle !== stepTitle ? `${groupTitle}: ${stepTitle}` : stepTitle;

      return {
        label: `${index + 1}. ${label}`,
        completed: Boolean(step.completed)
      };
    });

    const remainingCount = Math.max(0, steps.length - visibleSteps.length);
    const text = [
      ...visibleSteps.map((step) => `${step.completed ? "✓" : "○"} ${step.label}`),
      ...(remainingCount ? [`+${remainingCount} adım daha`] : [])
    ].join("\n");

    return {
      items: visibleSteps,
      text
    };
  };

  const reportSourceProcesses = periodReportProcesses || activeProcesses;
  const availablePeriodMonths = useMemo(() => {
    return Array.from(
      new Set(
        reportSourceProcesses
          .filter((processItem) => processItem.status !== "archived" && processItem.scheduleType !== "range")
          .map((processItem) => String(processItem.month || ""))
          .filter((monthValue) => /^\d{4}-(0[1-9]|1[0-2])$/.test(monthValue))
      )
    ).sort((a, b) => b.localeCompare(a));
  }, [reportSourceProcesses]);

  const openPeriodReport = async () => {
    setShowPeriodReport(true);
    setPeriodReportLoading(true);
    setPeriodReportSelection("all");

    try {
      const allProcesses = await getAllMonthlyProcesses();
      setPeriodReportProcesses(allProcesses);
    } catch (error) {
      console.error("Dönem raporu görevleri yüklenemedi:", error);
      setPeriodReportProcesses(activeProcesses);
      alert(
        "Tüm kanbanlardaki görevler alınamadı. Rapor, açık kanbandaki kayıtlarla gösteriliyor.",
        "warning",
        "Dönem raporu"
      );
    } finally {
      setPeriodReportLoading(false);
    }
  };

  const periodReport = useMemo(() => {
    const periodProcesses = reportSourceProcesses
      .filter((processItem) => {
        if (processItem.status === "archived") return false;
        if (periodReportSelection === "all") return true;
        if (periodReportSelection === "pending") {
          return processItem.status !== "completed" && !isCompletedOwner(processItem.owner);
        }
        if (processItem.scheduleType === "range") return false;
        return processItem.month === periodReportSelection;
      })
      .sort((a, b) =>
        String(b.month || "").localeCompare(String(a.month || "")) ||
        String(getProcessDisplayTitle(a)).localeCompare(String(getProcessDisplayTitle(b)), "tr")
      );

    const completed = periodProcesses.filter(
      (processItem) => processItem.status === "completed" || isCompletedOwner(processItem.owner)
    );
    const notCompleted = periodProcesses.filter(
      (processItem) => processItem.status !== "completed" && !isCompletedOwner(processItem.owner)
    );
    const exemptionPending = notCompleted.filter(
      (processItem) => processItem.exemptionDayReportDelivered !== true
    );
    const exemptionDelivered = periodProcesses.filter(
      (processItem) => processItem.exemptionDayReportDelivered === true
    );

	    const companyRows = periodProcesses.map((processItem) => {
      const stepSummary = getProcessStepSummary(processItem, 20);
      const steps = getProcessStepsWithProgress(processItem);
      const completedStepCount = steps.filter((step) => Boolean(step.completed)).length;
      const totalStepCount = steps.length;

      return {
        id: processItem.id,
        owner: processItem.owner || "Atanmamış",
        title: getProcessDisplayTitle(processItem),
        companyName: processItem.companyName || "",
        companyShortName: processItem.companyShortName || processItem.firmaKisaAdi || "",
        stage: getProcessStageLabel(processItem),
        progress: processItem.completionRate || 0,
        period: formatProcessPeriod(processItem),
        completed: processItem.status === "completed" || isCompletedOwner(processItem.owner),
        exemptionPending:
          processItem.status !== "completed" &&
          !isCompletedOwner(processItem.owner) &&
          processItem.exemptionDayReportDelivered !== true,
        exemptionDelivered: processItem.exemptionDayReportDelivered === true,
        completedStepCount,
        totalStepCount,
        boardName:
          processProjects.find((projectItem) => projectItem.id === (processItem.processProjectId || processItem.boardId))?.name ||
          processItem.processProjectName ||
          "Aylık Süreçler",
        steps: stepSummary.items,
	        stepsText: stepSummary.text
	      };
	    });
    const visibleCompanyRows = companyRows;
	    const ownerMap = new Map();
	
	    visibleCompanyRows.forEach((companyRow) => {
      const ownerName = companyRow.owner;
      if (!ownerMap.has(ownerName)) {
        ownerMap.set(ownerName, {
          owner: ownerName,
          total: 0,
          completed: 0,
          exemptionPending: 0,
          exemptionDelivered: 0,
          companies: []
        });
      }

      const ownerReport = ownerMap.get(ownerName);
      ownerReport.total += 1;

      if (companyRow.completed) {
        ownerReport.completed += 1;
      }

      if (companyRow.exemptionDelivered) {
        ownerReport.exemptionDelivered += 1;
      }

      if (companyRow.exemptionPending) {
        ownerReport.exemptionPending += 1;
      }

      ownerReport.companies.push(companyRow);
    });

    const ownerRows = Array.from(ownerMap.values())
      .map((ownerReport) => ({
        ...ownerReport,
        completionRate: ownerReport.total ? Math.round((ownerReport.completed / ownerReport.total) * 100) : 0,
        companies: ownerReport.companies
          .sort((a, b) =>
            Number(a.completed) - Number(b.completed) ||
            Number(b.exemptionPending) - Number(a.exemptionPending) ||
            String(a.title).localeCompare(String(b.title), "tr")
          )
      }))
      .sort((a, b) => {
        const completedOwnerCompare = Number(isCompletedOwner(a.owner)) - Number(isCompletedOwner(b.owner));
        if (completedOwnerCompare !== 0) return completedOwnerCompare;
        return b.total - a.total || String(a.owner).localeCompare(String(b.owner), "tr");
      });
    const stageSummary = Array.from(
      periodProcesses.reduce((stageMap, processItem) => {
        const stageLabel = getProcessStageLabel(processItem);
        stageMap.set(stageLabel, (stageMap.get(stageLabel) || 0) + 1);
        return stageMap;
      }, new Map())
    )
      .map(([stage, count]) => ({ stage, count }))
      .sort((a, b) => b.count - a.count || String(a.stage).localeCompare(String(b.stage), "tr"));
    const completedRate = periodProcesses.length ? Math.round((completed.length / periodProcesses.length) * 100) : 0;

    return {
      periodLabel:
        periodReportSelection === "all"
          ? "Tüm Görevler"
          : periodReportSelection === "pending"
          ? "Yalnızca Bekleyen Görevler"
          : formatMonthLabel(periodReportSelection),
      processes: periodProcesses,
      total: periodProcesses.length,
      completed: completed.length,
      ongoing: notCompleted.length,
      completedRate,
      exemptionPending: exemptionPending.length,
      exemptionDelivered: exemptionDelivered.length,
	      stageSummary,
	      companyRows,
      visibleCompanyRows,
	      ownerRows
	    };
  }, [periodReportSelection, processProjects, reportSourceProcesses]);

  const exportPeriodReportToExcel = () => {
    const summaryRows = [
      { Başlık: "Dönem", Değer: periodReport.periodLabel },
      { Başlık: "Toplam süreç", Değer: periodReport.total },
      { Başlık: "Tamamlanan süreç", Değer: periodReport.completed },
      { Başlık: "Devam eden süreç", Değer: periodReport.ongoing },
      { Başlık: "Gün sayısı raporu teslim edilecek", Değer: periodReport.exemptionPending },
      { Başlık: "Gün sayısı raporu teslim edilen", Değer: periodReport.exemptionDelivered }
    ];
    const companyRows = periodReport.ownerRows.flatMap((ownerReport) =>
      ownerReport.companies.map((company) => ({
      Personel: company.owner,
      Firma: company.title,
      "Mevcut Aşama": company.stage,
      "Tamamlanan Adım": `${company.completedStepCount}/${company.totalStepCount}`,
      "Süreç Yüzdesi": `%${company.progress}`,
      "Gün Sayısı Raporu": company.exemptionDelivered ? "Teslim edildi" : company.exemptionPending ? "Bekliyor" : "-",
      "Görev Adımları": company.stepsText,
      Kanban: company.boardName,
      Dönem: company.period
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summaryRows), "Özet");
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(companyRows.length ? companyRows : [{ Personel: "", Firma: "Kayıt bulunmuyor", "Mevcut Aşama": "", "Tamamlanan Adım": "", "Süreç Yüzdesi": "", "Gün Sayısı Raporu": "", "Görev Adımları": "", Kanban: "", Dönem: "" }]),
      "Firma Personel Detayı"
    );
    XLSX.writeFile(
      workbook,
      periodReportSelection === "all"
        ? "Aylik_Surec_Tum_Gorevler.xlsx"
        : periodReportSelection === "pending"
        ? "Aylik_Surec_Tum_Bekleyen_Gorevler.xlsx"
        : `Aylik_Surec_Raporu_${periodReportSelection}.xlsx`
    );
  };

  const exportPeriodReportToPdf = async () => {
    const [{ jsPDF }, autoTableModule] = await Promise.all([
      import("jspdf"),
      import("jspdf-autotable")
    ]);
    const autoTable = autoTableModule.default;
    const arrayBufferToBase64 = (buffer) => {
      const bytes = new Uint8Array(buffer);
      let binary = "";
      const chunkSize = 0x8000;

      for (let index = 0; index < bytes.length; index += chunkSize) {
        const chunk = bytes.subarray(index, index + chunkSize);
        binary += String.fromCharCode.apply(null, chunk);
      }

      return window.btoa(binary);
    };
    const [regularFontResponse, boldFontResponse] = await Promise.all([
      fetch("/fonts/Arial.ttf"),
      fetch("/fonts/Arial-Bold.ttf")
    ]);
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    doc.addFileToVFS("Arial.ttf", arrayBufferToBase64(await regularFontResponse.arrayBuffer()));
    doc.addFileToVFS("Arial-Bold.ttf", arrayBufferToBase64(await boldFontResponse.arrayBuffer()));
    doc.addFont("Arial.ttf", "EcalpArial", "normal");
    doc.addFont("Arial-Bold.ttf", "EcalpArial", "bold");
    doc.setFont("EcalpArial", "normal");
    const pageWidth = doc.internal.pageSize.getWidth();
    const generatedAt = new Date().toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
    doc.setProperties({
      title: `Aylık Süreç Dönem Raporu - ${periodReport.periodLabel}`,
      subject: `${periodReport.periodLabel} dönem süreç raporu`,
      creator: "Argevio Ar-Ge Yönetim Platformu"
    });
    doc.setFont("EcalpArial", "bold");
    doc.setTextColor(18, 66, 84);
    doc.setFontSize(18);
    doc.text("Aylık Süreç Dönem Raporu", 14, 17);

    doc.setFont("EcalpArial", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(
      `${["all", "pending"].includes(periodReportSelection) ? periodReport.periodLabel : `${periodReport.periodLabel} dönemi`} • Oluşturulma: ${generatedAt}`,
      14,
      24
    );

    doc.setDrawColor(14, 165, 233);
    doc.setLineWidth(0.8);
    doc.line(14, 29, pageWidth - 14, 29);

    const summaryCards = [
      ["Toplam Süreç", periodReport.total],
      ["Tamamlanan", periodReport.completed],
      ["Devam Eden", periodReport.ongoing],
      ["Başarı Oranı", `%${periodReport.completedRate}`],
      ["Rapor Bekleyen", periodReport.exemptionPending],
      ["Rapor Teslim", periodReport.exemptionDelivered]
    ];

    autoTable(doc, {
      startY: 35,
      body: [summaryCards.map((card) => `${card[0]}\n${card[1]}`)],
      theme: "plain",
      styles: {
        font: "EcalpArial",
        fontSize: 10,
        fontStyle: "bold",
        textColor: [18, 66, 84],
        cellPadding: 4,
        halign: "center",
        valign: "middle",
        lineColor: [226, 232, 240],
        lineWidth: 0.2,
        fillColor: [248, 250, 252]
      },
      columnStyles: summaryCards.reduce((styles, _card, index) => {
        styles[index] = { cellWidth: (pageWidth - 28) / summaryCards.length };
        return styles;
      }, {})
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["Sorumlu", "Toplam", "Tamamlanan", "Devam Eden", "Gün Sayısı Bekleyen", "Gün Sayısı Teslim", "Oran"]],
      body: periodReport.ownerRows.map((ownerReport) => [
        ownerReport.owner,
        ownerReport.total,
        ownerReport.completed,
        ownerReport.total - ownerReport.completed,
        ownerReport.exemptionPending,
        ownerReport.exemptionDelivered,
        `%${ownerReport.completionRate}`
      ]),
      theme: "grid",
      headStyles: { fillColor: [18, 66, 84], textColor: 255, fontStyle: "bold", font: "EcalpArial" },
      bodyStyles: { font: "EcalpArial", fontSize: 8, textColor: [30, 41, 59] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      styles: { cellPadding: 2.5, lineColor: [226, 232, 240], lineWidth: 0.15 },
      margin: { left: 14, right: 14 }
    });

    if (periodReport.stageSummary.length) {
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 7,
        head: [["Aşama Özeti", "Firma/Süreç"]],
        body: periodReport.stageSummary.map((stageReport) => [stageReport.stage, stageReport.count]),
        theme: "grid",
        headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: "bold", font: "EcalpArial" },
        bodyStyles: { font: "EcalpArial", fontSize: 8, textColor: [30, 41, 59] },
        alternateRowStyles: { fillColor: [239, 246, 255] },
        styles: { cellPadding: 2.5, lineColor: [219, 234, 254], lineWidth: 0.15 },
        margin: { left: 14, right: 14 },
        columnStyles: { 0: { cellWidth: 220 }, 1: { halign: "center", cellWidth: 35 } }
      });
    }

    doc.addPage("a4", "landscape");
    doc.setFont("EcalpArial", "bold");
    doc.setTextColor(18, 66, 84);
    doc.setFontSize(14);
    doc.text("Personel / Firma Süreç Detayı", 14, 16);
    doc.setFont("EcalpArial", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(
      ["all", "pending"].includes(periodReportSelection) ? periodReport.periodLabel : `${periodReport.periodLabel} dönemi`,
      14,
      22
    );

    if (!periodReport.ownerRows.length) {
      autoTable(doc, {
        startY: 28,
        head: [["Personel", "Firma", "Mevcut Aşama", "Adım", "Yüzde", "Gün Sayısı", "Görev Adımları"]],
        body: [["-", "Kayıt bulunmuyor", "-", "-", "-", "-", "-"]],
        theme: "grid",
        tableWidth: 269,
        headStyles: { fillColor: [20, 83, 45], textColor: 255, fontStyle: "bold", font: "EcalpArial" },
        bodyStyles: { font: "EcalpArial", fontSize: 6.6, textColor: [30, 41, 59] },
        styles: { cellPadding: 2, lineColor: [226, 232, 240], lineWidth: 0.12, overflow: "linebreak", valign: "top" },
        margin: { left: 14, right: 14 }
      });
    } else {
      let nextStartY = 30;

      periodReport.ownerRows.forEach((ownerReport) => {
        if (nextStartY > 178) {
          doc.addPage("a4", "landscape");
          nextStartY = 18;
        }

        doc.setFont("EcalpArial", "bold");
        doc.setFontSize(10);
        doc.setTextColor(18, 66, 84);
        doc.text(`${ownerReport.owner} • ${ownerReport.total} devam eden firma/süreç`, 14, nextStartY);

        autoTable(doc, {
          startY: nextStartY + 4,
          head: [["Firma", "Mevcut Aşama", "Adım", "Yüzde", "Gün Sayısı", "Görev Adımları"]],
          body: ownerReport.companies.map((company) => [
            company.title,
            company.stage,
            `${company.completedStepCount}/${company.totalStepCount}`,
            `%${company.progress}`,
            company.exemptionDelivered ? "Teslim edildi" : company.exemptionPending ? "Bekliyor" : "-",
            company.stepsText
          ]),
          theme: "grid",
          tableWidth: 269,
          headStyles: { fillColor: [20, 83, 45], textColor: 255, fontStyle: "bold", font: "EcalpArial" },
          bodyStyles: { font: "EcalpArial", fontSize: 6.6, textColor: [30, 41, 59] },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          styles: { cellPadding: 2, lineColor: [226, 232, 240], lineWidth: 0.12, overflow: "linebreak", valign: "top" },
          margin: { left: 14, right: 14 },
          columnStyles: {
            0: { cellWidth: 58 },
            1: { cellWidth: 52 },
            2: { halign: "center", cellWidth: 16 },
            3: { halign: "center", cellWidth: 16 },
            4: { halign: "center", cellWidth: 25 },
            5: { cellWidth: 102 }
          }
        });

        nextStartY = (doc.lastAutoTable?.finalY || nextStartY) + 8;
      });
    }

    const pageCount = doc.internal.getNumberOfPages();
    for (let pageIndex = 1; pageIndex <= pageCount; pageIndex += 1) {
      doc.setPage(pageIndex);
      doc.setFont("EcalpArial", "normal");
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      doc.text(`Argevio Ar-Ge Yönetim Platformu • Sayfa ${pageIndex}/${pageCount}`, 14, doc.internal.pageSize.getHeight() - 8);
    }

    doc.save(
      periodReportSelection === "all"
        ? "Aylik_Surec_Tum_Gorevler.pdf"
        : periodReportSelection === "pending"
        ? "Aylik_Surec_Tum_Bekleyen_Gorevler.pdf"
        : `Aylik_Surec_Raporu_${periodReportSelection}.pdf`
    );
  };

  const ownerMiniButtonStyle = (disabled = false) => ({
    padding: "7px 9px",
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    background: disabled ? "#f1f5f9" : "#fff",
    color: disabled ? "#94a3b8" : "#475569",
    fontWeight: 900,
    cursor: disabled ? "not-allowed" : "pointer"
  });

  const getDialogPalette = (type) => {
    if (type === "success") {
      return {
        icon: "✓",
        background: "#dcfce7",
        color: "#166534",
        button: "#16a34a"
      };
    }

    if (type === "danger" || type === "error") {
      return {
        icon: "!",
        background: "#fee2e2",
        color: "#b91c1c",
        button: "#dc2626"
      };
    }

    if (type === "warning") {
      return {
        icon: "!",
        background: "#fef3c7",
        color: "#92400e",
        button: "#f59e0b"
      };
    }

    return {
      icon: "i",
      background: "#dbeafe",
      color: "#1d4ed8",
      button: "#124254"
    };
  };

  const getGroupedSteps = (steps = []) => {
    const hasSavedGroups = steps.some((step) => step.groupTitle);

    if (hasSavedGroups) {
      const groupedMap = new Map();

      steps.forEach((step, originalIndex) => {
        const groupTitle = step.groupTitle || "Diğer Adımlar";

        if (!groupedMap.has(groupTitle)) {
          groupedMap.set(groupTitle, {
            title: groupTitle,
            steps: []
          });
        }

        groupedMap.get(groupTitle).steps.push({
          ...step,
          originalIndex
        });
      });

      return Array.from(groupedMap.values());
    }
    const groups = [
      {
        title: "Bilgi Talepleri",
        stepTitles: [
          "Personel yıllık izin/rapor, proje ilerleme, mali veriler istendi",
          "PDKS giriş-çıkış bilgisi istendi"
        ]
      },
      {
        title: "Puantaj Hazırlık ve Giriş",
        stepTitles: [
          "Puantaj exceli oluşturuldu",
          "Ar-Ge faaliyet tür puantajları girildi",
          "Uzaktan çalışma puantajları girildi"
        ]
      },
      {
        title: "Sistem Girişleri",
        stepTitles: [
          "Proje ilerleme verileri sisteme girildi",
          "SMM aylık excel bilgi formu girildi"
        ]
      },
      {
        title: "Kontroller",
        stepTitles: [
          "5746 hizmet listesi kontrolü yapıldı"
        ]
      }
    ];

    const matchedIndexes = new Set();

    const groupedSteps = groups
      .map((group) => {
        const groupSteps = group.stepTitles
          .map((title) => {
            const originalIndex = steps.findIndex(
              (step, index) => !matchedIndexes.has(index) && step.title === title
            );

            if (originalIndex === -1) return null;

            matchedIndexes.add(originalIndex);

            return {
              ...steps[originalIndex],
              originalIndex
            };
          })
          .filter(Boolean);

        return {
          ...group,
          steps: groupSteps
        };
      })
      .filter((group) => group.steps.length > 0);

    const ungroupedSteps = steps
      .map((step, originalIndex) => ({ ...step, originalIndex }))
      .filter((_, originalIndex) => !matchedIndexes.has(originalIndex));

    if (ungroupedSteps.length > 0) {
      groupedSteps.push({
        title: "Diğer Adımlar",
        steps: ungroupedSteps
      });
    }

    return groupedSteps;
  };

  if (!processProject?.id) {
    return (
      <div style={{ height: "calc(100vh - 90px)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div
          style={{
            background: "#fff",
            borderRadius: 18,
            padding: 24,
            boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
            color: "#124254"
          }}
        >
          <h2 style={{ marginTop: 0 }}>Süreç projesi erişimi bulunmuyor</h2>
          <p style={{ marginBottom: 0, color: "#64748b", fontWeight: 700 }}>
            Bu kullanıcıya görüntüleyebileceği bir süreç projesi atanmadı. Admin kullanıcısı, Kullanıcı Yönetimi ekranından süreç projesi erişimi tanımlayabilir.
          </p>
        </div>
      </div>
    );
  }

  return (
   <div style={{ height: "calc(100vh - 90px)", display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {appDialog.open && (() => {
        const palette = getDialogPalette(appDialog.type);

        return (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15,23,42,0.48)",
              zIndex: 12000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 18
            }}
          >
            <div
              style={{
                width: 430,
                maxWidth: "94vw",
                background: "#fff",
                borderRadius: 22,
                boxShadow: "0 24px 70px rgba(15,23,42,0.28)",
                padding: 22,
                border: "1px solid #e2e8f0"
              }}
            >
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 999,
                    background: palette.background,
                    color: palette.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 1000,
                    fontSize: 20,
                    flex: "0 0 42px"
                  }}
                >
                  {palette.icon}
                </div>

                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: "0 0 8px", color: "#124254", fontSize: 18 }}>
                    {appDialog.title}
                  </h3>

                  <div style={{ color: "#475569", fontWeight: 700, lineHeight: 1.5, whiteSpace: "pre-line" }}>
                    {appDialog.message}
                  </div>
                </div>
              </div>

              {appDialog.mode === "prompt" && (
                <input
                  autoFocus
                  value={appDialog.inputValue}
                  onChange={(e) => setAppDialog((current) => ({ ...current, inputValue: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") closeDialog(true);
                    if (e.key === "Escape") closeDialog(false);
                  }}
                  placeholder={appDialog.inputPlaceholder}
                  style={{
                    width: "100%",
                    marginTop: 18,
                    padding: "12px 14px",
                    borderRadius: 12,
                    border: "1px solid #cbd5e1",
                    fontSize: 15,
                    fontWeight: 800,
                    boxSizing: "border-box"
                  }}
                />
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22 }}>
                {appDialog.mode !== "alert" && (
                  <button
                    onClick={() => closeDialog(false)}
                    style={{
                      padding: "11px 14px",
                      borderRadius: 12,
                      border: "1px solid #cbd5e1",
                      background: "#fff",
                      color: "#475569",
                      fontWeight: 900,
                      cursor: "pointer"
                    }}
                  >
                    {appDialog.cancelText}
                  </button>
                )}

                <button
                  autoFocus={appDialog.mode !== "prompt"}
                  onClick={() => closeDialog(true)}
                  style={{
                    padding: "11px 16px",
                    borderRadius: 12,
                    border: "none",
                    background: palette.button,
                    color: "#fff",
                    fontWeight: 900,
                    cursor: "pointer"
                  }}
                >
                  {appDialog.confirmText}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {isAdmin && showProcessProjectManagement && (
        <div
          style={{
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 18,
            padding: 14,
            marginBottom: 14,
            boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
            flex: "0 0 auto"
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 12 }}>
            <div>
              <div style={{ color: "#124254", fontWeight: 1000, fontSize: 16 }}>
                Süreç Projeleri
              </div>
              <div style={{ color: "#64748b", fontWeight: 700, fontSize: 13, marginTop: 3 }}>
                Menü sırası, proje adı ve pasifleştirme işlemlerini buradan yönetin.
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gap: 8 }}>
            {processProjects.map((project, index) => {
              const isSelectedProject = project.id === processProjectId;
              const isDefaultProject = project.id === "monthly-processes";

              return (
                <div
                  key={project.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto auto auto auto",
                    gap: 8,
                    alignItems: "center",
                    padding: "10px 12px",
                    borderRadius: 14,
                    background: isSelectedProject ? "#eef6f9" : "#f8fafc",
                    border: isSelectedProject ? "1px solid #b9dce7" : "1px solid #e2e8f0"
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        color: "#124254",
                        fontWeight: 950,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {project.name}
                    </div>
                    <div style={{ color: "#64748b", fontWeight: 700, fontSize: 12, marginTop: 2 }}>
                      {project.technoparkName && project.technoparkName !== project.name
                        ? project.technoparkName
                        : isSelectedProject ? "Şu an açık proje" : "Süreç projesi"}
                      {isDefaultProject ? " · Varsayılan" : ""}
                    </div>
                  </div>

                  <button
                    disabled={index === 0}
                    onClick={() => moveProcessProject?.(index, -1)}
                    style={{
                      padding: "8px 10px",
                      borderRadius: 10,
                      border: "1px solid #dbe2ea",
                      background: "#fff",
                      color: "#124254",
                      fontWeight: 900,
                      cursor: index === 0 ? "not-allowed" : "pointer",
                      opacity: index === 0 ? 0.45 : 1
                    }}
                  >
                    Yukarı
                  </button>

                  <button
                    disabled={index === processProjects.length - 1}
                    onClick={() => moveProcessProject?.(index, 1)}
                    style={{
                      padding: "8px 10px",
                      borderRadius: 10,
                      border: "1px solid #dbe2ea",
                      background: "#fff",
                      color: "#124254",
                      fontWeight: 900,
                      cursor: index === processProjects.length - 1 ? "not-allowed" : "pointer",
                      opacity: index === processProjects.length - 1 ? 0.45 : 1
                    }}
                  >
                    Aşağı
                  </button>

                  <button
                    onClick={() => renameProcessProject?.(project)}
                    style={{
                      padding: "8px 10px",
                      borderRadius: 10,
                      border: "1px solid #dbe2ea",
                      background: "#fff",
                      color: "#124254",
                      fontWeight: 900,
                      cursor: "pointer"
                    }}
                  >
                    Düzenle
                  </button>

                  <button
                    disabled={isDefaultProject}
                    onClick={() => archiveProcessProject?.(project)}
                    style={{
                      padding: "8px 10px",
                      borderRadius: 10,
                      border: "1px solid #fecaca",
                      background: isDefaultProject ? "#f1f5f9" : "#fff5f5",
                      color: isDefaultProject ? "#94a3b8" : "#b91c1c",
                      fontWeight: 900,
                      cursor: isDefaultProject ? "not-allowed" : "pointer"
                    }}
                  >
                    Pasifleştir
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {processProjects.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 10,
            padding: "8px 10px",
            borderRadius: 16,
            background: isDark ? "#122033" : "#ffffff",
            border: isDark ? "1px solid #263b55" : "1px solid #e2e8f0",
            boxShadow: isDark ? "none" : "0 8px 24px rgba(15,23,42,0.06)"
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              overflowX: "auto",
              paddingBottom: 2,
              flex: 1
            }}
          >
            {processProjects.map((project) => {
              const isSelectedProject = project.id === processProjectId;

              return (
                <button
                  key={project.id}
                  type="button"
                  onClick={() => selectProcessProject?.(project.id)}
                  title={project.technoparkName || project.name}
                  style={{
                    border: isSelectedProject
                      ? "1px solid transparent"
                      : isDark
                      ? "1px solid #2c3d55"
                      : "1px solid #dbe2ea",
                    background: isSelectedProject
                      ? "linear-gradient(135deg, #124254, #0f766e)"
                      : isDark
                      ? "#0f172a"
                      : "#f8fafc",
                    color: isSelectedProject ? "#fff" : isDark ? "#d7e3f1" : "#124254",
                    padding: "9px 13px",
                    borderRadius: 999,
                    fontWeight: 950,
                    fontSize: 13,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    boxShadow: isSelectedProject ? "0 8px 18px rgba(18,66,84,0.18)" : "none"
                  }}
                >
                  {project.name}
                </button>
              );
            })}

            {isEditor && openMailTasks && (
              <button
                type="button"
                onClick={() => openMailTasks?.()}
                style={{
                  border: isDark ? "1px solid #2c3d55" : "1px solid #dbe2ea",
                  background: isDark ? "#0f172a" : "#f8fafc",
                  color: isDark ? "#d7e3f1" : "#124254",
                  padding: "9px 13px",
                  borderRadius: 999,
                  fontWeight: 950,
                  fontSize: 13,
                  cursor: "pointer",
                  whiteSpace: "nowrap"
                }}
              >
                Mail Görevleri
              </button>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center", flex: "0 0 auto" }}>
            {tenantMode && openVioOperator && (
              <button
                type="button"
                onClick={openVioOperator}
                style={{
                  border: "none",
                  background: "linear-gradient(135deg, #1fa98f, #438fe7)",
                  color: "#fff",
                  padding: "9px 13px",
                  borderRadius: 999,
                  fontWeight: 950,
                  fontSize: 13,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  boxShadow: "0 8px 18px rgba(34,148,154,.2)"
                }}
              >
                ✦ Vio Operatör
              </button>
            )}

            <button
              type="button"
              onClick={openPeriodReport}
              style={{
                border: isDark ? "1px solid #315a75" : "1px solid #c7d2fe",
                background: isDark ? "#172947" : "#eef2ff",
                color: isDark ? "#c7d2fe" : "#3730a3",
                padding: "9px 13px",
                borderRadius: 999,
                fontWeight: 950,
                fontSize: 13,
                cursor: "pointer",
                whiteSpace: "nowrap"
              }}
            >
              Dönem Raporu
            </button>

            {isAdmin && (
              <button
                type="button"
                onClick={createNewProcessProject}
                style={{
                  border: "none",
                  background: "#0f766e",
                  color: "#fff",
                  padding: "9px 13px",
                  borderRadius: 999,
                  fontWeight: 950,
                  fontSize: 13,
                  cursor: "pointer",
                  whiteSpace: "nowrap"
                }}
              >
                + Yeni Süreç
              </button>
            )}
          </div>
        </div>
      )}

      {showCompanyAssignmentReport && (
        <div
          onClick={() => setShowCompanyAssignmentReport(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2100,
            background: "rgba(15,23,42,0.42)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: "min(980px, 96vw)",
              maxHeight: "86vh",
              overflow: "hidden",
              background: "#fff",
              borderRadius: 22,
              boxShadow: "0 24px 60px rgba(15,23,42,0.22)",
              display: "flex",
              flexDirection: "column"
            }}
          >
            <div
              style={{
                padding: "20px 22px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                gap: 14,
                alignItems: "center"
              }}
            >
              <div>
                <h2 style={{ margin: 0, color: "#124254", fontSize: 22 }}>
                  Firma Ataması Raporu
                </h2>
                <div style={{ marginTop: 4, color: "#64748b", fontWeight: 800, fontSize: 13 }}>
                  {companyAssignmentReportRows.length} kullanıcıda firma ataması var
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={exportCompanyAssignmentReportToExcel}
                  style={{
                    border: "none",
                    borderRadius: 12,
                    padding: "10px 13px",
                    background: "#16a34a",
                    color: "#fff",
                    fontWeight: 900,
                    cursor: "pointer"
                  }}
                >
                  Excel Al
                </button>

                <button
                  type="button"
                  onClick={printCompanyAssignmentReport}
                  style={{
                    border: "1px solid #dbe2ea",
                    borderRadius: 12,
                    padding: "10px 13px",
                    background: "#f8fafc",
                    color: "#124254",
                    fontWeight: 900,
                    cursor: "pointer"
                  }}
                >
                  PDF / Yazdır
                </button>

                <button
                  type="button"
                  onClick={() => setShowCompanyAssignmentReport(false)}
                  style={{
                    border: "none",
                    borderRadius: 12,
                    padding: "10px 13px",
                    background: "#e5e7eb",
                    color: "#111827",
                    fontWeight: 900,
                    cursor: "pointer"
                  }}
                >
                  Kapat
                </button>
              </div>
            </div>

            <div style={{ padding: 18, overflowY: "auto" }}>
              {companyAssignmentReportRows.length === 0 ? (
                <div
                  style={{
                    padding: 18,
                    borderRadius: 16,
                    background: "#f8fafc",
                    border: "1px solid #e5e7eb",
                    color: "#64748b",
                    fontWeight: 800
                  }}
                >
                  Firma ataması yapılmış kullanıcı bulunmuyor.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 12 }}>
                  {companyAssignmentReportRows.map((row) => (
                    <div
                      key={row.id}
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 16,
                        background: "#f8fafc",
                        padding: 14
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
                        <div>
                          <div style={{ color: "#124254", fontWeight: 1000, fontSize: 16 }}>
                            {row.name}
                          </div>
                          <div style={{ color: "#64748b", fontWeight: 800, fontSize: 12, marginTop: 2 }}>
                            {row.role}
                          </div>
                        </div>
                        <span
                          style={{
                            alignSelf: "flex-start",
                            borderRadius: 999,
                            background: "#e0f2fe",
                            color: "#075985",
                            padding: "6px 10px",
                            fontSize: 12,
                            fontWeight: 1000
                          }}
                        >
                          {row.companies.length} firma
                        </span>
                      </div>

                      <div style={{ display: "grid", gap: 7 }}>
                        {row.companies.map((companyName) => (
                          <div
                            key={companyName}
                            style={{
                              borderRadius: 10,
                              background: "#fff",
                              border: "1px solid #dbe2ea",
                              color: "#334155",
                              padding: "8px 10px",
                              fontSize: 12,
                              fontWeight: 800,
                              lineHeight: 1.35
                            }}
                          >
                            {companyName}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showPeriodReport && (
        <div
          onClick={() => setShowPeriodReport(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2100,
            background: "rgba(15,23,42,0.48)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: "min(1120px, 96vw)",
              maxHeight: "88vh",
              overflow: "hidden",
              background: isDark
                ? "linear-gradient(180deg, #101827 0%, #0b1220 100%)"
                : "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
              color: isDark ? "#e5edf6" : "#124254",
              borderRadius: 22,
              boxShadow: "0 24px 70px rgba(15,23,42,0.28)",
              border: isDark ? "1px solid #26364d" : "1px solid #e5e7eb",
              display: "flex",
              flexDirection: "column"
            }}
          >
            <div
              style={{
                padding: "20px 22px",
                borderBottom: isDark ? "1px solid #26364d" : "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                gap: 14,
                alignItems: "center",
                background: isDark
                  ? "linear-gradient(135deg, rgba(14,165,233,0.16), rgba(20,184,166,0.08))"
                  : "linear-gradient(135deg, #ecfeff, #eff6ff)"
              }}
            >
              <div>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    borderRadius: 999,
                    padding: "5px 10px",
                    background: isDark ? "rgba(14,165,233,0.14)" : "#dbeafe",
                    color: isDark ? "#bae6fd" : "#1d4ed8",
                    fontSize: 11,
                    fontWeight: 1000,
                    marginBottom: 8
                  }}
                >
                  📊 Dönem Özeti
                </div>
                <h2 style={{ margin: 0, color: isDark ? "#e5edf6" : "#124254", fontSize: 24, letterSpacing: "-0.02em" }}>
                  Aylık Süreç Dönem Raporu
                </h2>
                <div style={{ marginTop: 4, color: isDark ? "#9ca3af" : "#64748b", fontWeight: 850, fontSize: 13 }}>
                  {["all", "pending"].includes(periodReportSelection) ? periodReport.periodLabel : `${periodReport.periodLabel} dönemi`} · {periodReport.total} görev kartı · Tüm kanbanlar
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <label
                  style={{
                    display: "grid",
                    gap: 3,
                    color: isDark ? "#cbd5e1" : "#475569",
                    fontSize: 10,
                    fontWeight: 950
                  }}
                >
                  RAPOR DÖNEMİ
                  <select
                    value={periodReportSelection}
                    onChange={(event) => setPeriodReportSelection(event.target.value)}
                    disabled={periodReportLoading}
                    style={{
                      minWidth: 150,
                      border: isDark ? "1px solid #315a75" : "1px solid #bfdbfe",
                      borderRadius: 10,
                      padding: "8px 10px",
                      background: isDark ? "#172947" : "#fff",
                      color: isDark ? "#e5edf6" : "#124254",
                      fontWeight: 900,
                      cursor: periodReportLoading ? "wait" : "pointer"
                    }}
                  >
                    <option value="all">Tüm görevler</option>
                    <option value="pending">Yalnızca bekleyen görevler</option>
                    {availablePeriodMonths.map((monthValue) => (
                      <option key={monthValue} value={monthValue}>
                        {formatMonthLabel(monthValue)}
                      </option>
                    ))}
                  </select>
                </label>

                <button
                  type="button"
                  onClick={exportPeriodReportToPdf}
                  disabled={periodReportLoading}
                  style={{
                    border: "none",
                    borderRadius: 12,
                    padding: "10px 13px",
                    background: "#2563eb",
                    color: "#fff",
                    fontWeight: 900,
                    cursor: "pointer"
                  }}
                >
                  PDF Al
                </button>

                <button
                  type="button"
                  onClick={exportPeriodReportToExcel}
                  disabled={periodReportLoading}
                  style={{
                    border: "none",
                    borderRadius: 12,
                    padding: "10px 13px",
                    background: "#16a34a",
                    color: "#fff",
                    fontWeight: 900,
                    cursor: "pointer"
                  }}
                >
                  Excel Al
                </button>

                <button
                  type="button"
                  onClick={() => setShowPeriodReport(false)}
                  style={{
                    border: "none",
                    borderRadius: 12,
                    padding: "10px 13px",
                    background: isDark ? "#27364d" : "#e5e7eb",
                    color: isDark ? "#e5edf6" : "#111827",
                    fontWeight: 900,
                    cursor: "pointer"
                  }}
                >
                  Kapat
                </button>
              </div>
            </div>

            <div style={{ padding: 18, overflowY: "auto" }}>
              {periodReportLoading && (
                <div
                  style={{
                    marginBottom: 14,
                    padding: "12px 14px",
                    borderRadius: 14,
                    background: isDark ? "rgba(14,165,233,0.12)" : "#eff6ff",
                    border: isDark ? "1px solid #315a75" : "1px solid #bfdbfe",
                    color: isDark ? "#bae6fd" : "#1d4ed8",
                    fontWeight: 900
                  }}
                >
                  Tüm kanbanlardaki aylık görev kartları hazırlanıyor…
                </div>
              )}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(260px, 1.2fr) minmax(260px, 0.8fr)",
                  gap: 14,
                  marginBottom: 16
                }}
              >
                <div
                  style={{
                    borderRadius: 18,
                    padding: 16,
                    border: isDark ? "1px solid #26364d" : "1px solid #dbeafe",
                    background: isDark ? "rgba(22,34,53,0.9)" : "#ffffff",
                    boxShadow: isDark ? "none" : "0 12px 30px rgba(15,23,42,0.06)"
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                    <div>
                      <div style={{ color: isDark ? "#9ca3af" : "#64748b", fontSize: 12, fontWeight: 950 }}>
                        Genel tamamlanma oranı
                      </div>
                      <div style={{ marginTop: 4, fontSize: 34, lineHeight: 1, fontWeight: 1000, color: isDark ? "#fff" : "#0f172a" }}>
                        %{periodReport.completedRate}
                      </div>
                    </div>
                    <div
                      style={{
                        borderRadius: 18,
                        padding: "10px 12px",
                        background: isDark ? "#0f172a" : "#f0fdf4",
                        color: isDark ? "#86efac" : "#166534",
                        fontSize: 12,
                        fontWeight: 1000,
                        textAlign: "right"
                      }}
                    >
                      {periodReport.completed} tamamlanan<br />
                      {periodReport.ongoing} devam eden
                    </div>
                  </div>
                  <div
                    style={{
                      height: 12,
                      borderRadius: 999,
                      background: isDark ? "#0f172a" : "#e2e8f0",
                      overflow: "hidden",
                      marginTop: 14
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.min(100, Math.max(0, periodReport.completedRate))}%`,
                        height: "100%",
                        borderRadius: 999,
                        background: "linear-gradient(90deg, #14b8a6, #22c55e)"
                      }}
                    />
                  </div>
                  <div style={{ marginTop: 10, color: isDark ? "#9ca3af" : "#64748b", fontSize: 12, fontWeight: 800 }}>
                    Bu oran dönem içindeki tamamlanan süreçlerin toplam sürece oranıdır.
                  </div>
                </div>

                <div
                  style={{
                    borderRadius: 18,
                    padding: 16,
                    border: isDark ? "1px solid #26364d" : "1px solid #e2e8f0",
                    background: isDark ? "rgba(22,34,53,0.9)" : "#ffffff",
                    boxShadow: isDark ? "none" : "0 12px 30px rgba(15,23,42,0.06)"
                  }}
                >
                  <div style={{ color: isDark ? "#e5edf6" : "#124254", fontSize: 14, fontWeight: 1000, marginBottom: 10 }}>
                    En yoğun aşamalar
                  </div>
                  <div style={{ display: "grid", gap: 8 }}>
                    {(periodReport.stageSummary.slice(0, 4).length ? periodReport.stageSummary.slice(0, 4) : [{ stage: "Kayıt bulunmuyor", count: 0 }]).map((stageReport) => (
                      <div key={stageReport.stage} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, alignItems: "center" }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ color: isDark ? "#dbeafe" : "#334155", fontSize: 12, fontWeight: 900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {stageReport.stage}
                          </div>
                          <div style={{ height: 6, borderRadius: 999, background: isDark ? "#0f172a" : "#e2e8f0", marginTop: 5, overflow: "hidden" }}>
                            <div
                              style={{
                                width: `${periodReport.total ? Math.max(6, Math.round((stageReport.count / periodReport.total) * 100)) : 0}%`,
                                height: "100%",
                                borderRadius: 999,
                                background: "linear-gradient(90deg, #38bdf8, #2563eb)"
                              }}
                            />
                          </div>
                        </div>
                        <strong style={{ color: isDark ? "#fff" : "#0f172a", fontSize: 13 }}>{stageReport.count}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(5, minmax(130px, 1fr))",
                  gap: 10,
                  marginBottom: 16
                }}
              >
                {[
                  { label: "Toplam Süreç", value: periodReport.total, color: "#0ea5e9" },
                  { label: "Tamamlanan", value: periodReport.completed, color: "#16a34a" },
                  { label: "Devam Eden", value: periodReport.ongoing, color: "#f59e0b" },
                  {
                    label: "Rapor Teslim Edilecek",
                    value: periodReport.ongoing,
                    helper: `${periodReport.exemptionPending} gün sayısı raporu`,
                    color: "#ef4444"
                  },
                  { label: "Rapor Teslim Edilen", value: periodReport.exemptionDelivered, color: "#22c55e" }
                ].map((item) => (
                  <div
                    key={item.label}
                    style={{
                      borderRadius: 14,
                      padding: "12px 14px",
                      border: isDark ? "1px solid #26364d" : "1px solid #e5e7eb",
                      background: isDark ? "#162235" : "#fff",
                      borderLeft: `4px solid ${item.color}`
                    }}
                  >
                    <div style={{ color: isDark ? "#9ca3af" : "#64748b", fontSize: 11, fontWeight: 950, marginBottom: 6 }}>
                      {item.label}
                    </div>
                    <div style={{ fontSize: 26, fontWeight: 1000, color: isDark ? "#fff" : "#124254", lineHeight: 1 }}>
                      {item.value}
                    </div>
                    {item.helper && (
                      <div style={{ marginTop: 6, color: isDark ? "#cbd5e1" : "#64748b", fontSize: 10.5, fontWeight: 900 }}>
                        {item.helper}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {periodReport.ownerRows.length === 0 ? (
                <div
                  style={{
                    padding: 18,
                    borderRadius: 16,
                    background: isDark ? "#162235" : "#f8fafc",
                    border: isDark ? "1px solid #26364d" : "1px solid #e5e7eb",
                    color: isDark ? "#9ca3af" : "#64748b",
                    fontWeight: 850
                  }}
                >
                  Bu dönem için süreç kaydı bulunmuyor.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 12 }}>
                  {periodReport.ownerRows.map((ownerReport) => (
                    <div
                      key={ownerReport.owner}
                      style={{
                        border: isDark ? "1px solid #26364d" : "1px solid #e5e7eb",
                        borderRadius: 16,
                        background: isDark ? "#162235" : "#f8fafc",
                        overflow: "hidden"
                      }}
                    >
                      <div
                        style={{
                          padding: "12px 14px",
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          alignItems: "center",
                          borderBottom: isDark ? "1px solid #26364d" : "1px solid #e5e7eb"
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 16, fontWeight: 1000 }}>
                            {ownerReport.owner}
                          </div>
                          <div style={{ color: isDark ? "#9ca3af" : "#64748b", fontSize: 12, fontWeight: 850, marginTop: 3 }}>
                            {ownerReport.total} görev kartı · {ownerReport.completed} tamamlanan · {ownerReport.exemptionPending} rapor bekliyor
                          </div>
                        </div>
                        <span
                          style={{
                            borderRadius: 999,
                            padding: "6px 10px",
                            background: isDark ? "#27364d" : "#e0f2fe",
                            color: isDark ? "#dbeafe" : "#075985",
                            fontSize: 12,
                            fontWeight: 1000
                          }}
                        >
                          %{ownerReport.completionRate}
                        </span>
                      </div>

                      <div style={{ padding: 12, display: "grid", gap: 10 }}>
                        {ownerReport.companies.map((company) => (
                          <div
                            key={`${ownerReport.owner}-${company.id}`}
                            style={{
                              borderRadius: 12,
                              border: isDark ? "1px solid #26364d" : "1px solid #dbe2ea",
                              background: isDark ? "#101827" : "#fff",
                              padding: 12
                            }}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", marginBottom: 10 }}>
                              <div style={{ minWidth: 0 }}>
                                <strong
                                  title={`${company.title} · ${company.period}`}
                                  style={{
                                    display: "block",
                                    color: isDark ? "#e5edf6" : "#124254",
                                    fontSize: 13,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                  }}
                                >
                                  {company.title}
                                </strong>
                                <div style={{ color: isDark ? "#a7b4c7" : "#64748b", fontSize: 11, fontWeight: 850, marginTop: 3 }}>
                                  {company.boardName} · {company.stage} · {company.completedStepCount}/{company.totalStepCount || 0} adım · {company.period}
                                </div>
                              </div>
                              <span
                                style={{
                                  flex: "0 0 auto",
                                  borderRadius: 999,
                                  padding: "4px 8px",
                                  background: getProgressColor(company.progress),
                                  color: "#fff",
                                  fontSize: 11,
                                  fontWeight: 1000
                                }}
                              >
                                %{company.progress}
                              </span>
                            </div>

                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                                gap: 7
                              }}
                            >
                              {(company.steps?.length ? company.steps : [{ label: "Adım bilgisi yok", completed: false }]).map((step) => (
                                <div
                                  key={`${company.id}-${step.label}`}
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "18px 1fr",
                                    gap: 6,
                                    alignItems: "start",
                                    borderRadius: 10,
                                    padding: "7px 8px",
                                    background: step.completed
                                      ? (isDark ? "rgba(34,197,94,0.14)" : "#ecfdf5")
                                      : (isDark ? "#1d2a3f" : "#f8fafc"),
                                    border: step.completed
                                      ? (isDark ? "1px solid rgba(34,197,94,0.28)" : "1px solid #bbf7d0")
                                      : (isDark ? "1px solid #26364d" : "1px solid #e2e8f0"),
                                    color: isDark ? "#dbeafe" : "#124254",
                                    fontSize: 10.5,
                                    fontWeight: 850,
                                    lineHeight: 1.35
                                  }}
                                >
                                  <span style={{ color: step.completed ? "#16a34a" : (isDark ? "#64748b" : "#94a3b8"), fontWeight: 1000 }}>
                                    {step.completed ? "✓" : "○"}
                                  </span>
                                  <span>{step.label}</span>
                                </div>
                              ))}
                            </div>

                            {company.exemptionPending && (
                              <div
                                style={{
                                  marginTop: 9,
                                  borderRadius: 10,
                                  padding: "7px 9px",
                                  background: isDark ? "rgba(239,68,68,0.14)" : "#fef2f2",
                                  color: isDark ? "#fecaca" : "#b91c1c",
                                  fontSize: 10.5,
                                  fontWeight: 950
                                }}
                              >
                                Gün sayısı raporu teslim bekliyor.
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {errorMessage && (
        <div
          style={{
            background: "#fee2e2",
            border: "1px solid #fecaca",
            color: "#991b1b",
            borderRadius: 14,
            padding: "12px 14px",
            fontWeight: 700,
            marginBottom: 16
          }}
        >
          {errorMessage}
        </div>
      )}

      <div style={{ background: "#fff", borderRadius: 18, padding: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.06)", flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "visible" }}>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginBottom: showDelayedOnly ? 8 : 0 }}>
            {showDelayedOnly && (
            <button
              onClick={() => setShowDelayedOnly(false)}
              style={{
                padding: "8px 12px",
                borderRadius: 10,
                border: "1px solid #fecaca",
                background: "#fff",
                color: "#b91c1c",
                fontWeight: 900,
                cursor: "pointer"
              }}
            >
              Filtreyi Temizle
            </button>
          )}
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "center",
            marginBottom: 8,
            flex: "0 0 auto",
            overflow: "visible",
            paddingBottom: 2,
            position: "relative",
            zIndex: 80
          }}
        >
          <input
            value={processSearchText}
            onChange={(e) => setProcessSearchText(e.target.value)}
            placeholder={tenantMode ? "Firma, kolon veya görev ara..." : "Firma, sorumlu veya süreç ara..."}
            style={{
              flex: "0 0 260px",
              minWidth: 240,
              padding: "10px 12px",
              borderRadius: 11,
              border: isDark ? "1px solid #2c3d55" : "1px solid #dbe2ea",
              background: isDark ? "#0f172a" : "#fff",
              color: isDark ? "#e5edf6" : "#124254",
              fontSize: 13,
              boxSizing: "border-box",
              outline: "none"
            }}
          />

          <div style={{ position: "relative", marginLeft: "auto", flex: "0 0 auto", zIndex: 90 }}>
            <button
              type="button"
              onClick={() => setShowProcessActionsMenu((value) => !value)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 14px",
                borderRadius: 11,
                border: isDark ? "1px solid #2c3d55" : "1px solid #dbe2ea",
                background: showProcessActionsMenu ? (isDark ? "#172234" : "#eef6f8") : (isDark ? "#0f172a" : "#fff"),
                color: isDark ? "#dbeafe" : "#124254",
                fontWeight: 900,
                fontSize: 12,
                cursor: "pointer",
                whiteSpace: "nowrap"
              }}
            >
              İşlemler
              <span style={{ fontSize: 12, opacity: 0.8 }}>{showProcessActionsMenu ? "▲" : "▼"}</span>
            </button>

            {showProcessActionsMenu && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  zIndex: 5000,
                  width: 250,
                  borderRadius: 16,
                  border: isDark ? "1px solid #263b55" : "1px solid #dbe2ea",
                  background: isDark ? "#101827" : "#fff",
                  boxShadow: isDark ? "0 22px 50px rgba(0,0,0,0.36)" : "0 22px 50px rgba(15,23,42,0.16)",
                  padding: 8,
                  display: "grid",
                  gap: 5
                }}
              >
                {isAdmin && (
                  <>
                    <div style={{ padding: "6px 8px 4px", color: isDark ? "#93a4bb" : "#64748b", fontSize: 10, fontWeight: 1000, textTransform: "uppercase", letterSpacing: 0.4 }}>
                      Yönetim
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setShowOwnerManagement((value) => !value);
                        setShowProcessActionsMenu(false);
                      }}
                      style={{
                        border: "none",
                        borderRadius: 11,
                        background: showOwnerManagement ? (isDark ? "#1f3440" : "#e6f4f8") : "transparent",
                        color: isDark ? "#e5edf6" : "#124254",
                        padding: "10px 11px",
                        textAlign: "left",
                        fontWeight: 900,
                        cursor: "pointer"
                      }}
                    >
                      {tenantMode ? "Kanban Kolonları" : "Sorumlu Yönetimi"}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        resetTemplateForm();
                        setShowTemplateCreateModal(true);
                        setShowProcessActionsMenu(false);
                      }}
                      style={{
                        border: "none",
                        borderRadius: 11,
                        background: "transparent",
                        color: isDark ? "#e5edf6" : "#124254",
                        padding: "10px 11px",
                        textAlign: "left",
                        fontWeight: 900,
                        cursor: "pointer"
                      }}
                    >
                      Şablon Oluştur
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setShowBulkCreateModal(true);
                        setShowProcessActionsMenu(false);
                      }}
                      style={{
                        border: "none",
                        borderRadius: 11,
                        background: "transparent",
                        color: isDark ? "#e5edf6" : "#124254",
                        padding: "10px 11px",
                        textAlign: "left",
                        fontWeight: 900,
                        cursor: "pointer"
                      }}
                    >
                      Toplu Oluştur
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        enableRepeatForSelectedPeriod();
                        setShowProcessActionsMenu(false);
                      }}
                      disabled={isBulkRepeatUpdating}
                      style={{
                        border: "none",
                        borderRadius: 11,
                        background: "transparent",
                        color: isBulkRepeatUpdating ? (isDark ? "#64748b" : "#94a3b8") : (isDark ? "#bfdbfe" : "#1d4ed8"),
                        padding: "10px 11px",
                        textAlign: "left",
                        fontWeight: 900,
                        cursor: isBulkRepeatUpdating ? "not-allowed" : "pointer"
                      }}
                    >
                      {isBulkRepeatUpdating ? "Tekrarlı yapılıyor..." : "Tekrarlı Yap"}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setShowProcessProjectManagement((value) => !value);
                        setShowProcessActionsMenu(false);
                      }}
                      style={{
                        border: "none",
                        borderRadius: 11,
                        background: showProcessProjectManagement ? (isDark ? "#1f3440" : "#e6f4f8") : "transparent",
                        color: isDark ? "#e5edf6" : "#124254",
                        padding: "10px 11px",
                        textAlign: "left",
                        fontWeight: 900,
                        cursor: "pointer"
                      }}
                    >
                      Süreç Yönetimi
                    </button>
                  </>
                )}

                <div style={{ height: 1, background: isDark ? "#263b55" : "#e2e8f0", margin: "4px 2px" }} />

                <button
                  type="button"
                  onClick={() => {
                    setShowDelayedOnly((value) => !value);
                    setShowProcessActionsMenu(false);
                  }}
                  style={{
                    border: "none",
                    borderRadius: 11,
                    background: showDelayedOnly ? (isDark ? "#3a1618" : "#fee2e2") : "transparent",
                    color: showDelayedOnly ? (isDark ? "#fca5a5" : "#b91c1c") : (isDark ? "#e5edf6" : "#124254"),
                    padding: "10px 11px",
                    textAlign: "left",
                    fontWeight: 900,
                    cursor: "pointer"
                  }}
                >
                  {showDelayedOnly ? "Tümünü Göster" : `Gecikenler (${delayedProcessesCount})`}
                </button>

                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => {
                      archiveAllProcessesForMonth();
                      setShowProcessActionsMenu(false);
                    }}
                    style={{
                      border: "none",
                      borderRadius: 11,
                      background: isDark ? "rgba(249,115,22,0.12)" : "#fff7ed",
                      color: isDark ? "#fdba74" : "#c2410c",
                      padding: "10px 11px",
                      textAlign: "left",
                      fontWeight: 900,
                      cursor: "pointer"
                    }}
                  >
                    Dönemi Kapat
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            padding: 6,
            borderRadius: 14,
            border: isDark ? "1px solid #263b55" : "1px solid #e2e8f0",
            background: isDark ? "#101827" : "#f8fafc",
            marginBottom: 8,
            flex: "0 0 auto",
            overflowX: "auto",
            overflowY: "hidden",
            scrollbarWidth: "thin"
          }}
        >
          <div style={{ display: "flex", gap: 8, alignItems: "center", flex: "0 0 auto" }}>
            {[
              { key: "active", label: "Aktif Görevler", count: activeProcesses.length },
              { key: "archive", label: "Arşiv", count: archivedProcesses.length }
            ].map((tab) => {
              const isSelected = processListMode === tab.key;

              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => {
                    setProcessListMode(tab.key);
                    if (tab.key === "archive") setShowDelayedOnly(false);
                  }}
                  style={{
                    border: isSelected ? "none" : `1px solid ${isDark ? "#263b55" : "#dbe2ea"}`,
                    background: isSelected ? "#124254" : isDark ? "#0f172a" : "#ffffff",
                    color: isSelected ? "#fff" : isDark ? "#dbeafe" : "#124254",
                    borderRadius: 11,
                    padding: "9px 12px",
                    fontWeight: 950,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    whiteSpace: "nowrap"
                  }}
                >
                  <span>{tab.label}</span>
                  <span
                    style={{
                      borderRadius: 999,
                      padding: "2px 7px",
                      background: isSelected ? "rgba(255,255,255,0.18)" : isDark ? "#1f2937" : "#e2e8f0",
                      color: isSelected ? "#fff" : isDark ? "#dbeafe" : "#124254",
                      fontSize: 12,
                      fontWeight: 950
                    }}
                  >
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>

          {processListMode === "active" && processProjectId === DEFAULT_PROCESS_PROJECT_ID && (
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "stretch",
                justifyContent: "flex-end",
                marginLeft: "auto",
                flex: "1 1 auto",
                minWidth: 460
              }}
            >
            <button
              onClick={() => setReportListMode("pending")}
              style={{
                border: isDark ? "1px solid #7c4a18" : "1px solid #fed7aa",
                borderRadius: 12,
                padding: "7px 10px",
                background: isDark ? "#2a1d12" : "#fff7ed",
                color: isDark ? "#fdba74" : "#9a3412",
                cursor: "pointer",
                textAlign: "left",
                boxShadow: isDark ? "0 8px 24px rgba(0, 0, 0, 0.18)" : "0 8px 24px rgba(15, 23, 42, 0.06)",
                display: "grid",
                gridTemplateColumns: "1fr auto",
                columnGap: 10,
                alignItems: "center",
                flex: "1 1 240px",
                minWidth: 220,
                maxWidth: 360
              }}
            >
              <div>
                <div style={{ fontSize: 11, fontWeight: 950, lineHeight: 1.2 }}>
                  Gün Sayısı Raporu Teslim Edilecek
                </div>
                <div style={{ fontSize: 10, fontWeight: 800, opacity: 0.78, marginTop: 2, lineHeight: 1.15 }}>
                  Henüz teslim edildi işaretlenmemiş tüm firmalar
                </div>
              </div>
              <div style={{ fontSize: 22, fontWeight: 1000, lineHeight: 1 }}>
                {exemptionDayReportPendingProcesses.length}
              </div>
            </button>

            <button
              onClick={() => setReportListMode("delivered")}
              style={{
                border: isDark ? "1px solid #276749" : "1px solid #86efac",
                borderRadius: 12,
                padding: "7px 10px",
                background: isDark ? "#122d22" : "#ecfdf5",
                color: isDark ? "#86efac" : "#166534",
                cursor: "pointer",
                textAlign: "left",
                boxShadow: isDark ? "0 8px 24px rgba(0, 0, 0, 0.18)" : "0 8px 24px rgba(15, 23, 42, 0.06)",
                display: "grid",
                gridTemplateColumns: "1fr auto",
                columnGap: 10,
                alignItems: "center",
                flex: "1 1 220px",
                minWidth: 210,
                maxWidth: 330
              }}
            >
              <div>
                <div style={{ fontSize: 11, fontWeight: 950, lineHeight: 1.2 }}>
                  Gün Sayısı Raporu Teslim Edilen
                </div>
                <div style={{ fontSize: 10, fontWeight: 800, opacity: 0.78, marginTop: 2, lineHeight: 1.15 }}>
                  Raporu gönderildi olarak işaretlenen firmalar
                </div>
              </div>
              <div style={{ fontSize: 22, fontWeight: 1000, lineHeight: 1 }}>
                {exemptionDayReportReadyProcesses.length}
              </div>
            </button>
            </div>
          )}
        </div>

        {processListMode === "active" && isAdmin && showOwnerManagement && (
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 16, padding: 14, marginBottom: 12, flex: "0 0 auto" }}>
            <div
              style={{
                marginBottom: 12,
                padding: "10px 12px",
                borderRadius: 12,
                background: "#eef6f9",
                border: "1px solid #cde7ef",
                color: "#124254",
                fontWeight: 800,
                fontSize: 13
              }}
            >
              {tenantMode
                ? `Bu kanban kolonları yalnızca “${processProject?.name || "Aylık Süreçler"}” teknoparkı için geçerlidir.`
                : `Bu sorumlu kolonları yalnızca “${processProject?.name || "Aylık Süreçler"}” süreç projesi için geçerlidir.`}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 10, alignItems: "center", marginBottom: 12 }}>
              <input
                value={editingOwnerId ? editingOwnerName : newOwnerName}
                onChange={(e) =>
                  editingOwnerId
                    ? setEditingOwnerName(e.target.value)
                    : setNewOwnerName(e.target.value)
                }
                placeholder={tenantMode ? "Kolon adı (örn. Evrak Bekleniyor)" : "Sorumlu adı"}
                style={{
                  width: "100%",
                  padding: "11px 12px",
                  borderRadius: 10,
                  border: "1px solid #dbe2ea",
                  fontSize: 14,
                  boxSizing: "border-box"
                }}
              />

              {editingOwnerId && (
                <button
                  onClick={resetOwnerForm}
                  style={{
                    padding: "11px 14px",
                    borderRadius: 10,
                    border: "1px solid #cbd5e1",
                    background: "#fff",
                    color: "#475569",
                    fontWeight: 900,
                    cursor: "pointer"
                  }}
                >
                  Vazgeç
                </button>
              )}

              <button
                onClick={saveProcessOwner}
                style={{
                  padding: "11px 16px",
                  borderRadius: 10,
                  border: "none",
                  background: "#124254",
                  color: "#fff",
                  fontWeight: 900,
                  cursor: "pointer"
                }}
              >
                {editingOwnerId ? "Güncelle" : "Ekle"}
              </button>
            </div>

            {owners.length === 0 ? (
              <div style={{ display: "grid", gap: 10 }}>
                <div style={{ color: "#64748b", fontWeight: 800 }}>
                  {tenantMode
                    ? "Henüz kanban kolonu yok. Kolonları tek tek ekleyebilir veya varsayılan iş akışını oluşturabilirsiniz."
                    : "Henüz sorumlu yok. İlk sorumluyu ekleyerek Kanban kolonlarını oluşturabilirsiniz."}
                </div>
                {tenantMode && (
                  <button
                    type="button"
                    disabled={creatingDefaultKanban}
                    onClick={createDefaultKanbanColumns}
                    style={{
                      justifySelf: "start",
                      padding: "10px 14px",
                      borderRadius: 10,
                      border: "none",
                      background: "#0f766e",
                      color: "#fff",
                      fontWeight: 900,
                      cursor: creatingDefaultKanban ? "wait" : "pointer",
                      opacity: creatingDefaultKanban ? 0.7 : 1
                    }}
                  >
                    {creatingDefaultKanban ? "Oluşturuluyor..." : "Varsayılan Kanbanı Oluştur"}
                  </button>
                )}
              </div>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {owners.map((owner, index) => {
                  const ownerProcessCount = activeProcesses.filter(
                    (processItem) => processItem.owner === owner.name
                  ).length;

                  return (
                    <div
                      key={owner.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto auto auto auto",
                        gap: 8,
                        alignItems: "center",
                        background: "#fff",
                        border: "1px solid #e2e8f0",
                        borderRadius: 12,
                        padding: 10
                      }}
                    >
                      <div>
                        <strong style={{ color: "#124254" }}>{owner.name}</strong>
                        <span style={{ marginLeft: 8, color: "#64748b", fontWeight: 800, fontSize: 12 }}>
                          {ownerProcessCount} kart
                        </span>
                      </div>

                      <button
                        disabled={index === 0}
                        onClick={() => moveProcessOwner(index, -1)}
                        style={ownerMiniButtonStyle(index === 0)}
                      >
                        ↑
                      </button>

                      <button
                        disabled={index === owners.length - 1}
                        onClick={() => moveProcessOwner(index, 1)}
                        style={ownerMiniButtonStyle(index === owners.length - 1)}
                      >
                        ↓
                      </button>

                      <button
                        onClick={() => editProcessOwner(owner)}
                        style={{ ...ownerMiniButtonStyle(false), color: "#2563eb", borderColor: "#bfdbfe" }}
                      >
                        Düzenle
                      </button>

                      <button
                        onClick={() => removeProcessOwner(owner)}
                        style={{ ...ownerMiniButtonStyle(false), color: "#dc2626", borderColor: "#fecaca" }}
                      >
                        Sil
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div style={{ color: "#64748b", fontWeight: 700 }}>Yükleniyor...</div>
        ) : processListMode === "archive" ? (
          <div
            style={{
              display: "grid",
              gap: 10,
              overflowY: "auto",
              paddingRight: 6,
              flex: 1,
              minHeight: 0
            }}
          >
            {filteredArchivedProcesses.length === 0 ? (
              <div
                style={{
                  color: isDark ? "#9fb0c4" : "#64748b",
                  fontWeight: 800,
                  padding: 18,
                  borderRadius: 16,
                  border: isDark ? "1px solid #263b55" : "1px solid #e2e8f0",
                  background: isDark ? "#101827" : "#f8fafc"
                }}
              >
                Arşivde gösterilecek görev bulunmuyor.
              </div>
            ) : (
              filteredArchivedProcesses.map((processItem) => (
                <button
                  key={processItem.id}
                  type="button"
                  onClick={() => setSelectedProcess(processItem)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(260px, 1.5fr) minmax(140px, 0.7fr) minmax(140px, 0.7fr) minmax(120px, 0.5fr) auto",
                    gap: 12,
                    alignItems: "center",
                    textAlign: "left",
                    width: "100%",
                    padding: "12px 14px",
                    borderRadius: 14,
                    border: isDark ? "1px solid #263b55" : "1px solid #e2e8f0",
                    background: isDark ? "#101827" : "#ffffff",
                    color: isDark ? "#e5edf6" : "#124254",
                    cursor: "pointer",
                    boxShadow: isDark ? "none" : "0 4px 14px rgba(15,23,42,0.05)"
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 1000, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {getProcessDisplayTitle(processItem)}
                    </div>
                    <div style={{ color: isDark ? "#9fb0c4" : "#64748b", fontSize: 12, fontWeight: 800, marginTop: 4 }}>
                      {processItem.companyName || processItem.title || "Genel görev"}
                    </div>
                  </div>

                  <div>
                    <div style={{ color: isDark ? "#9fb0c4" : "#64748b", fontSize: 11, fontWeight: 900 }}>
                      {tenantMode ? "Son Kolon" : "Sorumlu"}
                    </div>
                    <div style={{ fontWeight: 950, marginTop: 3 }}>
                      {processItem.owner || "Atanmamış"}
                    </div>
                  </div>

                  <div>
                    <div style={{ color: isDark ? "#9fb0c4" : "#64748b", fontSize: 11, fontWeight: 900 }}>
                      Dönem / Tarih
                    </div>
                    <div style={{ fontWeight: 950, marginTop: 3 }}>
                      {formatProcessPeriod(processItem)}
                    </div>
                  </div>

                  <div>
                    <div style={{ color: isDark ? "#9fb0c4" : "#64748b", fontSize: 11, fontWeight: 900 }}>
                      Tamamlanma
                    </div>
                    <div style={{ fontWeight: 1000, color: getProgressColor(processItem.completionRate || 0), marginTop: 3 }}>
                      %{processItem.completionRate || 0}
                    </div>
                  </div>

                  <div
                    style={{
                      justifySelf: "end",
                      borderRadius: 999,
                      padding: "6px 10px",
                      background: isDark ? "#27364d" : "#f1f5f9",
                      color: isDark ? "#dbeafe" : "#475569",
                      fontSize: 12,
                      fontWeight: 950,
                      whiteSpace: "nowrap"
                    }}
                  >
                    Arşiv: {formatFirestoreDate(processItem.archivedAt)}
                  </div>
                </button>
              ))
            )}
          </div>
        ) : owners.length === 0 ? (
          tenantMode ? (
            <div
              style={{
                display: "grid",
                justifyItems: "center",
                alignContent: "center",
                gap: 12,
                minHeight: 260,
                padding: 24,
                textAlign: "center",
                borderRadius: 18,
                border: isDark ? "1px dashed #334155" : "1px dashed #cbd5e1",
                background: isDark ? "#101827" : "#f8fafc"
              }}
            >
              <div style={{ color: isDark ? "#e5edf6" : "#124254", fontWeight: 1000, fontSize: 18 }}>
                Önce kanban kolonlarını oluşturalım
              </div>
              <div style={{ color: isDark ? "#9fb0c4" : "#64748b", fontWeight: 750, maxWidth: 560 }}>
                Varsayılan akış; Yapılacak, Devam Ediyor, Kontrol ve Tamamlandı kolonlarını kurar. Ardından kolonlardaki + düğmesiyle görev kartı ve kart adımlarını ekleyebilirsiniz.
              </div>
              <button
                type="button"
                disabled={creatingDefaultKanban}
                onClick={createDefaultKanbanColumns}
                style={{
                  padding: "11px 16px",
                  borderRadius: 11,
                  border: "none",
                  background: "#0f766e",
                  color: "#fff",
                  fontWeight: 950,
                  cursor: creatingDefaultKanban ? "wait" : "pointer",
                  opacity: creatingDefaultKanban ? 0.7 : 1
                }}
              >
                {creatingDefaultKanban ? "Kanban oluşturuluyor..." : "Varsayılan Kanbanı Oluştur"}
              </button>
              <button
                type="button"
                onClick={() => setShowOwnerManagement(true)}
                style={{ border: "none", background: "transparent", color: "#0f766e", fontWeight: 900, cursor: "pointer" }}
              >
                Kolonları kendim belirlemek istiyorum
              </button>
            </div>
          ) : (
            <div style={{ color: "#64748b", fontWeight: 700 }}>
              Sorumlu kolonları henüz tanımlı değil. “Sorumlu Yönetimi” alanından ilk sorumluyu ekleyebilirsiniz.
            </div>
          )
        ) : (
          <div style={{ display: "flex", gap: 14, alignItems: "stretch", overflowX: "auto", overflowY: "hidden", flex: 1, minHeight: 0, paddingBottom: 8 }}>
            {orderedKanbanOwners.map((owner, ownerColumnIndex) => {
              const ownerProcesses = isCompletedOwner(owner.name)
                ? activeProcesses
                    .filter((processItem) => processItem.status === "completed" || isCompletedOwner(processItem.owner))
                    .filter((processItem) => {
                      const search = processSearchText.toLocaleLowerCase("tr-TR").trim();
                      const companyDisplayName = processItem.companyShortName || processItem.firmaKisaAdi || getCompanyDisplayName(processItem.companyName);

                      return (
                        !search ||
                        processItem.companyName?.toLocaleLowerCase("tr-TR").includes(search) ||
                        companyDisplayName?.toLocaleLowerCase("tr-TR").includes(search) ||
                        processItem.title?.toLocaleLowerCase("tr-TR").includes(search) ||
                        processItem.owner?.toLocaleLowerCase("tr-TR").includes(search) ||
                        processItem.templateName?.toLocaleLowerCase("tr-TR").includes(search)
                      );
                    })
                    .sort((a, b) => {
                      const firstDate = getProcessCompletedDate(a)?.getTime() || 0;
                      const secondDate = getProcessCompletedDate(b)?.getTime() || 0;

                      return secondDate - firstDate;
                    })
                : sortedOngoingProcesses.filter(
                    (processItem) => processItem.owner === owner.name
                  );
              const columnColor = getKanbanColumnColor(owner, ownerColumnIndex);
              const visibleColumnColor = isDark ? "#162235" : columnColor;
              const visibleDropColor = isDark ? "#123044" : "#e0f2fe";
              const columnBorderColor = isDark ? "#26364d" : "#e2e8f0";
              const columnTitleColor = isDark ? "#e5edf6" : "#124254";
              const countBadgeBackground = isDark ? "#27364d" : "#e2e8f0";
              const countBadgeColor = isDark ? "#e5edf6" : "#124254";

              return (
                <div
                  key={owner.id}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const processId = e.dataTransfer.getData("processId");
                    moveProcessToOwner(processId, owner.name);
                  }}
                  style={{
                    background: draggingProcessId ? visibleDropColor : visibleColumnColor,
                    borderRadius: 16,
                    padding: 12,
                    height: "100%",
                    minHeight: 0,
                    border: `1px solid ${columnBorderColor}`,
                    minWidth: 260,
                    maxWidth: 260,
                    flex: "0 0 260px",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden"
                  }}
                >
                  <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
                        <strong style={{ color: columnTitleColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{owner.name}</strong>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 5, flex: "0 0 auto" }}>
                        {!isCompletedOwner(owner.name) && (
                          <button
                            title="Yeni görev kartı oluştur"
                            onClick={() => {
                              setNewProcessModalOwner(owner);
                              setNewProcessForm((current) => ({
                                ...current,
                                month: selectedMonth
                              }));
                            }}
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: 999,
                              border: "none",
                              background: "#124254",
                              color: "#fff",
                              fontWeight: 900,
                              cursor: "pointer",
                              flex: "0 0 auto",
                              lineHeight: 1
                            }}
                          >
                            +
                          </button>
                        )}
                        <button
                          title="Kolonu sola taşı"
                          disabled={ownerColumnIndex === 0}
                          onClick={() => moveKanbanColumn(owner, -1)}
                          style={{
                            ...ownerMiniButtonStyle(ownerColumnIndex === 0),
                            width: 24,
                            height: 24,
                            padding: 0,
                            lineHeight: 1
                          }}
                        >
                          ←
                        </button>
                        <button
                          title="Kolonu sağa taşı"
                          disabled={ownerColumnIndex === orderedKanbanOwners.length - 1}
                          onClick={() => moveKanbanColumn(owner, 1)}
                          style={{
                            ...ownerMiniButtonStyle(ownerColumnIndex === orderedKanbanOwners.length - 1),
                            width: 24,
                            height: 24,
                            padding: 0,
                            lineHeight: 1
                          }}
                        >
                          →
                        </button>

                        <input
                          title="Kolon rengini değiştir"
                          aria-label={`${owner.name} kolon rengini değiştir`}
                          type="color"
                          value={columnColor}
                          onChange={(event) => updateKanbanColumnColor(owner, event.target.value)}
                          style={{
                            width: 24,
                            height: 24,
                            border: "1px solid #cbd5e1",
                            borderRadius: 8,
                            padding: 0,
                            background: "transparent",
                            cursor: "pointer"
                          }}
                        />
                        <span style={{ background: countBadgeBackground, color: countBadgeColor, borderRadius: 999, padding: "4px 8px", fontSize: 12, fontWeight: 900 }}>
                          {ownerProcesses.length}
                        </span>
                      </div>
                    </div>
                  </div>

                  {ownerProcesses.length === 0 ? (
                    <div style={{ color: "#94a3b8", fontSize: 13, fontWeight: 700, flex: 1 }}>
                      {tenantMode ? "Görev kartı yok." : "Süreç yok."}
                    </div>
                  ) : (
                    <div style={{ display: "grid", gap: 10, alignContent: "start", overflowY: "auto", overflowX: "hidden", paddingRight: 4, flex: 1, minHeight: 0 }}>
                      {ownerProcesses.map((processItem) => (
                        <div
                          key={processItem.id}
                          draggable
                          onDragStart={(e) => {
                            setDraggingProcessId(processItem.id);
                            e.dataTransfer.setData("processId", processItem.id);
                            e.dataTransfer.effectAllowed = "move";
                          }}
                          onDragEnd={() => setDraggingProcessId(null)}
                          onClick={() => setSelectedProcess(processItem)}
                          style={{
                            border: "1px solid #e5e7eb",
                            borderLeft: `4px solid ${
                              getProcessDelayInfo(processItem)?.background === "#111827"
                                ? "#111827"
                                : getProcessDelayInfo(processItem)
                                ? "#ef4444"
                                : getProgressColor(processItem.completionRate || 0)
                            }`,
                            borderRadius: 12,
                            padding: 12,
                            background: "#fff",
                            cursor: "grab",
                            boxShadow: "0 4px 12px rgba(15,23,42,0.08)"
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                            <strong style={{ color: "#124254", fontSize: 13, lineHeight: 1.4 }}>
                              {getProcessDisplayTitle(processItem)}
                            </strong>
                            {isEditor && (
                              <button
                                type="button"
                                title="Görevi Kopyala"
                                aria-label="Görevi Kopyala"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openProcessCopyModal(processItem);
                                }}
                                style={{
                                  width: 26,
                                  height: 26,
                                  flex: "0 0 auto",
                                  display: "grid",
                                  placeItems: "center",
                                  border: "1px solid #cbd5e1",
                                  borderRadius: 8,
                                  background: "#f8fafc",
                                  color: "#124254",
                                  cursor: "pointer",
                                  fontWeight: 1000,
                                  lineHeight: 1
                                }}
                              >
                                ⧉
                              </button>
                            )}
                          </div>

                          <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                            <span
                              style={{
                                background: "#fee2e2",
                                color: "#b91c1c",
                                borderRadius: 999,
                                padding: "4px 8px",
                                fontSize: 11,
                                fontWeight: 800
                              }}
                            >
                              {formatProcessPeriod(processItem)}
                            </span>

                            {getProcessDelayInfo(processItem) && (
                              <span
                                style={{
                                  background: getProcessDelayInfo(processItem).background,
                                  color: getProcessDelayInfo(processItem).color,
                                  border: `1px solid ${getProcessDelayInfo(processItem).borderColor}`,
                                  borderRadius: 999,
                                  padding: "4px 8px",
                                  fontSize: 11,
                                  fontWeight: 900
                                }}
                              >
                                {getProcessDelayInfo(processItem).label}
                              </span>
                            )}
                          </div>

                          <div style={{ marginTop: 8, color: "#94a3b8", fontSize: 11, fontWeight: 700 }}>
                            SG: {formatFirestoreDate(processItem.updatedAt)}
                          </div>

                          {(
                            (Array.isArray(processItem.notes) && processItem.notes.length > 0) ||
                            Boolean(String(processItem.note || "").trim())
                          ) && (
                            <div
                              style={{
                                marginTop: 8,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 5,
                                alignSelf: "flex-start",
                                padding: "4px 8px",
                                borderRadius: 999,
                                background: "#fffbeb",
                                border: "1px solid #facc15",
                                color: "#92400e",
                                fontSize: 11,
                                fontWeight: 900
                              }}
                            >
                              📝 Not var
                            </div>
                          )}

                          <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 11, fontWeight: 800, color: getProgressColor(processItem.completionRate || 0) }}>
                              %{processItem.completionRate || 0}
                            </span>

                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontSize: 11, color: "#64748b", fontWeight: 700 }}>
                                {getProcessStepsWithProgress(processItem).filter((step) => step.completed).length || 0}/{processItem.steps?.length || 0}
                              </span>

                              {isEditor && (
                                <button
                                  title="Arşive Taşı"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    archiveProcess(processItem.id);
                                  }}
                                  style={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: 7,
                                    border: "none",
                                    background: "#64748b",
                                    color: "#fff",
                                    fontSize: 12,
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: 0,
                                    lineHeight: 1
                                  }}
                                >
                                  📦
                                </button>
                              )}

                              {isAdmin && (
                                <button
                                  title="Sil"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteProcess(processItem.id);
                                  }}
                                  style={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: 7,
                                    border: "none",
                                    background: "#ef4444",
                                    color: "#fff",
                                    fontSize: 12,
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: 0,
                                    lineHeight: 1
                                  }}
                                >
                                  🗑
                                </button>
                              )}
                            </div>
                          </div>

                          <div style={{ marginTop: 6, height: 8, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
                            <div style={{ width: `${processItem.completionRate || 0}%`, height: "100%", background: getProgressColor(processItem.completionRate || 0) }} />
                          </div>

                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {isAdmin && showBulkCreateModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <div
            style={{
              width: "560px",
              maxWidth: "92vw",
              background: "#fff",
              borderRadius: 20,
              padding: 24,
              boxShadow: "0 24px 80px rgba(0,0,0,0.25)"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 18 }}>
              <div>
                <h3 style={{ margin: 0, color: "#124254" }}>Şablondan Toplu Oluştur</h3>
                <div style={{ color: "#64748b", fontWeight: 700, marginTop: 6 }}>
                  Seçilen şablon tüm firmalara görev kartı olarak çoğaltılır. Mevcut görevler atlanır.
                </div>
              </div>

              <button
                onClick={() => setShowBulkCreateModal(false)}
                disabled={isBulkCreating}
                style={{
                  border: "none",
                  background: "#f1f5f9",
                  borderRadius: 10,
                  padding: "8px 12px",
                  cursor: isBulkCreating ? "not-allowed" : "pointer",
                  fontWeight: 800
                }}
              >
                Kapat
              </button>
            </div>

            <div style={{ display: "grid", gap: 14 }}>
              <div>
                <label style={{ display: "block", marginBottom: 6, color: "#124254", fontWeight: 900 }}>
                  Şablon
                </label>
                <select
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    borderRadius: 12,
                    border: "1px solid #dbe2ea",
                    fontSize: 14,
                    boxSizing: "border-box"
                  }}
                >
                  <option value="">Şablon seçiniz</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 6, color: "#124254", fontWeight: 900 }}>
                  Dönem
                </label>
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    borderRadius: 12,
                    border: "1px solid #dbe2ea",
                    fontSize: 14,
                    boxSizing: "border-box"
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 6, color: "#124254", fontWeight: 900 }}>
                  {tenantMode ? "Başlangıç Kolonu" : "Sorumlu"}
                </label>
                <select
                  value={selectedOwner}
                  onChange={(e) => setSelectedOwner(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    borderRadius: 12,
                    border: "1px solid #dbe2ea",
                    fontSize: 14,
                    boxSizing: "border-box"
                  }}
                >
                  <option value="">{tenantMode ? "Başlangıç kolonu seçiniz" : "Sorumlu seçiniz"}</option>
                  {owners.map((owner) => (
                    <option key={owner.id} value={owner.name}>
                      {owner.name}
                    </option>
                  ))}
                </select>
              </div>

              <div
                style={{
                  padding: 14,
                  borderRadius: 14,
                  background: "#f8fafc",
                  border: "1px solid #e5e7eb",
                  color: "#475569",
                  fontWeight: 800,
                  lineHeight: 1.6
                }}
              >
                <div>Firma sayısı: <strong>{companies.length}</strong></div>
                <div>Seçilen dönem: <strong>{formatMonthLabel(selectedMonth)}</strong></div>
                <div>Mevcut aynı firma/ay/şablon kayıtları otomatik atlanır.</div>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20, borderTop: "1px solid #e5e7eb", paddingTop: 16 }}>
              <button
                onClick={() => setShowBulkCreateModal(false)}
                disabled={isBulkCreating}
                style={{
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: "1px solid #dbe2ea",
                  background: "#fff",
                  color: "#475569",
                  fontWeight: 800,
                  cursor: isBulkCreating ? "not-allowed" : "pointer"
                }}
              >
                Vazgeç
              </button>

              <button
                onClick={bulkCreateMonthlyProcesses}
                disabled={isBulkCreating}
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: "none",
                  background: isBulkCreating ? "#94a3b8" : "#0f766e",
                  color: "#fff",
                  fontWeight: 900,
                  cursor: isBulkCreating ? "not-allowed" : "pointer"
                }}
              >
                {isBulkCreating ? "Oluşturuluyor..." : "Tüm Firmalara Oluştur"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isAdmin && showTemplateCreateModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <div
            style={{
              width: "760px",
              maxWidth: "92vw",
              maxHeight: "88vh",
              overflowY: "auto",
              background: "#fff",
              borderRadius: 20,
              padding: 24,
              boxShadow: "0 24px 80px rgba(0,0,0,0.25)"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 18 }}>
              <div>
                <h3 style={{ margin: 0, color: "#124254" }}>
                  {editingTemplateId ? "Süreç Şablonu Güncelle" : "Süreç Şablonu Oluştur"}
                </h3>
                <div style={{ color: "#64748b", fontWeight: 700, marginTop: 6 }}>
                  Ana adım ve alt adımları tanımlayın. Daha sonra bu şablonu tüm firmalara çoğaltabilirsiniz.
                </div>
              </div>

              <button
                onClick={() => {
                  setShowTemplateCreateModal(false);
                  resetTemplateForm();
                }}
                style={{
                  border: "none",
                  background: "#f1f5f9",
                  borderRadius: 10,
                  padding: "8px 12px",
                  cursor: "pointer",
                  fontWeight: 800
                }}
              >
                Kapat
              </button>
            </div>

            <input
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Şablon adı"
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 12,
                border: "1px solid #dbe2ea",
                marginBottom: 16,
                fontSize: 14,
                boxSizing: "border-box",
                fontWeight: 800
              }}
            />

            <div
              style={{
                marginTop: 12,
                padding: "11px 12px",
                borderRadius: 10,
                border: "1px solid #bae6fd",
                background: "#f0f9ff",
                color: "#075985",
                fontSize: 13,
                fontWeight: 800
              }}
            >
              Aylık tekrar: Yeni dönem kartları ayın son günü saat 18:00'de oluşturulur.
            </div>

            <div style={{ marginTop: 12 }}>
              <label>Periyot</label>

              <input
                type="number"
                min="1"
                value={templateInterval}
                onChange={(e) => setTemplateInterval(Number(e.target.value))}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid #dbe2ea",
                  boxSizing: "border-box"
                }}
              />
            </div>

            <div style={{ display: "grid", gap: 14 }}>
              {structuredSteps.map((step, mainIndex) => (
                <div
                  key={`template-main-${mainIndex}`}
                  style={{
                    display: "grid",
                    gap: 10,
                    padding: 12,
                    borderRadius: 14,
                    border: "1px solid #e5e7eb",
                    background: "#f8fafc"
                  }}
                >
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center" }}>
                    <input
                      value={step.title}
                      onChange={(e) => updateMainStepTitle(mainIndex, e.target.value)}
                      placeholder="Ana adım adı"
                      style={{
                        width: "100%",
                        padding: "11px 12px",
                        borderRadius: 10,
                        border: "1px solid #dbe2ea",
                        fontSize: 14,
                        fontWeight: 900,
                        color: "#124254",
                        boxSizing: "border-box"
                      }}
                    />

                    <button
                      onClick={() => removeMainStep(mainIndex)}
                      disabled={structuredSteps.length === 1}
                      style={{
                        padding: "10px 12px",
                        borderRadius: 10,
                        border: "none",
                        background: structuredSteps.length === 1 ? "#f1f5f9" : "#fee2e2",
                        color: structuredSteps.length === 1 ? "#94a3b8" : "#b91c1c",
                        fontWeight: 900,
                        cursor: structuredSteps.length === 1 ? "not-allowed" : "pointer"
                      }}
                    >
                      Sil
                    </button>
                  </div>

                  <div style={{ display: "grid", gap: 8 }}>
                    {step.subSteps.map((subStep, subIndex) => (
                      <div key={`template-main-${mainIndex}-sub-${subIndex}`} style={{ display: "grid", gridTemplateColumns: "34px 1fr auto", gap: 8, alignItems: "center" }}>
                        <span style={{ color: "#64748b", fontWeight: 900 }}>
                          {subIndex + 1}.
                        </span>

                        <input
                          value={subStep}
                          onChange={(e) => updateSubStep(mainIndex, subIndex, e.target.value)}
                          placeholder="Alt adım adı"
                          style={{
                            width: "100%",
                            padding: "10px 12px",
                            borderRadius: 10,
                            border: "1px solid #dbe2ea",
                            fontSize: 14,
                            boxSizing: "border-box"
                          }}
                        />

                        <button
                          onClick={() => removeSubStep(mainIndex, subIndex)}
                          disabled={step.subSteps.length === 1}
                          style={{
                            padding: "9px 10px",
                            borderRadius: 10,
                            border: "none",
                            background: step.subSteps.length === 1 ? "#f1f5f9" : "#fff7ed",
                            color: step.subSteps.length === 1 ? "#94a3b8" : "#c2410c",
                            fontWeight: 900,
                            cursor: step.subSteps.length === 1 ? "not-allowed" : "pointer"
                          }}
                        >
                          Sil
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => addSubStep(mainIndex)}
                    style={{
                      justifySelf: "start",
                      padding: "9px 12px",
                      borderRadius: 10,
                      border: "none",
                      background: "#e0f2fe",
                      color: "#0369a1",
                      fontWeight: 900,
                      cursor: "pointer"
                    }}
                  >
                    + Alt Adım Ekle
                  </button>
                </div>
              ))}

              <button
                onClick={addMainStep}
                style={{
                  justifySelf: "start",
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: "none",
                  background: "#0f766e",
                  color: "#fff",
                  fontWeight: 900,
                  cursor: "pointer"
                }}
              >
                + Ana Adım Ekle
              </button>
            </div>

            <div
              style={{
                marginTop: 20,
                paddingTop: 16,
                borderTop: "1px solid #e5e7eb"
              }}
            >
              <h4 style={{ margin: "0 0 10px", color: "#124254" }}>
                Mevcut Şablonlar
              </h4>

              {templates.length === 0 ? (
                <div style={{ color: "#64748b", fontWeight: 700 }}>
                  Henüz kayıtlı süreç şablonu yok.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 10 }}>
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto auto auto",
                        gap: 10,
                        alignItems: "center",
                        padding: 12,
                        borderRadius: 12,
                        border: "1px solid #e5e7eb",
                        background: "#f8fafc"
                      }}
                    >
                      <div>
                        <strong style={{ color: "#124254" }}>
                          {template.name}
                        </strong>
                        <div style={{ marginTop: 4, color: "#64748b", fontSize: 12, fontWeight: 800 }}>
                          {template.steps?.length || 0} alt adım
                        </div>
                      </div>

                      <span
                        style={{
                          padding: "5px 9px",
                          borderRadius: 999,
                          background: "#e0f2fe",
                          color: "#0369a1",
                          fontSize: 12,
                          fontWeight: 900
                        }}
                      >
                        Şablon
                      </span>

                      <button
                        onClick={() => {
                          setTemplateName(template.name || "");
                          setEditingTemplateId(template.id);
                          setTemplateInterval(template.recurrence?.interval || 1);    

                          const groupedSteps = getGroupedSteps(template.steps || []);

                          setStructuredSteps(
                            groupedSteps.map((group) => ({
                              title: group.title || "",
                              subSteps: (group.steps || []).map((step) => step.title || "")
                            }))
                          );

                          setShowTemplateCreateModal(true);
                        }}
                        style={{
                          padding: "8px 10px",
                          borderRadius: 10,
                          border: "1px solid #dbe2ea",
                          background: "#fff",
                          color: "#124254",
                          fontWeight: 900,
                          cursor: "pointer"
                        }}
                      >
                        Düzenle
                      </button>

                      <button
                        onClick={() => removeTemplate(template)}
                        style={{
                          padding: "8px 10px",
                          borderRadius: 10,
                          border: "none",
                          background: "#fee2e2",
                          color: "#b91c1c",
                          fontWeight: 900,
                          cursor: "pointer"
                        }}
                      >
                        Sil
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20, borderTop: "1px solid #e5e7eb", paddingTop: 16 }}>
              <button
                onClick={() => {
                  setShowTemplateCreateModal(false);
                  resetTemplateForm();
                }}
                style={{
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: "1px solid #dbe2ea",
                  background: "#fff",
                  color: "#475569",
                  fontWeight: 800,
                  cursor: "pointer"
                }}
              >
                Vazgeç
              </button>

              <button
                onClick={saveTemplate}
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: "none",
                  background: "#124254",
                  color: "#fff",
                  fontWeight: 900,
                  cursor: "pointer"
                }}
              >
                {editingTemplateId ? "Şablonu Güncelle" : "Şablonu Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}

      {newProcessModalOwner && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <div
            style={{
              width: "720px",
              maxWidth: "92vw",
              background: "#fff",
              borderRadius: 20,
              padding: 24,
              boxShadow: "0 24px 80px rgba(0,0,0,0.25)"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0, color: "#124254" }}>
                  {tenantMode ? "Yeni Görev Kartı Oluştur" : "Yeni Görev Oluştur"}
                </h3>
                <div style={{ color: "#64748b", fontWeight: 700, marginTop: 6 }}>
                  {tenantMode ? "Başlangıç kolonu" : "Sorumlu"}: {newProcessModalOwner.name}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginBottom: 20,
                  borderBottom: "1px solid #e5e7eb",
                  paddingBottom: 12
                }}
              >
                <button
                  onClick={() => setNewProcessModalTab("details")}
                  style={{
                    padding: "10px 16px",
                    borderRadius: 10,
                    border: "none",
                    cursor: "pointer",
                    fontWeight: 800,
                    background:
                      newProcessModalTab === "details"
                        ? "#124254"
                        : "#f1f5f9",
                    color:
                      newProcessModalTab === "details"
                        ? "#fff"
                        : "#475569"
                  }}
                >
                  Detaylar
                </button>

                <button
                  onClick={() => setNewProcessModalTab("steps")}
                  style={{
                    padding: "10px 16px",
                    borderRadius: 10,
                    border: "none",
                    cursor: "pointer",
                    fontWeight: 800,
                    background:
                      newProcessModalTab === "steps"
                        ? "#124254"
                        : "#f1f5f9",
                    color:
                      newProcessModalTab === "steps"
                        ? "#fff"
                        : "#475569"
                  }}
                >
                  Adımlar
                </button>
              </div>
              

              <button
                onClick={closeNewProcessModal}
                style={{
                  border: "none",
                  background: "#f1f5f9",
                  borderRadius: 10,
                  padding: "8px 12px",
                  cursor: "pointer",
                  fontWeight: 800
                }}
              >
                Kapat
              </button>
            </div>
              {newProcessModalTab === "details" && (
                <div style={{ display: "grid", gap: 12 }}>
                  <input
                    value={newProcessForm.title}
                    onChange={(e) =>
                      setNewProcessForm({
                        ...newProcessForm,
                        title: e.target.value
                      })
                    }
                    placeholder="Görev başlığı (firma seçmeden genel görev için zorunlu)"
                    style={{
                      padding: "12px 14px",
                      borderRadius: 12,
                      border: "1px solid #dbe2ea"
                    }}
                  />

                  <select
                    value={newProcessForm.companyName}
                    onChange={(e) =>
                      setNewProcessForm({
                        ...newProcessForm,
                        companyName: e.target.value
                      })
                    }
                    style={{
                      padding: "12px 14px",
                      borderRadius: 12,
                      border: "1px solid #dbe2ea"
                    }}
                  >
                    <option value="">Firma seçmeden genel görev oluştur</option>

                    {companies.map((company) => (
                      <option key={company.name} value={company.name}>
                        {company.shortName ? `${company.shortName} - ${company.name}` : company.name}
                      </option>
                    ))}
                  </select>

                  <select
                    value={newProcessForm.scheduleType}
                    onChange={(e) =>
                      setNewProcessForm({
                        ...newProcessForm,
                        scheduleType: e.target.value
                      })
                    }
                    style={{
                      padding: "12px 14px",
                      borderRadius: 12,
                      border: "1px solid #dbe2ea",
                      fontWeight: 800
                    }}
                  >
                    <option value="month">Aylık dönem seç</option>
                    <option value="range">Başlangıç / bitiş günü seç</option>
                  </select>

                  {newProcessForm.scheduleType === "range" ? (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <label style={{ display: "grid", gap: 6, color: "#64748b", fontWeight: 800, fontSize: 12 }}>
                        Başlangıç günü
                        <input
                          type="date"
                          value={newProcessForm.startDate}
                          onChange={(e) =>
                            setNewProcessForm({
                              ...newProcessForm,
                              startDate: e.target.value,
                              endDate:
                                newProcessForm.endDate && newProcessForm.endDate < e.target.value
                                  ? e.target.value
                                  : newProcessForm.endDate
                            })
                          }
                          style={{
                            padding: "12px 14px",
                            borderRadius: 12,
                            border: "1px solid #dbe2ea"
                          }}
                        />
                      </label>

                      <label style={{ display: "grid", gap: 6, color: "#64748b", fontWeight: 800, fontSize: 12 }}>
                        Bitiş günü
                        <input
                          type="date"
                          value={newProcessForm.endDate}
                          min={newProcessForm.startDate || undefined}
                          onChange={(e) =>
                            setNewProcessForm({
                              ...newProcessForm,
                              endDate: e.target.value
                            })
                          }
                          style={{
                            padding: "12px 14px",
                            borderRadius: 12,
                            border: "1px solid #dbe2ea"
                          }}
                        />
                      </label>
                    </div>
                  ) : (
                    <input
                      type="month"
                      value={newProcessForm.month}
                      onChange={(e) =>
                        setNewProcessForm({
                          ...newProcessForm,
                          month: e.target.value
                        })
                      }
                      style={{
                        padding: "12px 14px",
                        borderRadius: 12,
                        border: "1px solid #dbe2ea"
                      }}
                    />
                  )}

                  <div style={{ color: "#64748b", fontWeight: 700, fontSize: 12 }}>
                    Firma seçerseniz kart firma bazlı ilerler. Firma seçmezseniz bu kart genel görev süreci olarak başlıkla takip edilir.
                    Tarih aralığında tek günlük görev için başlangıç ve bitiş gününü aynı seçebilirsiniz. Bitiş günü boş kalırsa başlangıç günü kullanılır.
                  </div>
                </div>
              )}

              {newProcessModalTab === "steps" && (
                <div style={{ display: "grid", gap: 14, maxHeight: "52vh", overflowY: "auto", paddingRight: 4 }}>
                  {newProcessForm.steps.map((mainStep, mainIndex) => (
                    <div
                      key={`new-process-main-${mainIndex}`}
                      style={{
                        display: "grid",
                        gap: 10,
                        padding: 12,
                        borderRadius: 14,
                        border: "1px solid #e5e7eb",
                        background: "#f8fafc"
                      }}
                    >
                      <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 12, alignItems: "center", marginBottom: 12, flex: "0 0 auto" }}>
                        <input
                          value={mainStep.title}
                          onChange={(e) => updateModalMainStep(mainIndex, e.target.value)}
                          placeholder="Ana adım adı"
                          style={{
                            width: "100%",
                            padding: "11px 12px",
                            borderRadius: 10,
                            border: "1px solid #dbe2ea",
                            fontSize: 14,
                            fontWeight: 900,
                            color: "#124254",
                            boxSizing: "border-box"
                          }}
                        />

                        <button
                          onClick={() => removeModalMainStep(mainIndex)}
                          disabled={newProcessForm.steps.length === 1}
                          style={{
                            padding: "10px 12px",
                            borderRadius: 10,
                            border: "none",
                            background: newProcessForm.steps.length === 1 ? "#f1f5f9" : "#fee2e2",
                            color: newProcessForm.steps.length === 1 ? "#94a3b8" : "#b91c1c",
                            fontWeight: 900,
                            cursor: newProcessForm.steps.length === 1 ? "not-allowed" : "pointer"
                          }}
                        >
                          Sil
                        </button>
                      </div>

                      <div style={{ display: "grid", gap: 8 }}>
                        {mainStep.subSteps.map((subStep, subIndex) => (
                          <div key={`new-process-main-${mainIndex}-sub-${subIndex}`} style={{ display: "grid", gridTemplateColumns: "34px 1fr auto", gap: 8, alignItems: "center" }}>
                            <span style={{ color: "#64748b", fontWeight: 900 }}>
                              {subIndex + 1}.
                            </span>

                            <input
                              value={subStep}
                              onChange={(e) => updateModalSubStep(mainIndex, subIndex, e.target.value)}
                              placeholder="Alt adım adı"
                              style={{
                                width: "100%",
                                padding: "10px 12px",
                                borderRadius: 10,
                                border: "1px solid #dbe2ea",
                                fontSize: 14,
                                boxSizing: "border-box"
                              }}
                            />

                            <button
                              onClick={() => removeModalSubStep(mainIndex, subIndex)}
                              disabled={mainStep.subSteps.length === 1}
                              style={{
                                padding: "9px 10px",
                                borderRadius: 10,
                                border: "none",
                                background: mainStep.subSteps.length === 1 ? "#f1f5f9" : "#fff7ed",
                                color: mainStep.subSteps.length === 1 ? "#94a3b8" : "#c2410c",
                                fontWeight: 900,
                                cursor: mainStep.subSteps.length === 1 ? "not-allowed" : "pointer"
                              }}
                            >
                              Sil
                            </button>
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={() => addModalSubStep(mainIndex)}
                        style={{
                          justifySelf: "start",
                          padding: "9px 12px",
                          borderRadius: 10,
                          border: "none",
                          background: "#e0f2fe",
                          color: "#0369a1",
                          fontWeight: 900,
                          cursor: "pointer"
                        }}
                      >
                        + Alt Adım Ekle
                      </button>
                    </div>
                  ))}

                  <button
                    onClick={addModalMainStep}
                    style={{
                      justifySelf: "start",
                      padding: "10px 14px",
                      borderRadius: 10,
                      border: "none",
                      background: "#0f766e",
                      color: "#fff",
                      fontWeight: 900,
                      cursor: "pointer"
                    }}
                  >
                    + Ana Adım Ekle
                  </button>
                </div>
              )}
              {/* Modal Footer */}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20, borderTop: "1px solid #e5e7eb", paddingTop: 16 }}>
                <button
                  onClick={closeNewProcessModal}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 10,
                    border: "1px solid #dbe2ea",
                    background: "#fff",
                    color: "#475569",
                    fontWeight: 800,
                    cursor: "pointer"
                  }}
                >
                  Vazgeç
                </button>

                <button
                  onClick={saveNewProcessFromModal}
                  style={{
                    padding: "10px 16px",
                    borderRadius: 10,
                    border: "none",
                    background: "#124254",
                    color: "#fff",
                    fontWeight: 900,
                    cursor: "pointer"
                  }}
                >
                  Görevi Oluştur
                </button>
              </div>
          </div>
        </div>
      )}

      {reportListMode && (() => {
        const isDeliveredList = reportListMode === "delivered";
        const listItems = isDeliveredList
          ? exemptionDayReportReadyProcesses
          : exemptionDayReportPendingProcesses;

        return (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15, 23, 42, 0.45)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 20
            }}
            onClick={() => setReportListMode(null)}
          >
            <div
              style={{
                width: "760px",
                maxWidth: "94vw",
                maxHeight: "82vh",
                overflow: "hidden",
                background: "#fff",
                borderRadius: 20,
                boxShadow: "0 24px 80px rgba(0,0,0,0.25)",
                display: "flex",
                flexDirection: "column"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ padding: 20, borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <h3 style={{ margin: 0, color: "#124254" }}>
                    {isDeliveredList ? "Gün Sayısı Raporu Teslim Edilen" : "Gün Sayısı Raporu Teslim Edilecek"}
                  </h3>
                  <div style={{ marginTop: 6, color: "#64748b", fontWeight: 800 }}>
                    Toplam {listItems.length} kayıt
                  </div>
                </div>

                <button
                  onClick={() => setReportListMode(null)}
                  style={{
                    border: "none",
                    background: "#f1f5f9",
                    borderRadius: 10,
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontWeight: 900,
                    alignSelf: "flex-start"
                  }}
                >
                  Kapat
                </button>
              </div>

              <div style={{ padding: 16, overflow: "auto", display: "grid", gap: 10 }}>
                {listItems.length === 0 ? (
                  <div style={{ color: "#64748b", fontWeight: 800, padding: 16 }}>
                    Bu listede kayıt yok.
                  </div>
                ) : (
                  listItems.map((processItem) => (
                    <div
                      key={processItem.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        gap: 12,
                        alignItems: "center",
                        border: "1px solid #e5e7eb",
                        borderRadius: 14,
                        padding: 12,
                        background: "#f8fafc"
                      }}
                    >
                      <button
                        onClick={() => {
                          setSelectedProcess(processItem);
                          setReportListMode(null);
                        }}
                        style={{
                          border: "none",
                          background: "transparent",
                          textAlign: "left",
                          cursor: "pointer",
                          padding: 0
                        }}
                      >
                        <strong style={{ color: "#124254", display: "block", marginBottom: 4 }}>
                          {getProcessDisplayTitle(processItem)}
                        </strong>
                        <span style={{ color: "#64748b", fontWeight: 800, fontSize: 12 }}>
                          {processItem.companyName || processItem.title || "Genel görev"} · {processItem.owner} · {formatProcessPeriod(processItem)}
                        </span>
                      </button>

                      {isDeliveredList ? (
                        <button
                          onClick={() => markExemptionDayReportDelivered(processItem, false)}
                          style={{
                            border: "1px solid #fed7aa",
                            background: "#fff7ed",
                            color: "#c2410c",
                            borderRadius: 10,
                            padding: "9px 12px",
                            fontWeight: 900,
                            cursor: "pointer",
                            whiteSpace: "nowrap"
                          }}
                        >
                          Bekliyor Yap
                        </button>
                      ) : (
                        <button
                          onClick={() => markExemptionDayReportDelivered(processItem, true)}
                          style={{
                            border: "none",
                            background: "#16a34a",
                            color: "#fff",
                            borderRadius: 10,
                            padding: "9px 12px",
                            fontWeight: 900,
                            cursor: "pointer",
                            whiteSpace: "nowrap"
                          }}
                        >
                          Teslim edildi işaretle
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {copySourceProcess && copyProcessForm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10050,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            background: isDark ? "rgba(2,6,23,0.82)" : "rgba(15,23,42,0.58)"
          }}
          onClick={closeProcessCopyModal}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="copy-process-title"
            onClick={(event) => event.stopPropagation()}
            style={{
              width: 720,
              maxWidth: "94vw",
              maxHeight: "88vh",
              overflowY: "auto",
              borderRadius: 20,
              border: `1px solid ${modalTheme.borderSoft}`,
              background: modalTheme.surface,
              color: modalTheme.bodyText,
              boxShadow: "0 28px 90px rgba(15,23,42,0.34)"
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 16,
                padding: "20px 22px",
                borderBottom: `1px solid ${modalTheme.borderSoft}`
              }}
            >
              <div>
                <h3 id="copy-process-title" style={{ margin: 0, color: modalTheme.text }}>
                  Görevi Kopyala
                </h3>
                <div style={{ marginTop: 6, color: modalTheme.muted, fontSize: 13, fontWeight: 800 }}>
                  {getProcessDisplayTitle(copySourceProcess)} · {formatProcessPeriod(copySourceProcess)}
                </div>
              </div>

              <button
                type="button"
                onClick={closeProcessCopyModal}
                disabled={isCopyingProcess}
                style={{
                  alignSelf: "flex-start",
                  padding: "8px 11px",
                  borderRadius: 10,
                  border: `1px solid ${modalTheme.borderSoft}`,
                  background: modalTheme.button,
                  color: modalTheme.bodyText,
                  cursor: isCopyingProcess ? "not-allowed" : "pointer",
                  fontWeight: 900
                }}
              >
                Kapat
              </button>
            </div>

            <div style={{ display: "grid", gap: 16, padding: 22 }}>
              <div
                style={{
                  padding: 12,
                  borderRadius: 13,
                  border: `1px solid ${modalTheme.infoBorder}`,
                  background: modalTheme.infoBg,
                  color: modalTheme.infoText,
                  fontSize: 12,
                  fontWeight: 800,
                  lineHeight: 1.55
                }}
              >
                Firma, görev yapısı, adımlar ve tekrarlama ayarı korunur. Adım tikleri, tamamlanma durumu, notlar, zaman kayıtları ve teslim bilgileri yeni karta taşınmaz.
              </div>

              <label style={{ display: "grid", gap: 6, color: modalTheme.bodyText, fontSize: 12, fontWeight: 900 }}>
                Firma
                <input
                  value={copyProcessForm.companyName || "Genel görev"}
                  readOnly
                  style={{
                    padding: "11px 12px",
                    borderRadius: 10,
                    border: `1px solid ${modalTheme.borderSoft}`,
                    background: modalTheme.surface2,
                    color: modalTheme.muted,
                    fontWeight: 800
                  }}
                />
              </label>

              <label style={{ display: "grid", gap: 6, color: modalTheme.bodyText, fontSize: 12, fontWeight: 900 }}>
                Yeni görev / süreç adı
                <input
                  value={copyProcessForm.title}
                  onChange={(event) => setCopyProcessForm((current) => ({ ...current, title: event.target.value }))}
                  style={{
                    padding: "11px 12px",
                    borderRadius: 10,
                    border: `1px solid ${modalTheme.borderSoft}`,
                    background: modalTheme.input,
                    color: modalTheme.bodyText,
                    fontWeight: 800
                  }}
                />
              </label>

              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 12 }}>
                <label style={{ display: "grid", gap: 6, color: modalTheme.bodyText, fontSize: 12, fontWeight: 900 }}>
                  {tenantMode ? "Kanban kolonu" : "Sorumlu kişi"}
                  <select
                    value={copyProcessForm.owner}
                    onChange={(event) => setCopyProcessForm((current) => ({ ...current, owner: event.target.value }))}
                    style={{
                      padding: "11px 12px",
                      borderRadius: 10,
                      border: `1px solid ${modalTheme.borderSoft}`,
                      background: modalTheme.input,
                      color: modalTheme.bodyText,
                      fontWeight: 800
                    }}
                  >
                    <option value="Atanmamış">Atanmamış</option>
                    {orderedKanbanOwners
                      .filter((owner) => !isCompletedOwner(owner.name))
                      .map((owner) => (
                        <option key={owner.id || owner.name} value={owner.name}>
                          {owner.name}
                        </option>
                      ))}
                    {copyProcessForm.owner !== "Atanmamış" &&
                      !orderedKanbanOwners.some((owner) => owner.name === copyProcessForm.owner) && (
                        <option value={copyProcessForm.owner}>{copyProcessForm.owner}</option>
                      )}
                  </select>
                </label>

                <label style={{ display: "grid", gap: 6, color: modalTheme.bodyText, fontSize: 12, fontWeight: 900 }}>
                  Tarih tipi
                  <select
                    value={copyProcessForm.scheduleType}
                    onChange={(event) => setCopyProcessForm((current) => ({
                      ...current,
                      scheduleType: event.target.value
                    }))}
                    style={{
                      padding: "11px 12px",
                      borderRadius: 10,
                      border: `1px solid ${modalTheme.borderSoft}`,
                      background: modalTheme.input,
                      color: modalTheme.bodyText,
                      fontWeight: 800
                    }}
                  >
                    <option value="month">Aylık dönem</option>
                    <option value="range">Tarih aralığı</option>
                  </select>
                </label>
              </div>

              {copyProcessForm.scheduleType === "range" ? (
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 12 }}>
                  <label style={{ display: "grid", gap: 6, color: modalTheme.bodyText, fontSize: 12, fontWeight: 900 }}>
                    Başlangıç tarihi
                    <input
                      type="date"
                      value={copyProcessForm.startDate}
                      onChange={(event) => setCopyProcessForm((current) => ({
                        ...current,
                        startDate: event.target.value,
                        endDate: current.endDate && current.endDate < event.target.value
                          ? event.target.value
                          : current.endDate
                      }))}
                      style={{ padding: "11px 12px", borderRadius: 10, border: `1px solid ${modalTheme.borderSoft}`, background: modalTheme.input, color: modalTheme.bodyText, fontWeight: 800 }}
                    />
                  </label>

                  <label style={{ display: "grid", gap: 6, color: modalTheme.bodyText, fontSize: 12, fontWeight: 900 }}>
                    Bitiş tarihi
                    <input
                      type="date"
                      min={copyProcessForm.startDate || undefined}
                      value={copyProcessForm.endDate}
                      onChange={(event) => setCopyProcessForm((current) => ({ ...current, endDate: event.target.value }))}
                      style={{ padding: "11px 12px", borderRadius: 10, border: `1px solid ${modalTheme.borderSoft}`, background: modalTheme.input, color: modalTheme.bodyText, fontWeight: 800 }}
                    />
                  </label>
                </div>
              ) : (
                <label style={{ display: "grid", gap: 6, color: modalTheme.bodyText, fontSize: 12, fontWeight: 900 }}>
                  Yeni dönem
                  <input
                    type="month"
                    value={copyProcessForm.month}
                    onChange={(event) => setCopyProcessForm((current) => ({ ...current, month: event.target.value }))}
                    style={{ padding: "11px 12px", borderRadius: 10, border: `1px solid ${modalTheme.borderSoft}`, background: modalTheme.input, color: modalTheme.bodyText, fontWeight: 800 }}
                  />
                </label>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) 150px",
                  gap: 12,
                  alignItems: "end",
                  padding: 12,
                  borderRadius: 13,
                  border: `1px solid ${modalTheme.borderSoft}`,
                  background: modalTheme.surface2
                }}
              >
                <label style={{ display: "flex", alignItems: "center", gap: 9, color: modalTheme.bodyText, fontSize: 13, fontWeight: 900 }}>
                  <input
                    type="checkbox"
                    checked={copyProcessForm.autoRepeat}
                    onChange={(event) => setCopyProcessForm((current) => ({ ...current, autoRepeat: event.target.checked }))}
                  />
                  Tekrarlama ayarını koru
                </label>

                <label style={{ display: "grid", gap: 6, color: modalTheme.bodyText, fontSize: 12, fontWeight: 900 }}>
                  Kaç ayda bir?
                  <input
                    type="number"
                    min="1"
                    disabled={!copyProcessForm.autoRepeat}
                    value={copyProcessForm.recurrence.interval}
                    onChange={(event) => setCopyProcessForm((current) => ({
                      ...current,
                      recurrence: {
                        ...current.recurrence,
                        interval: Math.max(1, Number(event.target.value) || 1)
                      }
                    }))}
                    style={{ padding: "10px 11px", borderRadius: 10, border: `1px solid ${modalTheme.borderSoft}`, background: modalTheme.input, color: modalTheme.bodyText, fontWeight: 800 }}
                  />
                </label>
              </div>

              <div style={{ color: modalTheme.muted, fontSize: 12, fontWeight: 800 }}>
                {copySourceProcess.steps?.length || 0} görev adımı işaretsiz olarak yeni karta aktarılacak.
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                padding: "16px 22px",
                borderTop: `1px solid ${modalTheme.borderSoft}`
              }}
            >
              <button
                type="button"
                onClick={closeProcessCopyModal}
                disabled={isCopyingProcess}
                style={{
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: `1px solid ${modalTheme.borderSoft}`,
                  background: modalTheme.button,
                  color: modalTheme.bodyText,
                  cursor: isCopyingProcess ? "not-allowed" : "pointer",
                  fontWeight: 900
                }}
              >
                Vazgeç
              </button>

              <button
                type="button"
                onClick={saveProcessCopy}
                disabled={isCopyingProcess}
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: "none",
                  background: "#0f766e",
                  color: "#fff",
                  cursor: isCopyingProcess ? "wait" : "pointer",
                  opacity: isCopyingProcess ? 0.7 : 1,
                  fontWeight: 1000
                }}
              >
                {isCopyingProcess ? "Kopya oluşturuluyor..." : "Kopyayı Oluştur"}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedProcess && (
        <div style={{
          position: "fixed",
          inset: 0,
          background: isDark ? "rgba(2,6,23,0.72)" : "rgba(0,0,0,0.45)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <div style={{
            width: isNotesPanelExpanded ? "1260px" : "1100px",
            maxWidth: "92vw",
            maxHeight: "85vh",
            overflowY: "auto",
            background: modalTheme.surface,
            color: modalTheme.bodyText,
            border: `1px solid ${modalTheme.borderSoft}`,
            borderRadius: 20,
            padding: 24,
            boxShadow: "0 24px 80px rgba(0,0,0,0.25)"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0, color: modalTheme.text }}>
                  {getProcessDisplayTitle(selectedProcess)}
                </h3>
                <div style={{ color: modalTheme.muted, fontWeight: 700, marginTop: 6 }}>
                  {selectedProcess.companyName ? `${selectedProcess.companyName} / ` : ""}
                  {selectedProcess.owner} / {formatProcessPeriod(selectedProcess)}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                {isEditor && (
                  <button
                    onClick={() => openProcessCopyModal(selectedProcess)}
                    style={{
                      background: isDark ? "#123b3a" : "#ecfdf5",
                      color: isDark ? "#99f6e4" : "#0f766e",
                      border: isDark ? "1px solid #0f766e" : "1px solid #99f6e4",
                      borderRadius: 10,
                      padding: "8px 12px",
                      cursor: "pointer",
                      fontWeight: 900
                    }}
                  >
                    Kopyala
                  </button>
                )}

                {isEditor && selectedProcess.status !== "archived" && (
                  <button
                    onClick={() => archiveProcess(selectedProcess.id)}
                    style={{
                      background: isDark ? "#26364d" : "#f1f5f9",
                      color: isDark ? "#dbeafe" : "#475569",
                      border: `1px solid ${modalTheme.borderSoft}`,
                      borderRadius: 10,
                      padding: "8px 12px",
                      cursor: "pointer",
                      fontWeight: 900
                    }}
                  >
                    Arşivle
                  </button>
                )}

                {isAdmin && (
                  <button
                    onClick={() => deleteProcess(selectedProcess.id)}
                    style={{
                      background: isDark ? "#3a1618" : "#fee2e2",
                      color: isDark ? "#fca5a5" : "#b91c1c",
                      border: isDark ? "1px solid #7f1d1d" : "1px solid #fecaca",
                      borderRadius: 10,
                      padding: "8px 12px",
                      cursor: "pointer",
                      fontWeight: 900
                    }}
                  >
                    Sil
                  </button>
                )}

                <button
                  onClick={() => {
                    cancelEditingSelectedSteps();
                    cancelEditingRepeatSettings();
                    setReminderDraft(null);
                    setIsNotesPanelExpanded(false);
                    setSelectedProcess(null);
                  }}
                  style={{
                    background: modalTheme.button,
                    color: modalTheme.bodyText,
                    border: `1px solid ${modalTheme.borderSoft}`,
                    borderRadius: 10,
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontWeight: 800
                  }}
                >
                  Kapat
                </button>
              </div>
            </div>

            {isEditor && selectedProcess.status !== "archived" && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "160px minmax(220px, 1fr)",
                  gap: 10,
                  alignItems: "center",
                  padding: 12,
                  borderRadius: 14,
                  border: `1px solid ${modalTheme.borderSoft}`,
                  background: modalTheme.surface2,
                  marginBottom: 14
                }}
              >
                <label style={{ color: modalTheme.muted, fontSize: 12, fontWeight: 950 }}>
                  Kanban Kolonu
                </label>
                <select
                  value={selectedProcess.status === "completed" || isCompletedOwner(selectedProcess.owner) ? "TAMAMLANDI" : selectedProcess.owner || ""}
                  onChange={(event) => moveProcessToKanbanTarget(selectedProcess, event.target.value)}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "9px 10px",
                    borderRadius: 10,
                    border: `1px solid ${modalTheme.borderSoft}`,
                    background: modalTheme.input,
                    color: modalTheme.bodyText,
                    fontWeight: 900
                  }}
                >
                  <option value="">Kolon seçin</option>
                  {orderedKanbanOwners.map((owner) => (
                    <option key={owner.id || owner.name} value={owner.name}>
                      {owner.name}
                    </option>
                  ))}
                  {!orderedKanbanOwners.some((owner) => isCompletedOwner(owner.name)) && (
                    <option value="TAMAMLANDI">TAMAMLANDI</option>
                  )}
                </select>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 10 }}>
              <strong style={{ color: getProgressColor(selectedProcess.completionRate || 0) }}>
                %{selectedProcess.completionRate || 0} tamamlandı
              </strong>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ color: modalTheme.muted, fontWeight: 800 }}>
                  {getProcessStepsWithProgress(selectedProcess).filter((step) => step.completed).length || 0}/{selectedProcess.steps?.length || 0} adım
                </span>

                {user && selectedProcess.status !== "archived" && !isEditingSelectedSteps && (
                  <button
                    onClick={startEditingSelectedSteps}
                    style={{
                      padding: "8px 10px",
                      borderRadius: 10,
                      border: `1px solid ${modalTheme.borderSoft}`,
                      background: modalTheme.button,
                      color: modalTheme.text,
                      fontWeight: 900,
                      cursor: "pointer"
                    }}
                  >
                    {isDefaultMonthlyProcessProject ? "Adımları Düzenle" : "Görevi Düzenle"}
                  </button>
                )}
              </div>
            </div>

            <div style={{ height: 10, background: modalTheme.progressTrack, borderRadius: 999, overflow: "hidden", marginBottom: 16 }}>
              <div style={{ width: `${selectedProcess.completionRate || 0}%`, height: "100%", background: getProgressColor(selectedProcess.completionRate || 0) }} />
            </div>

            <div
              style={{
                display: "grid",
                gap: 8,
                padding: 10,
                borderRadius: 16,
                border: `1px solid ${modalTheme.border}`,
                background: modalTheme.surface2,
                marginBottom: 16
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <h4 style={{ margin: 0, color: modalTheme.text }}>
                    Zaman Takibi
                  </h4>
                  <div style={{ color: modalTheme.muted, fontSize: 12, fontWeight: 800, marginTop: 3 }}>
                    {activeTimeEntry ? `Çalışıyor: ${formatTimeDuration(getTimeEntryDuration(activeTimeEntry))}` : "Sayaç kapalı"}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ padding: "7px 9px", borderRadius: 10, background: modalTheme.surface, border: `1px solid ${modalTheme.borderSoft}`, color: modalTheme.bodyText, fontWeight: 900 }}>
                    Toplam: {formatTimeDuration(totalTrackedSeconds)}
                  </div>
                  <div style={{ padding: "7px 9px", borderRadius: 10, background: modalTheme.surface, border: `1px solid ${modalTheme.borderSoft}`, color: modalTheme.bodyText, fontWeight: 900 }}>
                    Benim: {formatTimeDuration(myTrackedSeconds)}
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gap: 8, alignItems: "start" }}>
                <div style={{ display: "grid", gap: 10 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      alignItems: "center",
                      flexWrap: "wrap",
                      padding: 8,
                      borderRadius: 14,
                      background: activeTimeEntry ? (isDark ? "#123423" : "#ecfdf5") : modalTheme.surface,
                      border: `1px solid ${activeTimeEntry ? modalTheme.successBorder : modalTheme.borderSoft}`
                    }}
                  >
                    <div>
                      <div style={{ color: modalTheme.text, fontWeight: 900 }}>
                        {activeTimeEntry ? "Çalışma sürüyor" : "Bu görev için sayaç kapalı"}
                      </div>
                      <div style={{ color: modalTheme.muted, fontSize: 12, fontWeight: 800, marginTop: 4 }}>
                        {activeTimeEntry
                          ? `Geçen süre: ${formatTimeDuration(getTimeEntryDuration(activeTimeEntry))}`
                          : "Başlat dediğinizde yeni bir zaman kaydı açılır."}
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={activeTimeEntry ? stopTaskTimer : startTaskTimer}
                        disabled={timeActionLoading}
                        style={{
                          border: "none",
                          borderRadius: 11,
                          padding: "8px 12px",
                          background: activeTimeEntry ? "#ef4444" : "#16a34a",
                          color: "#fff",
                          fontWeight: 900,
                          cursor: timeActionLoading ? "not-allowed" : "pointer",
                          opacity: timeActionLoading ? 0.7 : 1
                        }}
                      >
                        {timeActionLoading ? "İşleniyor..." : activeTimeEntry ? "Durdur" : "Başlat"}
                      </button>

                      <button
                        type="button"
                        onClick={() => setShowManualTimeForm((current) => !current)}
                        style={{
                          border: `1px solid ${modalTheme.borderSoft}`,
                          borderRadius: 11,
                          padding: "8px 10px",
                          background: modalTheme.button,
                          color: modalTheme.text,
                          fontWeight: 900,
                          cursor: "pointer"
                        }}
                      >
                        Süre Ekle
                      </button>

                      <button
                        type="button"
                        onClick={() => setShowTimeEntries((current) => !current)}
                        style={{
                          border: `1px solid ${modalTheme.borderSoft}`,
                          borderRadius: 11,
                          padding: "8px 10px",
                          background: modalTheme.button,
                          color: modalTheme.text,
                          fontWeight: 900,
                          cursor: "pointer"
                        }}
                      >
                        {showTimeEntries ? "Kayıtları Gizle" : "Kayıtları Göster"}
                      </button>
                    </div>
                  </div>

                  {showTimeEntries && (
                  <div style={{ display: "grid", gap: 8, maxHeight: 220, overflowY: "auto" }}>
                    {timeEntriesLoading ? (
                      <div style={{ color: modalTheme.muted, fontWeight: 800 }}>Zaman kayıtları yükleniyor...</div>
                    ) : timeEntries.length === 0 ? (
                      <div style={{ color: modalTheme.muted, fontWeight: 800 }}>Henüz zaman kaydı yok.</div>
                    ) : (
                      timeEntries.map((entry) => (
                        <div
                          key={entry.id}
                          style={{
                            display: "grid",
                            gap: 5,
                            padding: "9px 10px",
                            borderRadius: 12,
                            border: `1px solid ${modalTheme.borderSoft}`,
                            background: modalTheme.surface,
                            color: modalTheme.bodyText,
                            fontSize: 12,
                            fontWeight: 800
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                            <span>
                              {entry.userName || entry.userEmail || "Kullanıcı"}
                            </span>
                            <span
                              style={{
                                padding: "3px 7px",
                                borderRadius: 999,
                                background: entry.isManual ? (isDark ? "#3b2f15" : "#fef3c7") : (entry.status === "running" ? modalTheme.successBg : modalTheme.infoBg),
                                color: entry.isManual ? (isDark ? "#fde68a" : "#92400e") : (entry.status === "running" ? (isDark ? "#86efac" : "#166534") : modalTheme.infoText),
                                fontWeight: 900
                              }}
                            >
                              {entry.isManual ? "Manuel" : entry.status === "running" ? "Aktif" : "Sayaç"}
                            </span>
                          </div>
                          <div style={{ color: modalTheme.muted }}>
                            {formatFirestoreDate(entry.startedAtIso)} → {entry.status === "running" ? "devam ediyor" : formatFirestoreDate(entry.endedAtIso)}
                          </div>
                          <div style={{ color: modalTheme.text, fontWeight: 900 }}>
                            Süre: {formatTimeDuration(getTimeEntryDuration(entry))}
                          </div>
                          {entry.note && (
                            <div style={{ color: modalTheme.muted, whiteSpace: "pre-wrap" }}>
                              Not: {entry.note}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  )}
                </div>

                {showManualTimeForm && (
                <div
                  style={{
                    display: "grid",
                    gap: 9,
                    padding: 12,
                    borderRadius: 14,
                    border: `1px solid ${modalTheme.borderSoft}`,
                    background: modalTheme.surface
                  }}
                >
                  <strong style={{ color: modalTheme.text }}>Süre Ekle</strong>
                  <div style={{ color: modalTheme.muted, fontSize: 12, fontWeight: 800 }}>
                    Sayaç unutulduysa manuel süre ekleyebilirsiniz. Bu kayıt “Manuel” olarak görünür.
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr", gap: 8 }}>
                    <input
                      type="date"
                      value={manualTimeForm.date}
                      onChange={(e) => setManualTimeForm((current) => ({ ...current, date: e.target.value }))}
                      style={timeInputStyle}
                    />
                    <input
                      type="time"
                      value={manualTimeForm.startTime}
                      onChange={(e) => setManualTimeForm((current) => ({ ...current, startTime: e.target.value }))}
                      style={timeInputStyle}
                    />
                    <input
                      type="time"
                      value={manualTimeForm.endTime}
                      onChange={(e) => setManualTimeForm((current) => ({ ...current, endTime: e.target.value }))}
                      style={timeInputStyle}
                    />
                  </div>
                  <textarea
                    value={manualTimeForm.note}
                    onChange={(e) => setManualTimeForm((current) => ({ ...current, note: e.target.value }))}
                    rows={2}
                    placeholder="Manuel süre notu (isteğe bağlı)"
                    style={{ ...timeInputStyle, resize: "vertical" }}
                  />
                  <button
                    type="button"
                    onClick={addManualTimeEntry}
                    disabled={timeActionLoading}
                    style={{
                      border: "none",
                      borderRadius: 11,
                      padding: "10px 12px",
                      background: "#2563eb",
                      color: "#fff",
                      fontWeight: 900,
                      cursor: timeActionLoading ? "not-allowed" : "pointer",
                      opacity: timeActionLoading ? 0.7 : 1
                    }}
                  >
                    Manuel Süre Ekle
                  </button>
                </div>
                )}
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isNotesPanelExpanded
                  ? "minmax(0, 0.9fr) minmax(420px, 0.75fr)"
                  : "minmax(0, 1fr) minmax(300px, 360px)",
                gap: 18,
                alignItems: "start"
              }}
            >
              <div style={{ minWidth: 0 }}>
            <div
              style={{
                display: "grid",
                gap: 10,
                padding: 14,
                borderRadius: 14,
                border: `1px solid ${modalTheme.border}`,
                background: selectedProcess.autoRepeat ? (isDark ? "#123423" : "#ecfdf5") : modalTheme.surface2,
                marginBottom: 16
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div>
                  <strong style={{ color: modalTheme.text }}>
                    Görev Tekrarlama Ayarı
                  </strong>

                  <div style={{ color: modalTheme.muted, fontSize: 12, fontWeight: 800, marginTop: 4 }}>
                    {selectedProcess.autoRepeat
                      ? `Aktif - ${selectedProcess.recurrence?.interval || 1} ayda bir, ayın son günü saat 18:00'de oluşturulur`
                      : "Bu görev otomatik olarak tekrarlanmaz"}
                  </div>
                </div>

                {isAdmin && !isEditingRepeatSettings && (
                  <button
                    onClick={startEditingRepeatSettings}
                    style={{
                      padding: "8px 10px",
                      borderRadius: 10,
                      border: `1px solid ${modalTheme.borderSoft}`,
                      background: modalTheme.button,
                      color: modalTheme.text,
                      fontWeight: 900,
                      cursor: "pointer"
                    }}
                  >
                    Tekrarlamayı Düzenle
                  </button>
                )}
              </div>

              {isEditingRepeatSettings && (
                <div style={{ display: "grid", gap: 12 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, color: modalTheme.bodyText, fontWeight: 800 }}>
                    <input
                      type="checkbox"
                      checked={editableRepeatSettings.autoRepeat}
                      onChange={(e) =>
                        setEditableRepeatSettings((current) => ({
                          ...current,
                          autoRepeat: e.target.checked
                        }))
                      }
                    />
                    Görev otomatik olarak tekrarlansın
                  </label>

                  <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 2fr)", gap: 10 }}>
                    <label style={{ display: "grid", gap: 6, color: modalTheme.bodyText, fontSize: 12, fontWeight: 800 }}>
                      Kaç ayda bir?
                      <input
                        disabled={!editableRepeatSettings.autoRepeat}
                        type="number"
                        min="1"
                        value={editableRepeatSettings.recurrence.interval}
                        onChange={(e) =>
                          setEditableRepeatSettings((current) => ({
                            ...current,
                            recurrence: {
                              ...current.recurrence,
                              interval: Math.max(1, Number(e.target.value) || 1)
                            }
                          }))
                        }
                        style={{ padding: "9px 10px", borderRadius: 9, border: `1px solid ${modalTheme.borderSoft}`, background: modalTheme.input, color: modalTheme.bodyText }}
                      />
                    </label>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "10px 12px",
                        borderRadius: 9,
                        border: `1px solid ${modalTheme.infoBorder}`,
                        background: modalTheme.infoBg,
                        color: modalTheme.infoText,
                        fontSize: 12,
                        fontWeight: 800,
                        opacity: editableRepeatSettings.autoRepeat ? 1 : 0.55
                      }}
                    >
                      Sonraki ayın kartı, ayın son günü saat 18:00'de otomatik oluşturulur.
                    </div>
                  </div>

                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <button
                      onClick={cancelEditingRepeatSettings}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 9,
                        border: `1px solid ${modalTheme.borderSoft}`,
                        background: modalTheme.button,
                        color: modalTheme.bodyText,
                        fontWeight: 800,
                        cursor: "pointer"
                      }}
                    >
                      Vazgeç
                    </button>

                    <button
                      onClick={saveRepeatSettings}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 9,
                        border: "none",
                        background: "#124254",
                        color: "#fff",
                        fontWeight: 900,
                        cursor: "pointer"
                      }}
                    >
                      Kaydet
                    </button>
                  </div>
                </div>
              )}
            </div>

            {user && isEditingSelectedSteps && (
              <div
                style={{
                  display: "grid",
                  gap: 12,
                  padding: 14,
                  borderRadius: 14,
                  border: `1px solid ${modalTheme.borderSoft}`,
                  background: modalTheme.surface2,
                  marginBottom: 16
                }}
              >
                <div
                  style={{
                    position: "sticky",
                    top: -14,
                    zIndex: 30,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 0",
                    background: modalTheme.surface2,
                    borderBottom: `1px solid ${modalTheme.borderSoft}`
                  }}
                >
                  <strong style={{ color: modalTheme.text }}>
                    {isDefaultMonthlyProcessProject ? "Adım Düzenleme" : "Görev Düzenleme"}
                  </strong>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <button
                      onClick={cancelEditingSelectedSteps}
                      style={{
                        padding: "8px 10px",
                        borderRadius: 10,
                        border: `1px solid ${modalTheme.borderSoft}`,
                        background: modalTheme.button,
                        color: modalTheme.bodyText,
                        fontWeight: 800,
                        cursor: "pointer"
                      }}
                    >
                      Vazgeç
                    </button>

                    <button
                      onClick={saveEditedSelectedSteps}
                      disabled={isSavingSelectedSteps}
                      style={{
                        padding: "8px 10px",
                        borderRadius: 10,
                        border: "none",
                        background: "#124254",
                        color: "#fff",
                        fontWeight: 900,
                        cursor: isSavingSelectedSteps ? "wait" : "pointer",
                        opacity: isSavingSelectedSteps ? 0.7 : 1
                      }}
                    >
                      {isSavingSelectedSteps ? "Kaydediliyor..." : "Kaydet"}
                    </button>
                  </div>
                </div>

                {!isDefaultMonthlyProcessProject && (
                  <div
                    style={{
                      display: "grid",
                      gap: 10,
                      padding: 12,
                      borderRadius: 14,
                      border: `1px solid ${modalTheme.border}`,
                      background: modalTheme.surface
                    }}
                  >
                    <div style={{ color: modalTheme.text, fontWeight: 900 }}>
                      Görev Bilgileri
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1.4fr)", gap: 10 }}>
                      <label style={{ display: "grid", gap: 6, color: modalTheme.bodyText, fontSize: 12, fontWeight: 800 }}>
                        Görev Başlığı
                        <input
                          value={editableSelectedProcessForm.title}
                          onChange={(e) =>
                            setEditableSelectedProcessForm((current) => ({
                              ...current,
                              title: e.target.value
                            }))
                          }
                          placeholder="Görev başlığı"
                          style={{ padding: "10px 11px", borderRadius: 10, border: `1px solid ${modalTheme.borderSoft}`, background: modalTheme.input, color: modalTheme.bodyText, fontWeight: 800 }}
                        />
                      </label>

                      <label style={{ display: "grid", gap: 6, color: modalTheme.bodyText, fontSize: 12, fontWeight: 800 }}>
                        {tenantMode ? "Kanban Kolonu" : "Sorumlu"}
                        <select
                          value={editableSelectedProcessForm.owner}
                          onChange={(e) =>
                            setEditableSelectedProcessForm((current) => ({
                              ...current,
                              owner: e.target.value
                            }))
                          }
                          style={{ padding: "10px 11px", borderRadius: 10, border: `1px solid ${modalTheme.borderSoft}`, background: modalTheme.input, color: modalTheme.bodyText, fontWeight: 800 }}
                        >
                          <option value="">Atanmamış</option>
                          {owners.map((owner) => (
                            <option key={owner.id || owner.name} value={owner.name}>
                              {owner.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 1fr)", gap: 10 }}>
                      <label style={{ display: "grid", gap: 6, color: modalTheme.bodyText, fontSize: 12, fontWeight: 800 }}>
                        Cari/Firma Adı
                        <input
                          value={editableSelectedProcessForm.companyName}
                          onChange={(e) =>
                            setEditableSelectedProcessForm((current) => ({
                              ...current,
                              companyName: e.target.value
                            }))
                          }
                          placeholder="Cari veya firma adı"
                          style={{ padding: "10px 11px", borderRadius: 10, border: `1px solid ${modalTheme.borderSoft}`, background: modalTheme.input, color: modalTheme.bodyText, fontWeight: 800 }}
                        />
                      </label>

                      <label style={{ display: "grid", gap: 6, color: modalTheme.bodyText, fontSize: 12, fontWeight: 800 }}>
                        Kısa Ad
                        <input
                          value={editableSelectedProcessForm.companyShortName}
                          onChange={(e) =>
                            setEditableSelectedProcessForm((current) => ({
                              ...current,
                              companyShortName: e.target.value
                            }))
                          }
                          placeholder="Kartta görünen kısa ad"
                          style={{ padding: "10px 11px", borderRadius: 10, border: `1px solid ${modalTheme.borderSoft}`, background: modalTheme.input, color: modalTheme.bodyText, fontWeight: 800 }}
                        />
                      </label>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 0.8fr) minmax(0, 1fr) minmax(0, 1fr)", gap: 10 }}>
                      <label style={{ display: "grid", gap: 6, color: modalTheme.bodyText, fontSize: 12, fontWeight: 800 }}>
                        Tarih Tipi
                        <select
                          value={editableSelectedProcessForm.scheduleType}
                          onChange={(e) =>
                            setEditableSelectedProcessForm((current) => ({
                              ...current,
                              scheduleType: e.target.value
                            }))
                          }
                          style={{ padding: "10px 11px", borderRadius: 10, border: `1px solid ${modalTheme.borderSoft}`, background: modalTheme.input, color: modalTheme.bodyText, fontWeight: 800 }}
                        >
                          <option value="month">Aylık dönem</option>
                          <option value="range">Tarih aralığı</option>
                        </select>
                      </label>

                      {editableSelectedProcessForm.scheduleType === "range" ? (
                        <>
                          <label style={{ display: "grid", gap: 6, color: modalTheme.bodyText, fontSize: 12, fontWeight: 800 }}>
                            Başlangıç
                            <input
                              type="date"
                              value={editableSelectedProcessForm.startDate}
                              onChange={(e) =>
                                setEditableSelectedProcessForm((current) => ({
                                  ...current,
                                  startDate: e.target.value
                                }))
                              }
                              style={{ padding: "10px 11px", borderRadius: 10, border: `1px solid ${modalTheme.borderSoft}`, background: modalTheme.input, color: modalTheme.bodyText, fontWeight: 800 }}
                            />
                          </label>

                          <label style={{ display: "grid", gap: 6, color: modalTheme.bodyText, fontSize: 12, fontWeight: 800 }}>
                            Bitiş
                            <input
                              type="date"
                              value={editableSelectedProcessForm.endDate}
                              onChange={(e) =>
                                setEditableSelectedProcessForm((current) => ({
                                  ...current,
                                  endDate: e.target.value
                                }))
                              }
                              style={{ padding: "10px 11px", borderRadius: 10, border: `1px solid ${modalTheme.borderSoft}`, background: modalTheme.input, color: modalTheme.bodyText, fontWeight: 800 }}
                            />
                          </label>
                        </>
                      ) : (
                        <label style={{ display: "grid", gap: 6, color: modalTheme.bodyText, fontSize: 12, fontWeight: 800 }}>
                          Dönem
                          <input
                            value={editableSelectedProcessForm.month}
                            onChange={(e) =>
                              setEditableSelectedProcessForm((current) => ({
                                ...current,
                                month: e.target.value
                              }))
                            }
                            placeholder="Örn: Temmuz 2026"
                            style={{ padding: "10px 11px", borderRadius: 10, border: `1px solid ${modalTheme.borderSoft}`, background: modalTheme.input, color: modalTheme.bodyText, fontWeight: 800 }}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                )}

                {isDefaultMonthlyProcessProject && (
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", color: modalTheme.bodyText, fontWeight: 800, fontSize: 13 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input
                      type="radio"
                      checked={stepEditScope === "single"}
                      onChange={() => setStepEditScope("single")}
                    />
                    Sadece bu görev kartı
                  </label>

                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input
                      type="radio"
                      checked={stepEditScope === "all"}
                      onChange={() => setStepEditScope("all")}
                    />
                    Aynı şablondaki tüm görev kartları
                  </label>
                </div>
                )}

                <div style={{ display: "grid", gap: 12 }}>
                  {editableSelectedStepGroups.map((group, groupIndex) => (
                    <div
                      key={`editable-group-${groupIndex}`}
                      style={{
                        display: "grid",
                        gap: 10,
                        padding: 12,
                        borderRadius: 14,
                        border: `1px solid ${modalTheme.border}`,
                        background: modalTheme.surface
                      }}
                    >
                      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 8, alignItems: "center" }}>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button
                            title="Ana adımı yukarı taşı"
                            onClick={() => moveEditableSelectedGroup(groupIndex, -1)}
                            disabled={groupIndex === 0}
                            style={{
                              width: 30,
                              height: 30,
                              borderRadius: 8,
                              border: `1px solid ${modalTheme.borderSoft}`,
                              background: groupIndex === 0 ? modalTheme.surface2 : modalTheme.button,
                              color: modalTheme.text,
                              fontWeight: 900,
                              cursor: groupIndex === 0 ? "not-allowed" : "pointer"
                            }}
                          >
                            ↑
                          </button>

                          <button
                            title="Ana adımı aşağı taşı"
                            onClick={() => moveEditableSelectedGroup(groupIndex, 1)}
                            disabled={groupIndex === editableSelectedStepGroups.length - 1}
                            style={{
                              width: 30,
                              height: 30,
                              borderRadius: 8,
                              border: `1px solid ${modalTheme.borderSoft}`,
                              background: groupIndex === editableSelectedStepGroups.length - 1 ? modalTheme.surface2 : modalTheme.button,
                              color: modalTheme.text,
                              fontWeight: 900,
                              cursor: groupIndex === editableSelectedStepGroups.length - 1 ? "not-allowed" : "pointer"
                            }}
                          >
                            ↓
                          </button>
                        </div>
                        <input
                          value={group.title}
                          onChange={(e) => updateEditableSelectedGroupTitle(groupIndex, e.target.value)}
                          placeholder="Ana adım adı"
                          style={{
                            width: "100%",
                            padding: "10px 12px",
                            borderRadius: 10,
                            border: `1px solid ${modalTheme.borderSoft}`,
                            background: modalTheme.input,
                            fontSize: 14,
                            fontWeight: 900,
                            color: modalTheme.text,
                            boxSizing: "border-box"
                          }}
                        />

                        <button
                          onClick={() => removeEditableSelectedGroup(groupIndex)}
                          style={{
                            padding: "9px 10px",
                            borderRadius: 10,
                            border: "none",
                            background: "#fee2e2",
                            color: "#b91c1c",
                            fontWeight: 900,
                            cursor: "pointer"
                          }}
                        >
                          Sil
                        </button>
                      </div>

                      <div style={{ display: "grid", gap: 8 }}>
                        {group.steps.map((step, stepIndex) => (
                          <div key={`editable-group-${groupIndex}-step-${stepIndex}`} style={{ display: "grid", gridTemplateColumns: "42px auto 1fr auto", gap: 8, alignItems: "center" }}>
                            <span style={{ color: modalTheme.muted, fontWeight: 900 }}>
                              {stepIndex + 1}.
                            </span>
                            <div style={{ display: "flex", gap: 4 }}>
                              <button
                                title="Alt adımı yukarı taşı"
                                onClick={() => moveEditableSelectedGroupStep(groupIndex, stepIndex, -1)}
                                disabled={stepIndex === 0}
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 8,
                                  border: `1px solid ${modalTheme.borderSoft}`,
                                  background: stepIndex === 0 ? modalTheme.surface2 : modalTheme.button,
                                  color: modalTheme.bodyText,
                                  fontWeight: 900,
                                  cursor: stepIndex === 0 ? "not-allowed" : "pointer"
                                }}
                              >
                                ↑
                              </button>

                              <button
                                title="Alt adımı aşağı taşı"
                                onClick={() => moveEditableSelectedGroupStep(groupIndex, stepIndex, 1)}
                                disabled={stepIndex === group.steps.length - 1}
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 8,
                                  border: `1px solid ${modalTheme.borderSoft}`,
                                  background: stepIndex === group.steps.length - 1 ? modalTheme.surface2 : modalTheme.button,
                                  color: modalTheme.bodyText,
                                  fontWeight: 900,
                                  cursor: stepIndex === group.steps.length - 1 ? "not-allowed" : "pointer"
                                }}
                              >
                                ↓
                              </button>
                            </div>

                            <input
                              value={step.title}
                              onChange={(e) => updateEditableSelectedGroupStepTitle(groupIndex, stepIndex, e.target.value)}
                              placeholder="Alt adım adı"
                              style={{
                                width: "100%",
                                padding: "10px 12px",
                                borderRadius: 10,
                                border: `1px solid ${modalTheme.borderSoft}`,
                                background: modalTheme.input,
                                color: modalTheme.bodyText,
                                fontSize: 14,
                                boxSizing: "border-box"
                              }}
                            />

                            <button
                              onClick={() => removeEditableSelectedGroupStep(groupIndex, stepIndex)}
                              style={{
                                padding: "9px 10px",
                                borderRadius: 10,
                                border: "none",
                                background: "#fff7ed",
                                color: "#c2410c",
                                fontWeight: 900,
                                cursor: "pointer"
                              }}
                            >
                              Sil
                            </button>
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={() => addEditableSelectedGroupStep(groupIndex)}
                        style={{
                          justifySelf: "start",
                          padding: "9px 12px",
                          borderRadius: 10,
                          border: "none",
                          background: "#e0f2fe",
                          color: "#0369a1",
                          fontWeight: 900,
                          cursor: "pointer"
                        }}
                      >
                        + Alt Adım Ekle
                      </button>
                    </div>
                  ))}

                  <button
                    onClick={addEditableSelectedGroup}
                    style={{
                      justifySelf: "start",
                      padding: "10px 14px",
                      borderRadius: 10,
                      border: "none",
                      background: "#0f766e",
                      color: "#fff",
                      fontWeight: 900,
                      cursor: "pointer"
                    }}
                  >
                    + Ana Adım Ekle
                  </button>

                  <div
                    style={{
                      position: "sticky",
                      bottom: -14,
                      zIndex: 25,
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 8,
                      flexWrap: "wrap",
                      padding: "12px 0 2px",
                      background: modalTheme.surface2,
                      borderTop: `1px solid ${modalTheme.borderSoft}`
                    }}
                  >
                    <button
                      onClick={cancelEditingSelectedSteps}
                      style={{
                        padding: "9px 13px",
                        borderRadius: 10,
                        border: `1px solid ${modalTheme.borderSoft}`,
                        background: modalTheme.button,
                        color: modalTheme.bodyText,
                        fontWeight: 800,
                        cursor: "pointer"
                      }}
                    >
                      Vazgeç
                    </button>

                    <button
                      onClick={saveEditedSelectedSteps}
                      style={{
                        padding: "9px 16px",
                        borderRadius: 10,
                        border: "none",
                        background: "#124254",
                        color: "#fff",
                        fontWeight: 950,
                        cursor: "pointer",
                        boxShadow: "0 8px 18px rgba(18,66,84,0.20)"
                      }}
                    >
                      Kaydet
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: "grid", gap: 12 }}>
              {getGroupedSteps(getProcessStepsWithProgress(selectedProcess)).map((group, groupIndex) => {
                const completedGroupSteps = group.steps.filter((step) => step.completed).length;
                const groupCompletionRate = group.steps.length
                  ? Math.round((completedGroupSteps / group.steps.length) * 100)
                  : 0;

                return (
                  <div
                    key={`${group.title}-${groupIndex}`}
                    style={{
                      border: `1px solid ${modalTheme.border}`,
                      borderRadius: 14,
                      overflow: "hidden",
                      background: modalTheme.surface2
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 12px",
                        background: modalTheme.surface3,
                        borderBottom: `1px solid ${modalTheme.border}`
                      }}
                    >
                      <strong style={{ color: modalTheme.text, fontSize: 14 }}>
                        {groupIndex + 1}. {group.title}
                      </strong>

                      <span style={{ color: getProgressColor(groupCompletionRate), fontSize: 12, fontWeight: 900 }}>
                        {completedGroupSteps}/{group.steps.length}
                      </span>
                    </div>

                    <div style={{ display: "grid", gap: 8, padding: 10 }}>
                      {group.steps.map((step) => (
                        <label
                          key={step.originalIndex}
                          style={{
                            display: "flex",
                            gap: 10,
                            alignItems: "center",
                            padding: "10px 12px",
                            borderRadius: 12,
                            border: step.completed ? `1px solid ${modalTheme.successBorder}` : `1px solid ${modalTheme.border}`,
                            background: step.completed ? modalTheme.successBg : modalTheme.input,
                            color: modalTheme.bodyText,
                            fontWeight: 700,
                            cursor: isEditingSelectedSteps ? "not-allowed" : "pointer",
                            opacity: isEditingSelectedSteps ? 0.72 : 1
                          }}
                        >
                          <input
                            type="checkbox"
                            disabled={isEditingSelectedSteps || financialVerificationKey === `${selectedProcess.id}:${step.originalIndex}`}
                            checked={Boolean(step.completed)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              if (!isEditingSelectedSteps) {
                                toggleStep(selectedProcess, step.originalIndex);
                              }
                            }}
                          />

                          <span style={{ color: modalTheme.muted, fontWeight: 900 }}>
                            {step.order}.
                          </span>

                          <span style={{ flex: 1 }}>{step.title}</span>
                          {step.verification?.type === "monthlyFinancialReport" && (
                            <span
                              title={`${step.verification.sourceFileName || "Aylık mali rapor"} · ${step.verification.periodKey || ""}`}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "4px 7px",
                                borderRadius: 999,
                                color: modalTheme.successText,
                                background: modalTheme.successBg,
                                border: `1px solid ${modalTheme.successBorder}`,
                                fontSize: 10,
                                fontWeight: 950,
                                whiteSpace: "nowrap"
                              }}
                            >
                              Rapor doğrulandı
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
              </div>

            <aside
              style={{
                position: "sticky",
                top: 0,
                border: `1px solid ${modalTheme.border}`,
                borderRadius: 16,
                background: modalTheme.surface2,
                padding: 14,
                maxHeight: isNotesPanelExpanded ? "74vh" : "68vh",
                overflowY: "auto"
              }}
            >
              <div
                style={{
                  display: "grid",
                  gap: 10,
                  padding: 12,
                  borderRadius: 14,
                  border: `1px solid ${modalTheme.infoBorder}`,
                  background: modalTheme.infoBg,
                  marginBottom: 14
                }}
              >
                <div>
                  <h4 style={{ margin: "0 0 4px", color: modalTheme.text }}>
                    Hatırlatma Taslağı
                  </h4>
                  <div style={{ color: modalTheme.muted, fontSize: 12, fontWeight: 800 }}>
                    Eksik adımlar: {getIncompleteProcessSteps(selectedProcess).length}
                  </div>
                  {selectedProcess.lastReminderDraftAt && (
                    <div style={{ color: modalTheme.muted, fontSize: 11, fontWeight: 800, marginTop: 4 }}>
                      Son taslak: {formatFirestoreDate(selectedProcess.lastReminderDraftAt)}
                    </div>
                  )}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <button
                    onClick={() => createReminderDraftForProcess(selectedProcess, "mail")}
                    disabled={getIncompleteProcessSteps(selectedProcess).length === 0}
                    style={{
                      border: "none",
                      borderRadius: 10,
                      padding: "9px 10px",
                      background: getIncompleteProcessSteps(selectedProcess).length === 0 ? modalTheme.progressTrack : "#2563eb",
                      color: getIncompleteProcessSteps(selectedProcess).length === 0 ? modalTheme.muted : "#fff",
                      fontWeight: 900,
                      cursor: getIncompleteProcessSteps(selectedProcess).length === 0 ? "not-allowed" : "pointer"
                    }}
                  >
                    Mail Taslağı
                  </button>

                  <button
                    onClick={() => createReminderDraftForProcess(selectedProcess, "whatsapp")}
                    disabled={getIncompleteProcessSteps(selectedProcess).length === 0}
                    style={{
                      border: "none",
                      borderRadius: 10,
                      padding: "9px 10px",
                      background: getIncompleteProcessSteps(selectedProcess).length === 0 ? modalTheme.progressTrack : "#16a34a",
                      color: getIncompleteProcessSteps(selectedProcess).length === 0 ? modalTheme.muted : "#fff",
                      fontWeight: 900,
                      cursor: getIncompleteProcessSteps(selectedProcess).length === 0 ? "not-allowed" : "pointer"
                    }}
                  >
                    WhatsApp
                  </button>
                </div>

                {reminderDraft?.processId === selectedProcess.id && (
                  <div style={{ display: "grid", gap: 8 }}>
                    <div
                      style={{
                        display: "grid",
                        gap: 4,
                        padding: 9,
                        borderRadius: 10,
                        background: modalTheme.surface,
                        border: `1px solid ${modalTheme.borderSoft}`,
                        color: modalTheme.bodyText,
                        fontSize: 12,
                        fontWeight: 800
                      }}
                    >
                      <div>
                        Alıcı:{" "}
                        <strong>
                          {reminderDraft.to || "Müşteriler bölümünde e-posta yok"}
                        </strong>
                      </div>
                      {reminderDraft.cc?.length > 0 && (
                        <div>Diğer alıcılar: {reminderDraft.cc.join(", ")}</div>
                      )}
                      <div>
                        WhatsApp:{" "}
                        <strong>
                          {reminderDraft.phone || "Müşteriler bölümünde telefon yok"}
                        </strong>
                      </div>
                    </div>

                    <label style={{ display: "grid", gap: 5, color: modalTheme.bodyText, fontSize: 12, fontWeight: 900 }}>
                      Mail konusu
                      <input
                        readOnly
                        value={reminderDraft.subject}
                        style={{
                          padding: "9px 10px",
                          borderRadius: 9,
                          border: `1px solid ${modalTheme.borderSoft}`,
                          background: modalTheme.input,
                          color: modalTheme.bodyText,
                          fontSize: 12
                        }}
                      />
                    </label>

                    <label style={{ display: "grid", gap: 5, color: modalTheme.bodyText, fontSize: 12, fontWeight: 900 }}>
                      Mail metni
                      <textarea
                        readOnly
                        value={reminderDraft.body}
                        rows={7}
                        style={{
                          padding: "9px 10px",
                          borderRadius: 9,
                          border: `1px solid ${modalTheme.borderSoft}`,
                          background: modalTheme.input,
                          color: modalTheme.bodyText,
                          fontSize: 12,
                          resize: "vertical"
                        }}
                      />
                    </label>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      <button
                        onClick={() => openMailDraft(reminderDraft)}
                        style={{
                          border: "none",
                          borderRadius: 10,
                          padding: "8px 10px",
                          background: "#124254",
                          color: "#fff",
                          fontWeight: 900,
                          cursor: "pointer"
                        }}
                      >
                        Mail Aç
                      </button>

                      <button
                        onClick={() => copyTextToClipboard(`${reminderDraft.subject}\n\n${reminderDraft.body}`, "Mail taslağı kopyalandı.")}
                        style={{
                          border: `1px solid ${modalTheme.borderSoft}`,
                          borderRadius: 10,
                          padding: "8px 10px",
                          background: modalTheme.button,
                          color: modalTheme.text,
                          fontWeight: 900,
                          cursor: "pointer"
                        }}
                      >
                        Kopyala
                      </button>
                    </div>

                    <label style={{ display: "grid", gap: 5, color: modalTheme.bodyText, fontSize: 12, fontWeight: 900 }}>
                      WhatsApp metni
                      <textarea
                        readOnly
                        value={reminderDraft.whatsappText}
                        rows={5}
                        style={{
                          padding: "9px 10px",
                          borderRadius: 9,
                          border: `1px solid ${modalTheme.borderSoft}`,
                          background: modalTheme.input,
                          color: modalTheme.bodyText,
                          fontSize: 12,
                          resize: "vertical"
                        }}
                      />
                    </label>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      <button
                        onClick={() => {
                          if (reminderDraft.whatsappUrl) {
                            window.open(reminderDraft.whatsappUrl, "_blank", "noopener,noreferrer");
                          } else {
                            alert("Bu firma için Müşteriler bölümünde telefon kaydı bulunamadı.");
                          }
                        }}
                        style={{
                          border: "none",
                          borderRadius: 10,
                          padding: "8px 10px",
                          background: reminderDraft.whatsappUrl ? "#16a34a" : modalTheme.progressTrack,
                          color: reminderDraft.whatsappUrl ? "#fff" : modalTheme.muted,
                          fontWeight: 900,
                          cursor: reminderDraft.whatsappUrl ? "pointer" : "not-allowed"
                        }}
                      >
                        WhatsApp Aç
                      </button>

                      <button
                        onClick={() => copyTextToClipboard(reminderDraft.whatsappText, "WhatsApp metni kopyalandı.")}
                        style={{
                          border: `1px solid ${modalTheme.borderSoft}`,
                          borderRadius: 10,
                          padding: "8px 10px",
                          background: modalTheme.button,
                          color: modalTheme.text,
                          fontWeight: 900,
                          cursor: "pointer"
                        }}
                      >
                        Kopyala
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <div>
                  <h4 style={{ margin: 0, color: modalTheme.text }}>
                    Süreç Notları
                  </h4>
                  <div style={{ color: modalTheme.muted, fontSize: 11, fontWeight: 800, marginTop: 3 }}>
                    Uzun notlar ve tablo metinleri için geniş görünümü kullanabilirsiniz.
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setIsNotesPanelExpanded((current) => !current)}
                  style={{
                    border: `1px solid ${modalTheme.borderSoft}`,
                    borderRadius: 10,
                    padding: "7px 9px",
                    background: modalTheme.button,
                    color: modalTheme.text,
                    fontSize: 12,
                    fontWeight: 900,
                    cursor: "pointer",
                    whiteSpace: "nowrap"
                  }}
                >
                  {isNotesPanelExpanded ? "Daralt" : "Genişlet"}
                </button>
              </div>

              {(() => {
                const savedNotes = Array.isArray(selectedProcess.notes)
                  ? selectedProcess.notes
                  : [];

                const legacyNote = selectedProcess.note
                  ? [{ text: selectedProcess.note, createdAt: selectedProcess.updatedAt, createdBy: "" }]
                  : [];

                const allNotes = [
                  ...legacyNote.map((noteItem) => ({ value: noteItem, isLegacy: true, savedIndex: -1 })),
                  ...savedNotes.map((noteItem, savedIndex) => ({ value: noteItem, isLegacy: false, savedIndex }))
                ];

                return allNotes.length === 0 ? (
                  <div style={{ color: "#94a3b8", fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
                    Henüz not eklenmedi.
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: 8, marginBottom: 14, maxHeight: isNotesPanelExpanded ? 430 : 260, overflowY: "auto" }}>
	                    {allNotes.map(({ value: noteItem, isLegacy, savedIndex }, noteIndex) => {
	                      const hasMention = typeof noteItem !== "string" && (
	                        noteItem.mentionAll ||
	                        (Array.isArray(noteItem.mentions) && noteItem.mentions.length > 0) ||
	                        (Array.isArray(noteItem.rawMentions) && noteItem.rawMentions.length > 0)
	                      );
	                      const noteBackground = hasMention
	                        ? (isDark ? "#182c4f" : "#eef2ff")
	                        : modalTheme.noteBg;
	                      const noteBorder = hasMention
	                        ? (isDark ? "#3b82f6" : "#818cf8")
	                        : modalTheme.noteBorder;
	                      const noteColor = hasMention
	                        ? (isDark ? "#dbeafe" : "#3730a3")
	                        : modalTheme.noteText;

	                      return (
	                      <div
	                        key={`${selectedProcess.id}-note-${noteIndex}`}
	                        style={{
	                          padding: "10px 12px",
	                          borderRadius: 12,
	                          background: noteBackground,
	                          border: `1px solid ${noteBorder}`,
	                          color: noteColor,
	                          fontSize: 13,
	                          fontWeight: 700,
	                          lineHeight: 1.45,
	                          whiteSpace: "pre-wrap",
	                          position: "relative",
	                          overflowX: "auto",
	                          boxShadow: hasMention
	                            ? (isDark ? "0 0 0 1px rgba(96,165,250,0.18)" : "0 8px 20px rgba(99,102,241,0.10)")
	                            : "none"
	                        }}
	                      >
	                        {hasMention && (
	                          <div
	                            style={{
	                              display: "inline-flex",
	                              alignItems: "center",
	                              gap: 6,
	                              padding: "4px 8px",
	                              borderRadius: 999,
	                              background: isDark ? "#1d4ed8" : "#dbeafe",
	                              color: isDark ? "#eff6ff" : "#1d4ed8",
	                              fontSize: 11,
	                              fontWeight: 950,
	                              marginBottom: 7
	                            }}
	                          >
	                            @{noteItem.mentionAll ? "herkes" : "etiket"}
	                          </div>
	                        )}
	                        {isAdmin && (
	                          <button
                            type="button"
                            onClick={() => deleteProcessNote(selectedProcess, savedIndex, isLegacy)}
                            title="Notu sil"
                            style={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              border: `1px solid ${isDark ? "#7f1d1d" : "#fecaca"}`,
                              background: isDark ? "#451a1a" : "#fff5f5",
                              color: isDark ? "#fecaca" : "#b91c1c",
                              borderRadius: 8,
                              padding: "4px 7px",
                              fontSize: 11,
                              fontWeight: 900,
                              cursor: "pointer"
                            }}
                          >
                            Sil
                          </button>
                        )}

                        <div
                          style={{
                            paddingRight: isAdmin ? 38 : 0,
                            minWidth: isNotesPanelExpanded ? "max-content" : "auto",
                            maxWidth: "100%",
                            fontFamily: String(typeof noteItem === "string" ? noteItem : noteItem.text || "").includes("\t")
                              ? "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
                              : "inherit"
                          }}
                        >
                          {typeof noteItem === "string" ? noteItem : noteItem.text}
                        </div>

	                        {typeof noteItem !== "string" && noteItem.createdAt && (
	                          <div style={{ marginTop: 6, color: noteColor, fontSize: 11, fontWeight: 800, opacity: 0.8 }}>
	                            {formatFirestoreDate(noteItem.createdAt)} {noteItem.createdBy ? `• ${noteItem.createdBy}` : ""}
	                          </div>
	                        )}
	                      </div>
	                      );
	                    })}
                  </div>
                );
              })()}

	              <div style={{ position: "relative" }}>
	                <textarea
	                  value={newProcessNoteText}
	                  onChange={(e) => setNewProcessNoteText(e.target.value)}
	                  rows={isNotesPanelExpanded ? 7 : 4}
	                  placeholder="Bu süreçle ilgili yeni not yazın... @herkes veya @isim ile kişileri etiketleyebilirsiniz."
	                  style={{
	                    width: "100%",
	                    minHeight: isNotesPanelExpanded ? 150 : 90,
	                    padding: "12px 14px",
	                    borderRadius: 12,
	                    border: `1px solid ${modalTheme.borderSoft}`,
	                    background: modalTheme.input,
	                    color: modalTheme.bodyText,
	                    fontSize: 14,
	                    boxSizing: "border-box",
	                    resize: "vertical"
	                  }}
	                />

	                {(() => {
	                  const mentionSuggestions = getNoteMentionSuggestions(newProcessNoteText);

	                  if (mentionSuggestions.length === 0) return null;

	                  return (
	                    <div
	                      style={{
	                        position: "absolute",
	                        left: 10,
	                        right: 10,
	                        bottom: "calc(100% + 8px)",
	                        zIndex: 25,
	                        border: `1px solid ${isDark ? "#334155" : "#c7d2fe"}`,
	                        borderRadius: 14,
	                        background: isDark ? "#111827" : "#ffffff",
	                        boxShadow: isDark
	                          ? "0 18px 35px rgba(0,0,0,0.35)"
	                          : "0 18px 35px rgba(30,41,59,0.16)",
	                        overflow: "hidden"
	                      }}
	                    >
	                      <div
	                        style={{
	                          padding: "8px 11px",
	                          background: isDark ? "#1e293b" : "#eef2ff",
	                          color: isDark ? "#dbeafe" : "#3730a3",
	                          fontSize: 11,
	                          fontWeight: 950
	                        }}
	                      >
	                        Etiketlenecek kişiyi seç
	                      </div>

	                      {mentionSuggestions.map((item) => (
	                        <button
	                          key={`${item.token}-${item.subLabel}`}
	                          type="button"
	                          onClick={() => insertNoteMention(item.token)}
	                          style={{
	                            width: "100%",
	                            display: "flex",
	                            alignItems: "center",
	                            gap: 10,
	                            padding: "9px 11px",
	                            border: "none",
	                            borderTop: `1px solid ${isDark ? "#1f2937" : "#eef2ff"}`,
	                            background: "transparent",
	                            color: modalTheme.bodyText,
	                            textAlign: "left",
	                            cursor: "pointer"
	                          }}
	                        >
	                          <span
	                            style={{
	                              width: 28,
	                              height: 28,
	                              borderRadius: 999,
	                              display: "inline-flex",
	                              alignItems: "center",
	                              justifyContent: "center",
	                              background: item.token === "herkes"
	                                ? (isDark ? "#1d4ed8" : "#dbeafe")
	                                : (isDark ? "#334155" : "#f1f5f9"),
	                              color: item.token === "herkes"
	                                ? (isDark ? "#eff6ff" : "#1d4ed8")
	                                : modalTheme.text,
	                              fontSize: 12,
	                              fontWeight: 1000
	                            }}
	                          >
	                            {item.avatar}
	                          </span>
	                          <span style={{ display: "grid", gap: 2 }}>
	                            <span style={{ fontSize: 13, fontWeight: 950 }}>
	                              @{item.token} · {item.label}
	                            </span>
	                            <span style={{ fontSize: 11, fontWeight: 800, color: modalTheme.muted }}>
	                              {item.subLabel}
	                            </span>
	                          </span>
	                        </button>
	                      ))}
	                    </div>
	                  );
	                })()}
	              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                <button
                  onClick={() => saveProcessNote(selectedProcess)}
                  style={{
                    padding: "10px 16px",
                    borderRadius: 10,
                    border: "none",
                    background: "#124254",
                    color: "#fff",
                    fontWeight: 900,
                    cursor: "pointer"
                  }}
                >
                  Notu Kaydet
                </button>
              </div>
            </aside>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MonthlyProcessesView;
